/**
 * Laser Ornament Studio - 2D CAD Vector Geometry Engine
 * File: assets/laser-studio-2d.js
 * Synthesizes State Tree, OpenType Font Pipeline, Vector Templates, Geometry Welding, SVG Exporter, UI Bindings & App Bootstrapper.
 */

window.LaserStudio = window.LaserStudio || {};


/* ==========================================================================
   2D MODULE: laser-studio-state.js
   ========================================================================== */

/**
 * Laser Ornament Studio - Global Application State Container
 * File: assets/laser-studio-state.js
 * Responsibility: Single source of truth for the entire application.
 * Manages active templates, typography enums, font caches, material selections, and order serialization.
 */

window.LaserStudio = window.LaserStudio || {};

// Typography Family Enum
window.LaserFontFamily = {
  MONTSERRAT: 'montserrat',
  ARCHIVO: 'archivo',
  OSWALD: 'oswald',
  PACIFICO: 'pacifico',
  BENTILLO: 'bentillo',
  CAPTAIN_HEROES: 'captain-heroes',
  HELODERMA: 'heloderma',
  RETRO_WORLD: 'retro-world'
};

// Numerical Font Weight Scale (400 to 800)
window.LaserFontWeight = {
  REGULAR: 400,
  MEDIUM: 500,
  SEMIBOLD: 600,
  BOLD: 700,
  EXTRABOLD: 800
};

// Font Classification Taxonomy
window.LaserFontCategories = {
  STANDARD: ['archivo', 'montserrat', 'oswald', 'pacifico'],
  PREMIUM: ['bentillo', 'captain-heroes', 'heloderma', 'retro-world'],
  LASER_STUDIO: ['bentillo', 'captain-heroes', 'heloderma', 'retro-world']
};

// Supported Weights Per Font
window.LaserFontWeightsSupported = {
  archivo: [400, 500, 600, 700, 800],
  montserrat: [400, 500, 600, 700, 800],
  oswald: [400, 500, 600, 700],
  pacifico: [400],
  bentillo: [400],
  'captain-heroes': [400],
  heloderma: [400],
  'retro-world': [400]
};

// Global State Tree
window.LaserState = {
  selectedTemplate: 'elf',
  lastSelectedTemplate: 'elf',
  selectedFontFamily: 'bentillo',
  selectedFontWeight: 400,
  selectedWoodSpecies: 'birch',
  selectedThickness: 5.0, // 5.0 mm (~3/16")

  // Layer 2: Acrylic Backplate / Body Infill profile
  layer2: {
    enabled: false,
    colorId: 'green',
    color: '#059669',
    name: 'Emerald Pine Green',
    material: 'acrylic_gloss',
    thickness: 3.0 // 3.0 mm
  },

  // OpenType font binary cache lookup table: fonts[family][weight]
  fonts: {
    [window.LaserFontFamily.MONTSERRAT]: { 400: null, 500: null, 600: null, 700: null, 800: null },
    [window.LaserFontFamily.ARCHIVO]: { 400: null, 500: null, 600: null, 700: null, 800: null },
    [window.LaserFontFamily.OSWALD]: { 400: null, 500: null, 600: null, 700: null, 800: null },
    [window.LaserFontFamily.PACIFICO]: { 400: null },
    [window.LaserFontFamily.BENTILLO]: { 400: null },
    [window.LaserFontFamily.CAPTAIN_HEROES]: { 400: null },
    [window.LaserFontFamily.HELODERMA]: { 400: null },
    [window.LaserFontFamily.RELIEF_SINGLELINE]: { 400: null },
    [window.LaserFontFamily.RETRO_WORLD]: { 400: null }
  },

  // Initialized dynamically by Paper.js in app.js
  DESIGN_CENTER: null,

  /**
   * Serializes current personalization state into a structured JSON payload for Shopify order/cart
   * @returns {Object} Normalized ornament specification payload
   */
  getOrderPayload: function () {
    const config = window.LaserEvents && window.LaserEvents.getDesignConfig ? window.LaserEvents.getDesignConfig() : {};
    return {
      template: window.LaserState.selectedTemplate || 'elf',
      mainText: config.mainText || '',
      topText: config.topText || '',
      bottomText: config.bottomText || '',
      fontFamily: config.fontFamily || 'archivo',
      fontWeight: config.fontWeight || 600,
      sizeInches: config.sizeInches || 4.0,
      woodSpecies: window.LaserState.selectedWoodSpecies || 'birch',
      woodThicknessMm: window.LaserState.selectedThickness || 5.0,
      layer2: {
        enabled: !!window.LaserState.layer2.enabled,
        colorId: window.LaserState.layer2.colorId || 'green',
        colorHex: window.LaserState.layer2.color || '#059669',
        colorName: window.LaserState.layer2.name || 'Emerald Pine Green',
        material: window.LaserState.layer2.material || 'acrylic_gloss',
        thicknessMm: window.LaserState.layer2.thickness || 3.0
      },
      timestamp: new Date().toISOString()
    };
  }
};

// Global interop aliases
window.LaserStudio.State = window.LaserState;
window.getOrnamentOrderPayload = function () {
  return window.LaserState.getOrderPayload();
};
window.getDesignOrderData = function () {
  return window.LaserState.getOrderPayload();
};


/* ==========================================================================
   2D MODULE: laser-studio-font-loader.js
   ========================================================================== */

/**
 * Laser Ornament Studio - Font Ingestion & OpenType Fallback Engine
 * Module: assets/laser-studio-font-loader.js
 * Responsibility: Asynchronously downloads, parses, and caches OpenType font binaries
 * into window.LaserState.fonts using Shopify CDN URLs with jsDelivr fallback.
 */

window.LaserStudio = window.LaserStudio || {};

window.LaserFontLoader = {
  /**
   * Asynchronously loads all vector fonts defined in window.LaserStudioFontManifest
   * @param {Function} onComplete - Callback executed once all fonts have loaded or resolved
   */
  loadFonts: function (onComplete) {
    window.LaserState = window.LaserState || {};
    window.LaserState.fonts = window.LaserState.fonts || {};
    const state = window.LaserState;

    const fontManifest = window.LaserStudioFontManifest || [
      // Fallback default manifest if snippet was not rendered
      { family: 'montserrat', weight: 400, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/montserrat/files/montserrat-latin-400-normal.woff' },
      { family: 'montserrat', weight: 500, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/montserrat/files/montserrat-latin-500-normal.woff' },
      { family: 'montserrat', weight: 600, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/montserrat/files/montserrat-latin-600-normal.woff' },
      { family: 'montserrat', weight: 700, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/montserrat/files/montserrat-latin-700-normal.woff' },
      { family: 'montserrat', weight: 800, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/montserrat/files/montserrat-latin-800-normal.woff' },
      { family: 'archivo', weight: 400, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/archivo/files/archivo-latin-400-normal.woff' },
      { family: 'archivo', weight: 500, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/archivo/files/archivo-latin-500-normal.woff' },
      { family: 'archivo', weight: 600, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/archivo/files/archivo-latin-600-normal.woff' },
      { family: 'archivo', weight: 700, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/archivo/files/archivo-latin-700-normal.woff' },
      { family: 'archivo', weight: 800, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/archivo/files/archivo-latin-800-normal.woff' },
      { family: 'oswald', weight: 400, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/oswald/files/oswald-latin-400-normal.woff' },
      { family: 'oswald', weight: 500, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/oswald/files/oswald-latin-500-normal.woff' },
      { family: 'oswald', weight: 600, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/oswald/files/oswald-latin-600-normal.woff' },
      { family: 'oswald', weight: 700, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/oswald/files/oswald-latin-700-normal.woff' },
      { family: 'oswald', weight: 800, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/oswald/files/oswald-latin-700-normal.woff' },
      { family: 'pacifico', weight: 400, cdn: 'https://cdn.jsdelivr.net/npm/@fontsource/pacifico/files/pacifico-latin-400-normal.woff' },
      { family: 'bentillo', weight: 400, local: 'laser-font-bentillo.otf' },
      { family: 'captain-heroes', weight: 400, local: 'laser-font-captain-heroes.otf' },
      { family: 'heloderma', weight: 400, local: 'laser-font-heloderma.otf' },
      { family: 'relief-singleline', weight: 400, local: 'laser-font-relief-singleline.otf' },
      { family: 'retro-world', weight: 400, local: 'laser-font-retro-world.otf' }
    ];

    let loadedCount = 0;
    const totalFonts = fontManifest.length;

    function checkComplete() {
      loadedCount++;
      if (loadedCount === totalFonts) {
        window.LaserFontLoader.hideLoadingOverlay();
        if (typeof onComplete === 'function') {
          onComplete();
        }
      }
    }

    if (typeof opentype === 'undefined') {
      console.error('[LaserFontLoader] opentype.js is not loaded on window.');
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    fontManifest.forEach((item) => {
      if (!state.fonts[item.family]) state.fonts[item.family] = {};

      const primaryUrl = item.local || item.cdn;
      if (!primaryUrl) {
        state.fonts[item.family][item.weight] = null;
        checkComplete();
        return;
      }

      opentype.load(primaryUrl, function (err, loadedFont) {
        if (err) {
          console.warn(`[LaserFontLoader] Could not load ${item.family} (${item.weight}) from primary URL: ${primaryUrl}. Trying fallback CDN...`, err);
          if (item.cdn && item.cdn !== primaryUrl) {
            opentype.load(item.cdn, function (cdnErr, cdnFont) {
              if (cdnErr) {
                console.error(`[LaserFontLoader] CDN fallback also failed for ${item.family} (${item.weight}):`, cdnErr);
                state.fonts[item.family][item.weight] = null;
              } else {
                state.fonts[item.family][item.weight] = cdnFont;
              }
              checkComplete();
            });
          } else {
            state.fonts[item.family][item.weight] = null;
            checkComplete();
          }
        } else {
          state.fonts[item.family][item.weight] = loadedFont;
          checkComplete();
        }
      });
    });
  },

  /**
   * Smoothly dismisses the loading overlay from the DOM
   */
  hideLoadingOverlay: function () {
    const overlay = document.getElementById('loadingOverlay');
    if (overlay) {
      overlay.style.opacity = '0';
      overlay.style.pointerEvents = 'none';
      setTimeout(() => {
        overlay.style.display = 'none';
      }, 500);
    }
  }
};

// Expose alias on LaserStudio namespace
window.LaserStudio.FontLoader = window.LaserFontLoader;


/* ==========================================================================
   2D MODULE: laser-studio-template-registry.js
   ========================================================================== */

/**
 * Laser Ornament Studio - CAD Template Registry Facade
 * File: assets/laser-studio-template-registry.js
 * Responsibility: Central catalog manager for registration and retrieval of holiday ornament frames.
 */

window.LaserStudio = window.LaserStudio || {};
window.LaserTemplates = window.LaserTemplates || {};

window.LaserStudio.TemplateRegistry = {
  /**
   * Registers a new template definition into the global registry
   * @param {Object} templateDef - Template definition matching LaserTemplate schema
   */
  register: function (templateDef) {
    if (!templateDef || !templateDef.id) {
      console.warn('[LaserStudio.TemplateRegistry] Invalid template definition:', templateDef);
      return;
    }
    window.LaserTemplates[templateDef.id] = templateDef;
  },

  /**
   * Retrieves a template by its ID
   * @param {String} templateId - Unique template identifier
   * @returns {Object|null} Template definition or null
   */
  get: function (templateId) {
    if (!templateId) return null;
    const cleanId = String(templateId).replace(/_(ornament|template)$/i, '');
    return window.LaserTemplates[templateId] || window.LaserTemplates[cleanId] || window.LaserTemplates[`${cleanId}_ornament`] || null;
  },

  /**
   * Retrieves all registered templates as an array
   * @returns {Array<Object>} List of template objects
   */
  getAll: function () {
    return Object.values(window.LaserTemplates);
  }
};

// Aliases for backwards compatibility
window.LaserStudio.Templates = window.LaserTemplates;
window.LaserStudio.registerTemplate = window.LaserStudio.TemplateRegistry.register;
window.LaserStudio.getTemplate = window.LaserStudio.TemplateRegistry.get;


/* ==========================================================================
   2D MODULE: laser-studio-template-elf.js
   ========================================================================== */

/**
 * Laser Ornament Studio - Template: Elf Ornament
 * File: assets/laser-studio-template-elf.js
 * Template ID: "elf"
 */

(function () {
  window.LaserTemplates = window.LaserTemplates || {};

  const elfTemplate = {
    id: "elf",
    name: "Elf Ornament",
    badge: "Holiday",
    description: "Decorative elf flourish with integrated hanging loop and central nameplate",
    iconSvg: `<svg viewBox="-536.6 249.1 101.6 109.7" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.5;fill:none" d="M486.39,249.19L486.94,249.28C489.75,249.85,491.68,252.46,491.39,255.31C491.31,256.09,491.07,256.84,490.68,257.52L491.88,257.65C519.4,260.95,539.19,285.71,536.36,313.28C533.5,341.19,508.55,361.49,480.63,358.62C452.84,355.77,432.57,331.01,435.26,303.2C437.61,279,456.77,259.85,480.97,257.52L480.82,257.23C479.48,254.54,480.52,251.27,483.16,249.84C484.15,249.31,485.27,249.08,486.39,249.19z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.5;fill:none" d="M441.17,298.23L440.97,299.26C439.92,304.77,439.89,310.44,440.88,315.96L530.42,315.96C531.48,310.09,531.38,304.07,530.13,298.23L441.17,298.23z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.5;fill:none" d="M481.63,319.79C481.56,325.07,481.62,327.72,481.77,330.36L482,330.78C482.33,330.93,482.57,331.22,482.64,331.58C482.7,332.29,482.75,333.01,482.78,333.73L483.86,344.75C483.63,346.19,482.88,347.5,481.75,348.43C479.33,349.38,477.15,349.08,476.16,348.77C474.71,348.35,471.43,348.27,468,349.1L466.76,349.39C472.89,352.29,479.55,353.72,486.29,353.64C492.36,353.56,498.35,352.24,503.89,349.76L503.27,349.56C499.34,348.59,494.87,348.67,493.14,349.18C490.21,349.38,487.77,347.97,486.25,344.35L487.29,333.84L488.36,330.35C488.59,326.83,488.64,323.31,488.52,319.79z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.5;fill:none" d="M495.62,328.66L491.3,328.8L490.94,331.02L495.49,331.27L496.06,328.94z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.5;fill:none" d="M474.19,328.7L474.1,330.84L478.97,331.16L479.1,328.92z"/>
    </svg>`,
    presets: {
      circleMode: "ring",
      circleRadius: 140,
      ringThickness: 18,
      useSupportBar: false,
      barHeight: 12,
      barYOffset: 0,
      fontSize: 84,
      textYOffset: 0
    },
    config: {
      targetWidthMultiplier: 2.0,
      fontSizeRatio: 28.3544,
      textCenterYOffsetRatio: 3.069432,
      slotHeightRatio: 17.728028,
      ringCenterRadiusRatio: 47.905,
      arcCenterYOffsetRatio: 4.0424,
      outerRadiusForFlatRatio: 54.863
    },
    rawSvg: `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="101.603mm" height="109.726mm" viewBox="-536.631958 249.171432 101.602783 109.726273">
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M486.391602,249.197998L486.94165,249.280518C489.756287,249.851212,491.680206,252.462372,491.390137,255.31958C491.31134,256.096588,491.07077,256.848541,490.684082,257.5271L491.887451,257.655762C519.408203,260.957581,539.198608,285.7117,536.361572,313.28418C533.500854,341.196259,508.550781,361.499054,480.639893,358.626953C452.844849,355.776611,432.573212,331.012787,435.269287,303.202393C437.61322,279.001434,456.773987,259.854034,480.976563,257.5271L480.822021,257.236084C479.48819,254.542572,480.524261,251.27655,483.167236,249.845215C484.152588,249.310455,485.276184,249.085037,486.391602,249.197998z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M481.63208,319.791992L481.585205,322.43457C481.565979,325.077637,481.629669,327.721039,481.776123,330.360107C481.802948,330.470062,481.831604,330.579559,481.864014,330.687988L482,330.783936C482.331512,330.930054,482.571869,331.22821,482.640137,331.583984C482.708893,332.299805,482.756836,333.017487,482.783936,333.736084L482.856201,334.395508C483.000854,335.403717,483.308533,336.381653,483.766846,337.29126L484.031982,337.919922C484.180054,338.236206,484.190033,338.6008,484.048096,338.919922C483.888031,339.211731,483.682739,339.47644,483.439941,339.704102L483.407959,339.76001L483.399902,339.808105L483.541016,340.299805C483.93103,341.750885,484.042389,343.262604,483.868896,344.755127C483.635193,346.196533,482.883789,347.502319,481.75708,348.431152C481.056152,348.970764,480.216553,349.300354,479.335938,349.384033C478.599976,349.384644,477.867523,349.28244,477.159912,349.080078L476.166504,348.772217C475.683319,348.625977,475.198395,348.486298,474.711426,348.353027C473.63678,348.09079,472.518188,348.06192,471.432129,348.271973C470.282196,348.5242,469.137817,348.801636,468,349.104004L467.552002,349.208008L467.222168,349.271729C467.064117,349.298492,466.90921,349.341278,466.76001,349.399902C466.767822,349.419037,466.770844,349.438599,466.783936,349.456055C472.89093,352.293976,479.556824,353.725739,486.290527,353.645752C492.362793,353.566345,498.354431,352.243896,503.895996,349.76001L503.919922,349.711914L503.864014,349.687988C503.757599,349.658264,503.649231,349.635803,503.539307,349.624268L503.271973,349.568115C501.960205,349.254211,500.650146,348.92868,499.343994,348.592041L498.788818,348.475098C497.485107,348.244202,496.146027,348.311462,494.87207,348.672119L493.144043,349.184082C492.193176,349.467743,491.191162,349.536957,490.210449,349.385742C489.266632,349.199005,488.411407,348.704071,487.779541,347.978516C486.86911,346.980682,486.329895,345.699585,486.251953,344.351074C486.155151,343.34314,486.213318,342.326324,486.424072,341.335938L486.776123,339.887939L486.719971,339.743896L486.459717,339.483887C486.285675,339.333618,486.155029,339.139679,486.082031,338.921631C485.964844,338.541962,486.014801,338.131226,486.208008,337.783936L486.402344,337.368164C486.897339,336.253754,487.199371,335.063477,487.295898,333.8479L487.407959,331.583984L487.48291,331.365234C487.621979,331.087158,487.869202,330.878174,488.167969,330.791992L488.238281,330.734619L488.303955,330.672119C488.335602,330.56778,488.354889,330.460297,488.36792,330.352051C488.595367,326.836945,488.648834,323.312469,488.528076,319.791992L481.63208,319.791992z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M508.866211,334.872314L508.87207,334.904053C508.99939,335.093567,509.134155,335.277985,509.277344,335.455811L509.435791,335.689697C509.737183,336.168396,509.945068,336.69989,510.048096,337.256104C510.101471,337.911011,510.101288,338.569183,510.048096,339.224121C509.719086,340.860657,508.578094,342.216187,507.021484,342.819092C505.032654,343.631622,502.782715,343.498322,500.904053,342.456055C499.689178,341.85022,498.690948,340.883698,498.048096,339.687988L497.672119,338.984131C497.638428,338.928223,497.602661,338.873566,497.560059,338.823975L497.536133,338.832031L497.511963,339.12793L497.525635,339.311279C497.558411,339.978424,497.153656,340.589752,496.52417,340.813232C496.166504,340.917938,495.781128,340.855988,495.471924,340.647949C494.840179,340.158905,494.273102,339.591614,493.783936,338.959961L493.495605,339.651367L493.191895,340.335938C492.934357,340.864777,492.387482,341.190521,491.800049,341.159912C491.490601,341.076691,491.213715,340.901764,491.008057,340.656006C490.670258,340.090149,490.355377,339.510986,490.063965,338.919922L489.991943,338.855957L489.936035,338.855957L489.87207,338.912109C489.070526,340.544006,488.693604,342.351715,488.776123,344.167969C488.850922,344.992523,489.245819,345.754761,489.877686,346.289795C490.466949,346.746002,491.232147,346.907684,491.955566,346.72876C492.587219,346.617004,493.209045,346.45578,493.815918,346.248047L494.482422,346.044678C496.051819,345.619995,497.696106,345.550232,499.295898,345.840088C500.571777,346.127197,501.836609,346.460815,503.088135,346.840088L503.762939,346.986572C504.744263,347.170319,505.747772,347.204102,506.739258,347.086914C508.86142,346.777252,510.674377,345.396027,511.536133,343.432129C512.510864,341.371613,512.445374,338.970123,511.361572,336.964844C510.79953,335.989166,509.917511,335.23761,508.864014,334.840088L508.866211,334.872314z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M461.378906,334.819336L460.986572,335.041748C459.834961,335.771973,459.022217,336.93222,458.72998,338.26416C458.161774,340.478485,458.682739,342.830231,460.133301,344.597168C461.457947,346.16153,463.443634,347.009155,465.489502,346.881348C466.219055,346.841797,466.942474,346.726105,467.647949,346.536133L468.848633,346.191895C469.471771,346.016754,470.09903,345.856171,470.729736,345.710693C472.313416,345.385101,473.949585,345.415649,475.52002,345.800049C476.19455,345.988739,476.867645,346.183716,477.538574,346.384766L478.191895,346.568115C479.39624,346.92627,480.673401,346.291565,481.114258,345.11499C481.290009,344.713745,481.394318,344.284943,481.424072,343.8479C481.439331,342.078217,481.021454,340.331757,480.208008,338.76001L480.159912,338.719971L480.008057,338.951904L479.633789,339.700928C479.487488,340.010254,479.323242,340.31076,479.14209,340.601074L479.020996,340.743896C478.572296,341.194763,477.849945,341.220581,477.371826,340.801025C477.246002,340.694946,477.141846,340.565002,477.072021,340.416016C476.90686,340.051453,476.74942,339.68335,476.600098,339.312012L476.407959,338.904053C476.344055,338.954285,476.282867,339.007813,476.224121,339.063965C476.018799,339.2789,475.829987,339.508972,475.657715,339.751221L475.479248,339.9646C475.254333,340.263184,474.958221,340.501678,474.616699,340.653564C474.053864,340.887482,473.405151,340.686432,473.072021,340.176025C472.868744,339.714172,472.74057,339.222626,472.696045,338.719971L472.664063,338.711914L472.624023,338.751953C472.474396,339.001587,472.334106,339.256683,472.202637,339.516357L471.959961,339.904053C470.309692,342.467316,467.191498,343.669708,464.247314,342.878906C462.575195,342.459076,461.22049,341.23645,460.63208,339.615967C460.415588,338.822479,460.365692,337.992767,460.484863,337.178955C460.659424,336.219452,461.113831,335.332916,461.791992,334.63208L461.378906,334.819336z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M499.055908,319.791992L498.945068,321.160156C498.707977,324.354187,498.624817,327.557983,498.696045,330.76001L498.719971,330.919922L498.921875,331.029297C499.11438,331.155273,499.272919,331.32663,499.384033,331.528076C499.540894,332.041809,499.635529,332.57254,499.665283,333.108887L499.750244,333.627441C500.016815,334.915192,500.595032,336.11795,501.43457,337.130127L501.593262,337.362305C501.693237,337.521423,501.778442,337.689392,501.8479,337.864014C501.945221,338.476105,501.693726,339.092346,501.191895,339.456055L501.208008,339.536133C502.126282,340.301758,503.275543,340.735657,504.470703,340.76709C505.302551,340.773224,506.113251,340.508392,506.783447,340.015625C507.561157,339.3685,507.836395,338.295044,507.461182,337.355469C507.192932,336.756805,506.511322,336.463867,505.894043,336.686035L505.758057,336.757568C505.566833,336.875916,505.413513,337.048218,505.328125,337.256104C505.310272,337.42572,505.315002,337.596893,505.341309,337.765381L505.34082,337.962891C505.309387,338.646393,504.777679,339.200653,504.096436,339.264404C503.325714,339.331635,502.644165,338.772705,502.549561,338.004883C502.53775,337.633667,502.603668,337.264557,502.742676,336.920166L502.800049,336.664063C502.834167,336.192627,502.863556,335.720093,502.887939,335.248047L502.932129,334.9646C503.306061,333.449249,504.572052,332.319183,506.120117,332.120117C507.034088,332.051086,507.952454,332.064178,508.864014,332.159912C509.76236,332.360199,510.618347,332.71759,511.39209,333.216064C512.52124,334.040192,513.398926,335.162109,513.927979,336.456055C514.487,337.634003,514.755615,338.928802,514.711914,340.231934C514.763672,341.248627,514.666626,342.267426,514.424072,343.256104C521.121582,337.79892,526.115234,330.53656,528.813232,322.329346C529.085754,321.489838,529.33728,320.643921,529.568115,319.791992L499.055908,319.791992z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M441.864014,319.791992L442.294678,321.277588C444.573792,328.668762,448.698608,335.3573,454.280029,340.711914C454.91806,341.402069,455.613892,342.036438,456.360107,342.60791C456.190582,341.975739,456.094238,341.326233,456.072021,340.672119C456.053436,340.094513,456.066223,339.516327,456.111328,338.940186C456.255371,337.278381,456.856598,335.68927,457.849121,334.348633C459.269897,332.596802,461.487457,331.692413,463.728027,331.951904C464.927124,332.019775,466.040466,332.593719,466.794434,333.528564C467.223846,334.091309,467.481079,334.766205,467.536133,335.471924L467.552002,336.384033C467.582794,336.674988,467.65625,336.959747,467.769531,337.229492L467.812744,337.492676C467.843811,337.864868,467.73819,338.235382,467.518066,338.537109C467.058655,339.099823,466.257355,339.236481,465.630127,338.870117C465.297913,338.671722,465.076782,338.329285,465.040039,337.944092L465.023926,337.47998C465.033173,336.982513,464.649261,336.564911,464.1521,336.543945C463.581696,336.515381,463.069702,336.888824,462.919922,337.439941C462.860565,337.797333,462.856995,338.161957,462.912109,338.52002C463.144836,339.210083,463.640747,339.780029,464.292969,340.104004C465.85083,340.877228,467.724792,340.599213,468.991943,339.407959L469.016602,339.345947L469.023926,339.280029C468.935181,339.153015,468.835358,339.034149,468.726563,338.923828L468.615967,338.759033C468.349823,338.270844,468.409851,337.668976,468.770264,337.245605C468.920471,337.011688,469.075623,336.780884,469.23584,336.553711L469.535156,336.048584C470.222443,334.812805,470.605438,333.431122,470.651611,332.017822L470.675537,331.863525C470.764008,331.487244,471.016479,331.168976,471.36792,331.008057L471.441162,330.924561C471.506287,330.805969,471.532623,330.671509,471.536133,330.536133C471.629517,326.950409,471.503815,323.362427,471.159912,319.791992L441.864014,319.791992z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M495.624023,328.664063L491.303955,328.808105L491.184082,328.832031L491.072021,328.864014L491.016113,328.968018C490.93277,329.649933,490.908722,330.33783,490.944092,331.023926L490.976074,331.095947C491.021393,331.124939,491.070099,331.148041,491.120117,331.167969C492.574493,331.272186,494.032684,331.309143,495.490479,331.278564C495.627106,331.268707,495.762848,331.24823,495.895996,331.216064L495.991943,331.120117L496.023926,331.055908L496.048096,330.991943C496.105499,330.31073,496.110657,329.626129,496.063965,328.944092L496.03418,328.818359L495.976074,328.704102C495.89386,328.664429,495.803101,328.644714,495.711914,328.647949L495.624023,328.664063z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M474.191895,328.704102L474.167969,328.728027C474.092743,329.432007,474.07135,330.140656,474.104004,330.8479C474.12204,330.93219,474.147675,331.014526,474.176025,331.095947C474.249878,331.132233,474.324066,331.167938,474.399902,331.199951C475.924225,331.294434,477.453247,331.283722,478.976074,331.167969C479.043579,331.130951,479.106354,331.086273,479.167969,331.040039C479.239807,330.336273,479.218292,329.626099,479.104004,328.927979L479.048096,328.8479C478.889618,328.797211,478.725983,328.764648,478.560059,328.751953L477.13208,328.751953C476.162262,328.753876,475.192657,328.729919,474.224121,328.679932L474.191895,328.704102z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M474.03125,324.799561L474.041504,325.223145C474.057495,325.505341,474.091675,325.786224,474.144043,326.063965C475.716003,326.277252,477.305939,326.32547,478.887939,326.208008C478.918427,326.191132,478.947083,326.171387,478.976074,326.1521C479.049683,325.653839,479.028137,325.146149,478.912109,324.656006C478.876953,324.613647,478.837433,324.575104,478.791992,324.543945L478.728027,324.503906C478.538086,324.468201,478.345337,324.449463,478.1521,324.447998L477.317871,324.463867C476.227203,324.473083,475.136688,324.443665,474.048096,324.375977L474.03125,324.799561z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M494.124268,324.400146C493.120453,324.432495,492.116272,324.44046,491.112061,324.424072C491.096649,324.936035,491.101715,325.448425,491.12793,325.959961L491.13916,326.023682L491.163086,326.08252L491.199463,326.134277L491.248047,326.176025L491.552002,326.216064C493.034241,326.29483,494.520599,326.23584,495.991943,326.040039L496.095947,325.944092C496.199799,325.425568,496.234436,324.89563,496.199951,324.36792L495.976074,324.343994L494.124268,324.400146z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M493.12793,320.583984L491.511963,320.664063L491.424072,320.664063L491.337646,320.674072L491.258789,320.707031L491.192627,320.760498L491.144043,320.832031C491.082611,321.111664,491.083649,321.401947,491.1521,321.679932L491.209473,321.743408L491.280029,321.791992C492.030731,321.887512,492.789032,321.908783,493.543945,321.855957C494.466034,321.855255,495.387329,321.804474,496.303955,321.704102C496.384613,321.393646,496.412842,321.072052,496.39209,320.751953L496.328125,320.687988L496.263916,320.615967C496.035034,320.567474,495.802032,320.541138,495.568115,320.536133L493.12793,320.583984z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M475.902344,320.592773L474.950439,320.633545L474,320.704102L473.92334,320.759033L473.8479,320.815918C473.813782,321.126648,473.836212,321.440674,473.912109,321.743896C475.417145,321.884125,476.930298,321.91629,478.439941,321.840088L478.87207,321.783936C478.919586,321.764709,478.957764,321.726746,478.968994,321.676514L479,321.60791C479.044647,321.341064,479.06366,321.070496,479.055908,320.800049L479.031982,320.728027L479,320.696045C478.800842,320.631989,478.59317,320.597107,478.384033,320.600098L477.808105,320.600098L476.855225,320.581543L475.902344,320.592773z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M441.176025,298.23999L440.970459,299.261475C439.924133,304.778687,439.89624,310.440765,440.887939,315.968018L530.424072,315.968018C531.485168,310.096436,531.387329,304.073975,530.135986,298.23999L441.176025,298.23999z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M466.04541,266.832764L464.956299,267.381348C454.120544,273.019531,445.981567,282.747284,442.343994,294.407959L472.048096,294.407959C471.964111,293.571991,471.935883,292.731262,471.9646,291.891602L471.917725,291.242188C471.781036,290.188721,471.4198,289.17691,470.85791,288.275391C470.325134,287.410278,469.677979,286.621002,468.934326,285.928711L468.615967,285.560059C468.17569,284.940979,468.316742,284.084076,468.928955,283.634277C469.056519,283.537384,469.197968,283.459473,469.352051,283.416016C470.763428,283.118958,472.20993,283.02475,473.647949,283.135986L473.793701,283.116211C473.923462,283.100891,474.047089,283.047028,474.135986,282.951904C475.354004,281.370758,476.196716,279.53363,476.599854,277.578857C476.707184,276.846771,476.664276,276.100708,476.476563,275.38501C476.159698,274.330963,475.330017,273.509827,474.273193,273.202393C473.273926,272.866913,472.187469,272.901062,471.211914,273.300293C469.93396,273.895233,469.14743,275.208405,469.224121,276.615967L469.256104,276.687988C469.397644,276.799713,469.557007,276.886902,469.728027,276.944092L469.937256,277.041748C471.263,277.72345,471.849609,279.304016,471.289551,280.685547C470.696106,282.198334,469.007904,282.964539,467.47876,282.414551C466.209564,281.916931,465.45874,280.60257,465.672119,279.256104L465.695557,279.105469C465.717529,278.907715,465.675903,278.70816,465.575928,278.536133L465.241455,278.096924C464.694275,277.343994,464.316772,276.480957,464.13623,275.567871C463.787811,273.973846,463.901642,272.313354,464.464844,270.781982C464.999939,269.220428,465.855438,267.787933,466.976074,266.575928C467.021729,266.507599,467.069824,266.440948,467.120117,266.375977L467.1521,266.312012L466.04541,266.832764z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M484.677979,262.345215L483.823975,262.36792L483.76001,262.39209L483.808105,262.407959C484.325592,262.526428,484.847382,262.625366,485.372314,262.70459L485.99707,262.826172C489.737183,263.594818,493.235138,265.259094,496.190674,267.676514C498.494537,269.5737,500.391388,271.917175,501.766602,274.565918C503.311707,277.460358,504.353516,280.596527,504.8479,283.840088L504.87207,283.904053C504.940704,283.941132,505.013275,283.970093,505.088135,283.991943C505.726837,284.045074,506.367218,284.077301,507.008057,284.088135L507.539063,284.130371C508.776001,284.257599,509.984711,284.581177,511.120117,285.088135C511.391602,285.272827,511.576385,285.558868,511.64209,285.880615C511.768372,286.447662,511.5401,287.035095,511.063965,287.36792C510.200104,287.830566,509.334625,288.286041,508.464111,288.736084L507.965576,289.030273C506.828735,289.761139,505.902985,290.776733,505.280029,291.976074C504.927551,292.749084,504.68512,293.567657,504.560059,294.407959L529.135986,294.407959C523.998108,277.673798,509.69873,265.382935,492.382568,262.817383C489.832916,262.437378,487.254974,262.279388,484.677979,262.345215z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M495.944092,282.991943C495.086853,284.603668,493.926849,286.035004,492.528076,287.208008C491.684326,287.968536,490.755585,288.629089,489.76001,289.176025C489.064484,289.416077,488.296356,289.136108,487.919922,288.503906C487.799988,288.277618,487.693268,288.044556,487.601563,287.80542L487.393799,287.371094C486.609741,285.812012,485.471954,284.458008,484.072021,283.416016L483.984131,283.647949L483.700928,284.452393C482.979034,286.307312,481.838104,287.970215,480.36792,289.312012C479.819855,289.721985,479.051208,289.652252,478.583984,289.1521C478.331726,288.891815,478.090485,288.621094,477.860352,288.341064L477.445068,287.916504C477.158997,287.642273,476.855072,287.387329,476.536133,287.1521C475.500946,286.480804,474.297638,286.11438,473.063965,286.095947L473.008057,286.112061L473.008057,286.159912L473.631348,287.128174C474.409668,288.424591,474.875214,289.884491,474.991943,291.39209L475.031982,292.384033L475.023438,292.612061C475.031799,292.840088,475.074066,293.065613,475.1521,293.280029C475.216705,293.329346,475.294342,293.35965,475.375977,293.352051C475.969269,293.203369,476.559723,293.043213,477.146729,292.871338L478.120117,292.60791C483.15918,291.21991,488.422302,290.840027,493.608398,291.490234C495.535614,291.750305,497.437988,292.169556,499.295898,292.743896L501.640137,293.528076L501.815918,292.815918C502.366272,290.564911,503.64624,288.559296,505.456055,287.112061L505.47998,287.072021L505.432129,287.055908C504.048584,287.021851,502.683014,287.373901,501.488037,288.072021C500.904633,288.484497,500.335632,288.916992,499.781982,289.368652L499.509766,289.535889L499.224121,289.679932C498.576477,289.846069,497.901276,289.534149,497.609863,288.932373L497.511963,288.664063C497.437103,288.119659,497.383881,287.57254,497.352051,287.023926L497.292725,286.488037C497.118011,285.24173,496.697479,284.042633,496.055908,282.959961L496.023926,282.936035L495.944092,282.991943z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M490.104004,274.256104L489.457031,274.266846C486.108246,274.365173,482.830597,275.255737,479.892822,276.866211C479.800018,276.929993,479.71286,277.001617,479.63208,277.080078L479.60791,277.1521L479.583984,277.656006C479.34375,279.414246,478.759338,281.107941,477.864014,282.640137L477.016113,284.080078L477.040039,284.112061C477.235901,284.2341,477.440704,284.341003,477.651855,284.434082L478.037598,284.647217C478.599487,284.984558,479.13269,285.367584,479.63208,285.791992C480.661224,284.352417,481.360809,282.703766,481.681152,280.963379L481.800049,280.432129C481.936798,279.955353,482.298187,279.575653,482.769287,279.42041C483.171173,279.322754,483.595703,279.372253,483.959961,279.568115C486.209991,281.033569,488.121796,282.961792,489.568115,285.224121L489.662354,285.344971C489.703888,285.414459,489.771179,285.462921,489.8479,285.488037L489.925781,285.477295L490,285.447998C492.037476,284.052094,493.464966,281.930878,493.992432,279.518066L494.120117,279.072021C494.243225,278.699127,494.520447,278.397614,494.880127,278.23999C495.388885,278.080627,495.943176,278.189575,496.356445,278.526367C496.488617,278.623596,496.613739,278.730164,496.728027,278.8479C498.424194,280.677521,499.584045,282.938965,500.080078,285.384033C500.434784,285.239044,500.781891,285.076141,501.120117,284.895996C501.356628,284.796234,501.591248,284.692291,501.823975,284.583984C501.865021,284.560822,501.8974,284.525055,501.912109,284.47998C501.512177,281.345612,500.506744,278.318878,498.951904,275.568115L498.87207,275.488037L498.773682,275.415527L498.664063,275.360107C496.50531,274.766602,494.29068,274.399323,492.055908,274.263916C491.405396,274.245483,490.754669,274.24292,490.104004,274.256104z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M485.561279,266.337646L484.654297,266.802734C483.416168,267.469482,482.235901,268.238617,481.125732,269.101807C479.981781,270.009521,478.943939,271.043488,478.031982,272.184082L478.445557,272.799316C478.685852,273.160065,478.900787,273.537079,479.088135,273.927979L479.112061,273.919922C479.69928,273.68042,480.281555,273.429443,480.858887,273.166992L481.723389,272.82251C485.358307,271.490845,489.243256,270.982758,493.098389,271.334717C494.070557,271.439789,495.03717,271.549225,496.008057,271.664063L496,271.624023C493.434814,268.857056,490.135925,266.876068,486.488037,265.912109L485.561279,266.337646z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M470.218018,267.664551L469.792969,267.995605C468.494598,269.078705,467.538177,270.514221,467.038086,272.129395C466.880432,272.712891,466.805481,273.315613,466.815918,273.919922L466.840088,273.895996L467.062988,273.407227C467.634552,272.290405,468.545044,271.383179,469.664063,270.815918L470.464111,270.471924L470.519043,270.400391L470.543945,270.312012C470.642548,269.331299,470.682587,268.345612,470.664063,267.360107L470.218018,267.664551z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M478.821289,265.015625L478.025879,265.060303C476.700531,265.159424,475.387482,265.383026,474.104004,265.728027C474.059448,265.757019,474.020691,265.793488,473.984131,265.832031C473.677643,267.178894,473.516571,268.554779,473.503906,269.936035L473.543945,270.023926L473.60791,270.080078C473.937775,270.163025,474.271515,270.229828,474.60791,270.280029C474.878754,270.346497,475.150909,270.40802,475.424072,270.464111C475.624084,270.374878,475.790131,270.223724,475.895996,270.031982L476.456543,269.414795C477.979309,267.788971,479.699036,266.35965,481.575928,265.159912L481.640137,265.080078C480.702515,265.00528,479.761353,264.983673,478.821289,265.015625z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M485.570801,252.231201L485.318848,252.269775C483.951263,252.551346,483.067413,253.883606,483.339111,255.253174C483.5979,256.544067,484.797638,257.425873,486.106445,257.283203C487.487518,257.131134,488.490662,255.898544,488.358887,254.515381C488.234253,253.209122,487.133484,252.214005,485.821289,252.219971L485.570801,252.231201z"/>
</svg>`
  };

  window.LaserTemplates.elf = elfTemplate;
})();


/* ==========================================================================
   2D MODULE: laser-studio-template-gingerbread.js
   ========================================================================== */

/**
 * Laser Ornament Studio - Template: Gingerbread Ornament
 * File: assets/laser-studio-template-gingerbread.js
 * Template ID: "gingerbread_ornament"
 */

(function () {
  window.LaserTemplates = window.LaserTemplates || {};

  const gingerbreadTemplate = {
    id: "gingerbread",
    name: "Gingerbread Ornament",
    badge: "Character",
    description: "Festive gingerbread man with bowtie & icing flourish",
    iconSvg: `<svg viewBox="-551.71 113.16 101.62 109.75" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M501.46,113.18C503.35,113.37,505,114.5,505.87,116.18C506.74,117.86,506.69,119.87,505.76,121.51L506.96,121.64C534.48,124.94,554.28,149.7,551.44,177.27C548.59,205.01,523.93,225.27,496.17,222.68C468.61,220.1,448.18,195.94,450.23,168.33C452.05,143.65,471.41,123.87,496.05,121.51L495.89,121.22C494.56,118.53,495.6,115.26,498.24,113.83C499.22,113.3,500.35,113.07,501.46,113.18z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M500.62,196.05L500.29,196.1C499.55,196.27,498.91,196.72,498.52,197.37L495.09,202.99L491.65,208.61C491.06,209.61,490.39,210.56,489.66,211.46C488.73,212.5,487.6,213.36,486.34,213.96C495.75,217.22,505.97,217.22,515.38,213.98C513.5,213.18,511.89,211.86,510.73,210.18C509.69,208.53,508.67,206.86,507.67,205.18C506.12,202.63,504.57,200.07,503.05,197.51L502.83,197.21C502.4,196.57,501.72,196.14,500.96,196.05L500.62,196.05z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M521.23,195.88L520.9,196.02C520.45,196.24,520.15,196.67,520.11,197.16C520.06,197.53,520.07,197.91,520.15,198.27C520.21,198.63,520.29,198.98,520.38,199.33L520.45,199.73C520.5,199.99,520.53,200.25,520.54,200.52C520.43,200.56,520.31,200.58,520.2,200.6C520.08,200.61,519.96,200.61,519.85,200.6C519.29,200.43,518.74,200.25,518.19,200.05L517.77,199.91C517.34,199.79,516.9,199.74,516.45,199.76C515.76,199.85,515.21,200.37,515.09,201.06C515.05,201.53,515.09,202.01,515.21,202.47L515.52,203.56C515.6,203.87,515.65,204.19,515.67,204.51L515.66,204.55L515.65,204.59C515.52,204.73,515.33,204.79,515.15,204.77C514.43,204.55,513.71,204.32,513,204.08L512.64,204.01C512.25,203.92,511.85,203.93,511.46,204.02C510.8,204.23,510.34,204.83,510.33,205.52C510.34,205.73,510.4,205.93,510.51,206.1C511.17,207.16,511.85,208.2,512.55,209.22L512.53,208.78C512.48,208.19,512.38,207.61,512.25,207.03L512.16,206.44C512.16,206.21,512.26,205.99,512.44,205.84C513.13,206.03,513.82,206.25,514.49,206.51L514.8,206.61C515.23,206.74,515.67,206.81,516.11,206.81C516.88,206.75,517.51,206.19,517.65,205.43C517.73,204.84,517.66,204.25,517.46,203.69L517.26,203.04C517.13,202.61,517.08,202.17,517.12,201.72L517.41,201.76C518.01,201.87,518.6,202.05,519.16,202.3L519.69,202.45C520.04,202.53,520.41,202.58,520.77,202.6C521.5,202.64,522.17,202.17,522.37,201.47C522.48,200.95,522.47,200.42,522.36,199.9L522.11,198.76C522.05,198.45,522.01,198.13,522.01,197.81C522.23,197.69,522.47,197.63,522.72,197.62L522.39,197.18C522.1,196.71,521.82,196.24,521.57,195.75L521.23,195.88z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M479.55,196.43C479.34,196.84,479.11,197.25,478.88,197.64L479.37,197.84C479.32,198.42,479.23,198.99,479.09,199.55L479.02,200.02C478.97,200.43,478.97,200.85,479.05,201.26C479.23,202.06,479.95,202.63,480.77,202.61C481.41,202.58,482.03,202.42,482.61,202.14L483.03,201.99C483.41,201.84,483.81,201.74,484.22,201.69L484.26,201.97C484.28,202.25,484.25,202.53,484.18,202.8L483.99,203.45C483.85,203.93,483.77,204.43,483.75,204.93C483.76,205.65,484.18,206.3,484.84,206.6C485.41,206.77,486.02,206.76,486.59,206.56C486.98,206.44,487.36,206.3,487.73,206.15L488.16,206.02C488.44,205.93,488.74,205.87,489.03,205.82L489.31,206.14C489.29,206.73,489.23,207.32,489.12,207.91L489.01,208.74L488.98,209.12C489.32,208.61,489.66,208.11,490.02,207.62L490.44,206.97C490.71,206.52,490.96,206.07,491.2,205.61C491.24,205,490.95,204.42,490.45,204.09C490.19,203.91,489.89,203.82,489.58,203.84C489.13,203.83,488.68,203.9,488.26,204.05L487.24,204.4L486.21,204.71C485.99,204.7,485.81,204.53,485.79,204.31C485.83,203.84,485.92,203.37,486.06,202.92L486.22,202.29C486.33,201.85,486.35,201.38,486.27,200.93C486.1,200.25,485.51,199.76,484.81,199.71C484.35,199.68,483.88,199.75,483.45,199.9L482.7,200.18C482.29,200.33,481.86,200.46,481.44,200.58L481.37,200.6L481.29,200.59L481.16,200.55C481.05,200.48,480.96,200.37,480.93,200.24C480.96,199.66,481.06,199.08,481.21,198.51L481.28,198.03C481.31,197.71,481.31,197.39,481.29,197.07C481.17,196.51,480.78,196.05,480.25,195.86L479.93,195.7L479.55,196.43z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M522.65,193.51L523.58,195.38C523.86,195.99,524.22,196.54,524.66,197.04C525.23,197.67,525.97,198.12,526.79,198.34C527.42,198.54,528.06,198.7,528.71,198.81L529.25,198.95C531.05,199.37,532.55,200.64,533.26,202.35C535.77,199.68,537.93,196.71,539.69,193.51L522.65,193.51z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M462.1,193.51L462.77,194.68C464.37,197.38,466.25,199.9,468.39,202.2C468.68,201.51,469.11,200.89,469.65,200.36C470.34,199.69,471.19,199.2,472.12,198.94C472.73,198.8,473.35,198.67,473.97,198.55L474.38,198.45C475.03,198.33,475.65,198.08,476.2,197.7C476.61,197.41,476.97,197.05,477.25,196.63C477.6,196.15,477.9,195.64,478.14,195.11L478.91,193.51L462.1,193.51z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M461.95,172L456.58,172.02C456.56,178.44,457.94,184.78,460.63,190.6L541.17,190.6C543.85,184.78,545.23,178.44,545.21,172.02L537.47,172.02L461.95,172z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M487.8,129.75L486.35,130.22C469.55,136.05,457.87,151.37,456.69,169.11L472.87,169.11C472.33,168.94,471.8,168.74,471.29,168.51C468.54,167.22,466.87,164.37,467.07,161.34C467.4,157.18,470.87,153.98,475.05,153.98C475.61,153.97,476.18,154,476.74,154.09C477.51,154.17,478.28,154.28,479.05,154.4L480.13,154.54L482.53,154.82L484.92,155.14C486.51,155.35,488.11,155.51,489.72,155.6C487.4,153.52,485.65,150.88,484.63,147.93C483.31,144.11,483.34,139.95,484.72,136.15C485.67,133.54,487.24,131.2,489.28,129.32L487.8,129.75z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M512.92,129.88C516.78,133.83,518.57,139.36,517.75,144.83C517.13,149.01,515.03,152.83,511.84,155.59C512.83,155.56,513.83,155.48,514.81,155.37L525.28,154.03C527.07,153.79,528.89,154.13,530.48,154.99C532.38,156.04,533.76,157.82,534.31,159.92C535.24,163.24,533.71,166.76,530.65,168.35C530.03,168.67,529.38,168.93,528.7,169.11L545.12,169.11C544.07,153.56,534.94,139.71,521.06,132.61C518.26,131.18,515.31,130.05,512.27,129.26L512.92,129.88z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M523.6,156.13L522.56,156.25L522.01,156.86L521.43,157.44C521.03,157.82,520.7,158.26,520.45,158.75C520.16,159.44,520.35,160.23,520.91,160.71C521.35,161.03,521.85,161.25,522.39,161.34C522.73,161.43,523.07,161.5,523.41,161.57L523.63,161.63C523.87,161.71,524.11,161.8,524.33,161.92L524.2,162.15C523.89,162.62,523.52,163.04,523.1,163.41L522.74,163.82C522.52,164.1,522.31,164.4,522.14,164.72C521.83,165.46,522.05,166.31,522.69,166.8C523.12,167.1,523.61,167.31,524.13,167.44L524.93,167.63C525.33,167.75,525.72,167.91,526.08,168.12C526.62,167.88,527.17,167.66,527.73,167.48L527.66,167.15C527.61,166.69,527.28,166.31,526.84,166.18C526.54,166.06,526.23,165.97,525.92,165.92L525.2,165.81C524.78,165.72,524.38,165.55,524.02,165.3L524.21,164.99C524.63,164.43,525.1,163.92,525.6,163.45L525.91,163.09C526.1,162.84,526.28,162.58,526.43,162.3C526.7,161.73,526.59,161.04,526.13,160.59C525.77,160.24,525.3,160.02,524.81,159.96C524.41,159.89,524.01,159.82,523.61,159.77L523.35,159.71C523,159.63,522.67,159.46,522.41,159.21C522.6,158.9,522.82,158.6,523.06,158.33L523.95,157.37C524.27,156.98,524.51,156.53,524.65,156.04L523.6,156.13z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M477.02,156.5L477.24,156.94C477.41,157.23,477.61,157.49,477.84,157.73C478.1,158.02,478.39,158.3,478.68,158.57L478.82,158.75C478.91,158.88,478.98,159.01,479.03,159.16C478.96,159.34,478.82,159.49,478.64,159.56L478.46,159.62L477.32,159.86L476.18,160.12C475.86,160.22,475.56,160.39,475.3,160.62C474.83,161.09,474.74,161.81,475.08,162.38C475.29,162.81,475.6,163.2,475.97,163.51C476.26,163.81,476.55,164.1,476.85,164.38L477.02,164.57C477.23,164.83,477.37,165.13,477.44,165.45C477.16,165.57,476.88,165.67,476.58,165.74C476.32,165.81,476.06,165.86,475.8,165.89L475.31,166.01C474.98,166.1,474.67,166.22,474.37,166.36C474.1,166.52,473.91,166.78,473.86,167.09L473.83,167.38L474.73,167.73L475.63,168.1L475.85,167.95C476.07,167.8,476.32,167.7,476.58,167.64C476.86,167.57,477.14,167.51,477.42,167.46L477.72,167.38C478.03,167.29,478.32,167.18,478.61,167.06C479.28,166.72,479.66,166,479.57,165.26C479.44,164.72,479.17,164.23,478.77,163.83C478.52,163.55,478.25,163.29,477.97,163.04L477.69,162.75C477.45,162.5,477.24,162.21,477.06,161.91L477.37,161.78C477.69,161.66,478.03,161.57,478.38,161.52C478.64,161.46,478.9,161.41,479.17,161.38L479.57,161.27C480.03,161.15,480.45,160.92,480.81,160.61C481.32,160.11,481.45,159.33,481.12,158.69C480.91,158.28,480.63,157.91,480.29,157.59L479.94,157.25C479.73,157.03,479.53,156.81,479.33,156.58L479.21,156.48C479.13,156.41,479.03,156.36,478.93,156.32C478.25,156.19,477.56,156.1,476.86,156.03L477.02,156.5z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M500.65,162.11L500.41,162.13C499.01,162.32,498.02,163.59,498.18,164.99C498.36,166.34,499.57,167.3,500.92,167.17C502.26,167.06,503.28,165.92,503.25,164.58C503.22,163.27,502.2,162.19,500.89,162.11L500.65,162.11z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M507.28,145.39L507.05,145.46C506.78,145.56,506.56,145.75,506.41,145.99L506.21,146.34C505.1,148.21,503.12,149.39,500.95,149.46C498.79,149.51,496.75,148.42,495.59,146.6L495.37,146.24C495.27,146.05,495.15,145.88,495,145.73C494.6,145.36,493.98,145.36,493.59,145.74C493.28,146.04,493.17,146.49,493.31,146.9C495.15,150.57,499.36,152.38,503.29,151.18C505.49,150.52,507.31,148.98,508.32,146.91C508.53,146.45,508.39,145.9,507.98,145.6C507.78,145.45,507.53,145.38,507.28,145.39z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M506.52,136.7L506.28,136.73C504.96,136.92,504.04,138.14,504.19,139.46C504.35,140.76,505.48,141.72,506.79,141.65C508.06,141.59,509.07,140.57,509.13,139.3C509.19,138.02,508.28,136.91,507.01,136.73C506.85,136.71,506.69,136.7,506.52,136.7z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M494.89,136.72L494.66,136.75C493.39,136.95,492.49,138.09,492.59,139.38C492.7,140.64,493.74,141.62,495.01,141.64C496.31,141.65,497.39,140.65,497.48,139.36C497.58,138.06,496.65,136.92,495.37,136.74C495.21,136.71,495.05,136.71,494.89,136.72z"/>
      <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M500.64,116.22L500.39,116.26C499.02,116.54,498.14,117.87,498.41,119.24C498.67,120.53,499.86,121.4,501.16,121.27C502.55,121.13,503.56,119.89,503.43,118.5C503.31,117.2,502.21,116.2,500.9,116.2L500.64,116.22z"/>
    </svg>`,
    presets: {
      circleMode: "ring",
      circleRadius: 140,
      ringThickness: 18,
      useSupportBar: false,
      barHeight: 18.6,
      barYOffset: 0,
      fontSize: 84,
      textYOffset: 0
    },
    config: {
      targetWidthMultiplier: 2.0,
      fontSizeRatio: 28.3544,
      textCenterYOffsetRatio: 13.2708,
      slotHeightRatio: 18.600098,
      ringCenterRadiusRatio: 47.905,
      arcCenterYOffsetRatio: 4.0424,
      outerRadiusForFlatRatio: 54.863
    },
    rawSvg: `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="101.624mm" height="109.745mm" viewBox="-551.710083 113.156693 101.624023 109.745163">
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M501.463623,113.183105C503.345673,113.37278,505.003937,114.501709,505.870514,116.183113C506.737091,117.864517,506.694305,119.870148,505.756592,121.512939L506.959961,121.641602C534.483459,124.943092,554.27655,149.699158,551.439697,177.274414C548.590576,205.009277,523.928101,225.269577,496.167725,222.68042C468.608643,220.101471,448.182465,195.937836,450.227051,168.333984C452.050781,143.647308,471.406311,123.869263,496.047852,121.512939L495.893311,121.22168C494.558441,118.528122,495.595184,115.261604,498.23877,113.830322C499.224518,113.296112,500.348083,113.070549,501.463623,113.183105z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M500.62207,196.054932L500.293945,196.103027C499.554901,196.266449,498.914032,196.72316,498.517578,197.36792L495.093018,202.992676L491.653564,208.60791C491.062683,209.608139,490.394623,210.560699,489.655273,211.456787C488.728668,212.503815,487.602051,213.355011,486.341553,213.959961C495.746399,217.215851,505.971375,217.224243,515.381592,213.983887C513.504211,213.182053,511.892426,211.864639,510.733398,210.184082C509.687012,208.530151,508.665588,206.860626,507.669434,205.176025C506.116882,202.630676,504.572937,200.071777,503.04541,197.511963L502.82959,197.208008C502.403931,196.567368,501.720734,196.143631,500.95752,196.048096L500.62207,196.054932z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M521.230225,195.880127L520.901611,196.023926C520.452637,196.235733,520.150574,196.669952,520.108154,197.164551C520.055542,197.533203,520.069397,197.90834,520.149658,198.271973C520.213318,198.62825,520.290039,198.982101,520.379639,199.332764L520.453613,199.725586C520.495789,199.988815,520.525269,200.253922,520.541504,200.52002C520.430115,200.558441,520.314819,200.584,520.197998,200.599121C520.080994,200.614075,519.962585,200.614227,519.845459,200.600098C519.28833,200.431915,518.736084,200.247787,518.189453,200.048096L517.768066,199.910889C517.340088,199.794342,516.896851,199.743927,516.453613,199.76001C515.76416,199.848007,515.211731,200.372452,515.085449,201.055908C515.045776,201.531326,515.085876,202.010117,515.205566,202.471924L515.517578,203.560059C515.596191,203.872238,515.646851,204.190811,515.669434,204.511963L515.658936,204.55249L515.645508,204.592041C515.517944,204.726532,515.333557,204.791733,515.149658,204.768066C514.429565,204.549744,513.713013,204.32077,512.999756,204.081055L512.643311,204.008057C512.251648,203.92424,511.84613,203.927994,511.456543,204.020996C510.797821,204.226944,510.343384,204.828354,510.32666,205.518311C510.335205,205.726151,510.399017,205.927765,510.509521,206.104004C511.165771,207.159256,511.846039,208.199738,512.549561,209.224121L512.531738,208.779053C512.47699,208.192017,512.384155,207.609207,512.253906,207.03418L512.157471,206.439941C512.159119,206.208603,512.261169,205.989838,512.4375,205.840088C513.13208,206.02594,513.815918,206.250168,514.485596,206.511963L514.803711,206.608398C515.225403,206.744293,515.666321,206.81218,516.109375,206.808594C516.87561,206.754593,517.506775,206.187408,517.645508,205.431885C517.726929,204.844238,517.664063,204.245575,517.461426,203.687988L517.261475,203.040039C517.126953,202.613831,517.078369,202.165176,517.117432,201.719971L517.409912,201.760742C518.01178,201.869614,518.598511,202.049667,519.157959,202.296875L519.685791,202.448242C520.042786,202.5327,520.406982,202.582977,520.773438,202.600098C521.503906,202.641144,522.165833,202.173447,522.373535,201.471924C522.475952,200.953781,522.469727,200.420135,522.357422,199.904053L522.109619,198.76001C522.047974,198.446228,522.012939,198.127792,522.005615,197.808105C522.22644,197.694855,522.469482,197.63121,522.717529,197.624023L522.389648,197.176025C522.09845,196.711044,521.823547,196.236084,521.56543,195.751953L521.230225,195.880127z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M479.548584,196.433594C479.338654,196.843201,479.114777,197.245514,478.877441,197.639893L479.373535,197.840088C479.322968,198.416733,479.22821,198.988647,479.090088,199.550781L479.021973,200.02002C478.965424,200.432037,478.974243,200.850586,479.048584,201.259766C479.228271,202.0634,479.949188,202.628891,480.772461,202.611084C481.4086,202.575272,482.031464,202.416992,482.607178,202.144043L483.030762,201.991943C483.412567,201.838959,483.812988,201.736618,484.221436,201.687988L484.255615,201.966309C484.276672,202.246353,484.25058,202.527863,484.181396,202.800049L483.989502,203.447998C483.845581,203.92984,483.76535,204.428467,483.750977,204.931152C483.756561,205.65152,484.181519,206.302155,484.837402,206.600098C485.411316,206.770554,486.02417,206.756805,486.5896,206.560059C486.976624,206.439941,487.358276,206.303268,487.733643,206.150635L488.158447,206.015381C488.44458,205.93367,488.735474,205.869461,489.029541,205.823975L489.30957,206.135986C489.294525,206.73053,489.22998,207.3228,489.117188,207.906738L489.005615,208.743896L488.981445,209.120117C489.315308,208.612793,489.659973,208.112793,490.015381,207.620361L490.437744,206.966797C490.710266,206.524872,490.963776,206.071518,491.19751,205.60791C491.240326,205.00325,490.952606,204.42337,490.446777,204.089355C490.194183,203.907928,489.886169,203.820023,489.575684,203.838379C489.128937,203.834381,488.684631,203.904831,488.261475,204.048096L487.23999,204.399658L486.205566,204.711914C485.987823,204.697586,485.81369,204.528854,485.789551,204.312012C485.828918,203.83812,485.920654,203.370056,486.062744,202.91626L486.221436,202.288086C486.332642,201.845093,486.348663,201.383667,486.269531,200.933838C486.101746,200.253006,485.512817,199.759476,484.813232,199.711914C484.349121,199.683792,483.883972,199.749237,483.445557,199.904053L482.702881,200.183594C482.287048,200.332855,481.864868,200.463852,481.4375,200.575928L481.365479,200.600098L481.293457,200.592041L481.164795,200.548584C481.047943,200.480087,480.959961,200.371185,480.925537,200.23999C480.964294,199.655075,481.05838,199.07515,481.206787,198.508057L481.276855,198.032715C481.308807,197.713394,481.311859,197.39183,481.2854,197.072021C481.172791,196.513763,480.78006,196.052673,480.245605,195.855957L479.933594,195.704102L479.548584,196.433594z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M522.645508,193.511963L523.581543,195.384033C523.859314,195.985031,524.223755,196.542267,524.66333,197.037354C525.231628,197.665146,525.969421,198.115372,526.787109,198.335693C527.418274,198.53653,528.061829,198.696213,528.713623,198.813965L529.245605,198.951904C531.052551,199.374695,532.546265,200.639633,533.261475,202.352051C535.765198,199.684616,537.925903,196.71492,539.693604,193.511963L522.645508,193.511963z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M462.101563,193.511963L462.769287,194.679443C464.368683,197.379822,466.252869,199.901016,468.389404,202.199951C468.684174,201.510468,469.112335,200.886093,469.648682,200.362061C470.338623,199.688492,471.18811,199.201096,472.117432,198.944092C472.732147,198.804749,473.348389,198.674255,473.966797,198.55249L474.383789,198.454346C475.033752,198.332016,475.651703,198.076767,476.198242,197.704346C476.61322,197.412476,476.971039,197.046814,477.25293,196.625C477.597717,196.150848,477.895142,195.643997,478.141602,195.112061L478.909424,193.511963L462.101563,193.511963z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M461.949463,172L461.949463,172.023926L456.581543,172.023926C456.559265,178.437149,457.940857,184.777588,460.629395,190.600098L541.165527,190.600098C543.853088,184.777252,545.234741,178.437073,545.213623,172.023926L537.469482,172.023926L537.469482,172L461.949463,172z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M487.804443,129.747559L486.347168,130.224854C469.548157,136.053268,457.868744,151.369537,456.693604,169.112061L472.869385,169.112061C472.33017,168.943207,471.801575,168.742096,471.286621,168.509521C468.539185,167.216949,466.865875,164.372437,470.070068,161.343018C467.401794,157.18428,470.874451,153.978546,475.046387,153.980225C475.613434,153.968185,476.180511,154.004166,476.741455,154.087891C477.51416,154.171753,478.284546,154.275787,479.051758,154.399902L480.125488,154.535889L482.525391,154.823975L484.91748,155.135986C486.510651,155.354935,488.111908,155.509735,489.717529,155.600098C487.396851,153.518402,485.645813,150.878448,484.631104,147.930664C483.310791,144.108673,483.341156,139.949371,484.717529,136.147217C485.671875,133.540665,487.235352,131.199982,489.277588,129.320068L487.804443,129.747559z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M512.916992,129.882568C516.781616,133.830338,518.569885,139.363037,517.74707,144.825928C517.128967,149.00618,515.032104,152.826111,511.837402,155.592041C512.832214,155.559601,513.825073,155.484787,514.813477,155.36792L525.277588,154.031982C527.070374,153.79245,528.892822,154.126938,530.483643,154.987549C532.376221,156.043472,533.758728,157.823166,534.314209,159.917969C535.237732,163.241196,533.707947,166.756927,530.646484,168.345703C530.027222,168.669601,529.375183,168.926529,528.701416,169.112061L545.117432,169.112061C544.070801,153.563614,534.9375,139.706604,521.061279,132.614258C518.260986,131.179779,515.31311,130.053802,512.269531,129.256104L512.916992,129.882568z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M523.599854,156.127686L522.557617,156.248047L522.013428,156.855957L521.429443,157.439941C521.03302,157.821457,520.701538,158.264954,520.445557,158.751953C520.161621,159.437241,520.348389,160.226624,520.909424,160.711914C521.348389,161.032211,521.854919,161.24736,522.389648,161.343994C522.727539,161.42511,523.0672,161.498825,523.408203,161.56543L523.630859,161.625977C523.872131,161.706146,524.107117,161.804108,524.333496,161.919922L524.197754,162.145264C523.892578,162.615189,523.523132,163.040009,523.100342,163.407715L522.742432,163.815918C522.515259,164.09848,522.314087,164.401031,522.141602,164.719971C521.8302,165.456558,522.05304,166.310699,522.685547,166.800049C523.12323,167.098038,523.611023,167.314575,524.125488,167.439941L524.933594,167.63208C525.333679,167.74675,525.718323,167.910095,526.077637,168.120117C526.61792,167.877594,527.170715,167.663971,527.733643,167.47998L527.661621,167.1521C527.605225,166.692673,527.28363,166.309158,526.839844,166.17749C526.54187,166.06076,526.232849,165.974472,525.91748,165.919922L525.19751,165.808105C524.775818,165.720032,524.37616,165.548416,524.021484,165.303955L524.214844,164.987549C524.63092,164.434601,525.095276,163.919922,525.602539,163.449219L525.907959,163.089844C526.1026,162.842316,526.277222,162.57962,526.429443,162.303955C526.704285,161.728088,526.586365,160.041122,526.131348,160.59375C525.77002,160.243454,525.304504,160.02066,524.805176,159.958008C524.406189,159.887848,524.006653,159.824554,523.605469,159.768066L523.350586,159.713379C522.996399,159.631851,522.669983,159.457336,522.405518,159.208008C522.600342,158.897583,522.819458,158.603043,523.061523,158.327881L523.945801,157.37085C524.273926,156.983994,524.512451,156.529556,524.645508,156.040039L523.599854,156.127686z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M477.02417,156.499512L477.24292,156.941406C477.407379,157.227463,477.606842,157.491974,477.837402,157.728027C478.104553,158.02243,478.38504,158.304459,478.678223,158.572998L478.822266,158.751221C478.911713,158.876068,478.980255,159.01445,479.029541,159.159912C478.959381,159.341751,478.818207,159.487061,478.637451,159.560059L478.461426,159.624023L477.319824,159.863525L476.181396,160.120117C475.856079,160.221329,475.556244,160.390732,475.30127,160.616699C474.834778,161.085678,474.742523,161.809708,475.076172,162.380859C475.293671,162.813812,475.595978,163.198669,475.965576,163.511963C476.256287,163.807983,476.552338,164.098663,476.853516,164.384033L477.020508,164.570557C477.227081,164.825958,477.3703,165.126465,477.4375,165.447998C477.161987,165.572998,476.875336,165.671768,476.581543,165.743896C476.322784,165.807083,476.060669,165.855881,475.796387,165.88916L475.307861,166.008789C474.984955,166.1008,474.6698,166.218246,474.365479,166.360107C474.098297,166.516388,473.914001,166.782852,473.861572,167.087891L473.82959,167.375977L474.734131,167.728271L475.629395,168.104004L475.845703,167.946533C476.07132,167.802368,476.320465,167.699142,476.581543,167.639893C476.858398,167.570129,477.137756,167.510818,477.418945,167.461426L477.723877,167.384521C478.025055,167.289169,478.321716,167.180298,478.613037,167.058105C479.277283,166.720047,479.661194,166.004074,479.573486,165.263916C479.441376,164.722931,479.165466,164.227539,478.773438,163.832031C478.522644,163.551895,478.254822,163.287521,477.972168,163.039551L477.692383,162.749512C477.450867,162.495346,477.239166,162.214371,477.061523,161.912109L477.365479,161.784912C477.692627,161.660645,478.032166,161.571655,478.378418,161.520752C478.638641,161.457779,478.902222,161.409485,479.167725,161.375L479.568848,161.27124C480.027985,161.150024,480.452393,160.923325,480.809082,160.609863C481.318817,160.107483,481.445465,159.333038,481.12207,158.69458C480.910583,158.280502,480.627441,157.906998,480.2854,157.592041L479.943359,157.25C479.734192,157.032791,479.530212,156.810608,479.331543,156.58374L479.210938,156.479248C479.126953,156.41275,479.033142,156.359726,479.933594,156.320068C478.24762,156.19278,477.556274,156.096619,476.861572,156.031982L477.02417,156.499512z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M500.648193,162.105469L500.410889,162.127441C499.011841,162.315338,498.021149,163.589249,498.183838,164.991455C498.362427,166.336639,499.572632,167.299973,500.923828,167.174561C502.262665,167.057831,503.280334,165.920929,503.248291,164.577393C503.220825,163.266647,502.197479,162.193298,500.889404,162.105469L500.648193,162.105469z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M507.277588,145.39209L507.050049,145.460938C506.784058,145.564285,506.559265,145.751511,506.405518,145.991943L506.206787,146.342773C505.102936,148.212509,503.11911,149.387497,500.948975,149.457764C498.786011,149.507065,496.753998,148.423569,495.5896,146.600098L495.36792,146.23584C495.27182,146.048767,495.147217,145.877762,494.999268,145.728271C494.599884,145.358063,493.98111,145.362442,493.589111,145.740479C493.27533,146.039551,493.167542,146.494507,493.30957,146.904053C495.145844,150.573563,499.362213,152.38205,503.286621,151.183594C505.486542,150.524612,507.310242,148.975876,508.317627,146.912109C508.530029,146.451752,508.390015,145.904968,507.98291,145.602783C507.779877,145.451569,507.530243,145.376038,507.277588,145.39209z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M506.523438,136.704346L506.284668,136.728271C504.963074,136.924576,504.036438,138.135574,504.193604,139.462402C504.348633,140.761963,505.483154,141.719727,506.790283,141.654053C508.059082,141.585266,509.070068,140.567795,509.131104,139.298584C509.192352,138.024323,508.275848,136.911774,507.013428,136.728027C506.851135,136.705322,506.687164,136.697098,506.523438,136.704346z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M494.89209,136.722412L494.661621,136.748779C493.390594,136.948669,492.48941,138.094574,492.591064,139.377197C492.696289,140.642807,493.743347,141.622772,495.013184,141.641846C496.308197,141.649002,497.387177,140.652435,497.484619,139.361084C497.579559,138.064331,496.653198,136.915878,495.365479,136.736084C495.208649,136.714569,495.049896,136.710007,494.89209,136.722412z"/>
    <path transform="matrix(-1,0,0,1,0,0)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M500.642578,116.216553L500.390625,116.255127C499.02182,116.537735,498.136963,117.871674,498.411133,119.242188C498.66864,120.525963,499.85907,121.404099,501.161621,121.270752C502.549957,121.127403,503.562653,119.891418,503.430664,118.501953C503.306335,117.198456,502.211517,116.203026,500.9021,116.203369L500.642578,116.216553z"/>
</svg>`
  };

  window.LaserTemplates.gingerbread = gingerbreadTemplate;
  window.LaserTemplates.gingerbread_ornament = gingerbreadTemplate;
})();


/* ==========================================================================
   2D MODULE: laser-studio-template-reindeer.js
   ========================================================================== */

/**
 * Laser Ornament Studio - Template: Reindeer Ornament
 * File: assets/laser-studio-template-reindeer.js
 * Template ID: "reindeer_ornament"
 */

(function () {
  window.LaserTemplates = window.LaserTemplates || {};

  const reindeerTemplate = {
    id: "reindeer",
    name: "Reindeer Ornament",
    badge: "Holiday",
    description: "Majestic reindeer silhouette with ornamental antlers",
    iconSvg: `<svg viewBox="-673.15 110.16 101.62 109.75" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M607.94,170.36L608.29,170.41C611.29,170.9,613.35,173.71,612.91,176.72C612.81,177.42,612.58,178.08,612.23,178.69L613.43,178.82C640.96,182.12,660.75,206.88,657.91,234.45C655.07,262.19,630.4,282.45,602.64,279.86C575.08,277.28,554.66,253.11,556.7,225.51C558.53,200.82,577.88,181.05,602.52,178.69L602.37,178.4C601.03,175.7,602.07,172.44,604.71,171.01C605.7,170.47,606.82,170.25,607.94,170.36z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M609.53,250.69L610.03,251.53C610.64,252.56,611.34,253.53,612.14,254.42C612.71,255.02,613.31,255.58,613.94,256.1L613.99,256.17L614.02,256.26L614.04,256.35L614.04,256.44C614.01,256.63,613.87,256.77,613.69,256.82C613.27,256.83,612.85,256.74,612.46,256.56L612.41,256.65L612.33,256.71C612.25,256.77,612.16,256.8,612.06,256.81C611.82,256.78,611.59,256.72,611.36,256.63L611.3,256.75C611.93,257.99,612.74,259.12,613.72,260.1C614.26,260.64,614.8,261.18,615.35,261.7L615.39,261.78L615.41,261.86L615.42,261.95L615.42,262.03L615.35,262.17C615.31,262.23,615.26,262.28,615.2,262.33C615.06,262.39,614.9,262.4,614.74,262.36C614.51,262.33,614.27,262.27,614.05,262.18C614.02,262.25,613.97,262.31,613.91,262.36L613.81,262.41L613.7,262.44C613.39,262.4,613.09,262.33,612.79,262.22C612.76,262.29,612.73,262.34,612.68,262.39C612.61,262.44,612.53,262.48,612.45,262.5C613.26,263.94,614.22,265.29,615.3,266.54C615.75,267.02,616.26,267.43,616.81,267.78L616.9,267.89C616.97,268.08,616.95,268.29,616.85,268.46L616.8,268.53L616.72,268.61L616.61,268.65L616.5,268.66C616.15,268.64,615.81,268.55,615.48,268.42C615.41,268.51,615.32,268.59,615.21,268.63C614.85,268.66,614.5,268.61,614.17,268.47L614.11,268.54C613.94,268.63,613.74,268.66,613.55,268.63C613.69,268.89,613.85,269.13,614.02,269.36C614.66,270.56,615.45,271.68,616.38,272.66C620.5,271.81,624.48,270.37,628.19,268.39L628.2,267.38L628.15,267.32L627.97,267.36C627.78,267.41,627.57,267.41,627.37,267.38L627.25,267.3L626.91,267.38L626.56,267.44C626.5,267.43,626.44,267.42,626.38,267.4L626.33,267.37L626.29,267.31L626.09,267.36C626,267.38,625.89,267.37,625.8,267.33L625.72,267.27C625.69,267.24,625.67,267.2,625.65,267.16C625.48,267.22,625.31,267.27,625.13,267.29C625.08,267.29,625.04,267.27,625,267.26C624.94,267.21,624.87,267.15,624.82,267.09C624.67,267.12,624.51,267.13,624.36,267.14L624.28,267.1L624.21,267.05C624.18,267.01,624.16,266.97,624.14,266.93C623.93,267,623.72,267.06,623.5,267.09L623.4,267.06C623.33,267,623.26,266.93,623.21,266.85C622.94,266.99,622.63,267.05,622.33,267.02C622.28,267.01,622.24,266.98,622.2,266.94L622.16,266.88L622.14,266.8C622.13,266.75,622.13,266.7,622.13,266.65C622.26,266.42,622.46,266.24,622.7,266.14L623.06,265.77C623.89,264.89,624.61,263.92,625.22,262.87L624.92,262.86C624.86,262.83,624.82,262.78,624.8,262.72C624.6,262.81,624.38,262.84,624.17,262.82L624.13,262.8L624.05,262.73L623.98,262.64L623.62,262.75L623.26,262.82C623.18,262.81,623.1,262.77,623.05,262.71L622.99,262.61C622.97,262.53,622.96,262.45,622.99,262.37C623.02,262.31,623.06,262.25,623.11,262.21L623.18,262.16L623.23,262.14C623.41,262.04,623.57,261.91,623.72,261.77C624.6,260.86,625.38,259.85,626.04,258.77L625.97,258.74C625.9,258.71,625.85,258.66,625.8,258.6C625.63,258.66,625.45,258.7,625.26,258.71C625.21,258.71,625.17,258.69,625.12,258.67C625.06,258.64,625.02,258.58,624.99,258.52C624.75,258.64,624.48,258.69,624.22,258.64L624.12,258.56L624.05,258.46C624.04,258.41,624.04,258.35,624.05,258.3C624.11,258.16,624.21,258.04,624.34,257.95L624.66,257.72C625.52,256.88,626.28,255.94,626.9,254.91L626.89,254.86L626.68,254.92C626.59,254.95,626.5,254.97,626.4,254.98L626.32,254.96L626.25,254.93L626.15,254.82L625.96,254.89C625.76,254.95,625.55,254.98,625.34,254.96C625.3,254.93,625.25,254.9,625.21,254.86C625.16,254.78,625.15,254.69,625.15,254.59L625.19,254.51L625.57,254.19C626.3,253.54,626.95,252.8,627.48,251.98C627.77,251.56,628.04,251.13,628.3,250.69L609.53,250.69z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M586.73,250.69L587.02,251.18L587.32,251.66L587.54,252.02C588.16,252.98,588.93,253.83,589.82,254.55L589.86,254.61L589.88,254.72C589.88,254.77,589.86,254.83,589.84,254.88C589.8,254.92,589.76,254.96,589.72,254.99C589.41,255.04,589.1,254.99,588.82,254.85C588.79,254.89,588.76,254.93,588.72,254.97L588.64,255C588.49,255.02,588.34,255,588.21,254.94L588.09,254.9L588.06,254.98C588.36,255.58,588.73,256.14,589.17,256.66C589.65,257.23,590.18,257.77,590.74,258.26L590.78,258.31L590.79,258.38L590.79,258.45C590.78,258.53,590.74,258.62,590.67,258.67L590.59,258.71L590.45,258.72C590.25,258.71,590.06,258.67,589.88,258.58L589.82,258.67L589.74,258.73L589.66,258.76L589.47,258.74L589.05,258.62C589.03,258.65,589,258.68,588.98,258.72C588.95,258.74,588.92,258.76,588.89,258.78L588.82,258.8C589.31,259.66,589.88,260.49,590.51,261.26C590.82,261.64,591.2,261.97,591.61,262.23L591.76,262.36L591.79,262.49L591.8,262.62C591.79,262.66,591.78,262.71,591.76,262.75C591.71,262.81,591.64,262.87,591.57,262.9C591.31,262.89,591.06,262.84,590.83,262.74C590.8,262.79,590.76,262.84,590.7,262.87C590.45,262.91,590.19,262.87,589.96,262.78L589.92,262.82L589.88,262.84C589.77,262.88,589.66,262.9,589.54,262.88C589.64,263.05,589.75,263.21,589.86,263.37C590.41,264.39,591.13,265.32,591.98,266.11L592.52,266.61C592.62,266.69,592.65,266.85,592.58,266.96L592.54,267.02L592.49,267.07L592.42,267.1C592.1,267.12,591.79,267.06,591.5,266.92L591.47,266.99C591.44,267.03,591.42,267.07,591.38,267.1L591.33,267.13L591.27,267.15L591.22,267.16C591.01,267.12,590.8,267.07,590.59,266.99C590.54,267.12,590.42,267.2,590.28,267.2C590.15,267.2,590.02,267.17,589.9,267.11C589.88,267.16,589.86,267.2,589.83,267.25L589.77,267.29L589.7,267.33L589.62,267.34L589.53,267.33L589.08,267.2C589.06,267.24,589.03,267.27,589.02,267.31L588.94,267.37L588.86,267.41L588.77,267.43C588.59,267.42,588.4,267.39,588.23,267.33L588.19,267.37L588.14,267.4C587.91,267.44,587.68,267.42,587.46,267.34C587.43,267.37,587.4,267.4,587.36,267.43C587.32,267.44,587.28,267.46,587.25,267.46L587.18,267.46L586.81,267.41L586.8,268.53C590.4,270.42,594.24,271.8,598.22,272.63C599.38,271.42,600.38,270.07,601.21,268.62L601.07,268.63L600.93,268.63L600.84,268.61C600.78,268.59,600.73,268.56,600.67,268.53C600.63,268.48,600.59,268.45,600.56,268.39C600.29,268.49,600,268.54,599.71,268.54C599.63,268.54,599.56,268.51,599.5,268.46L599.41,268.38L599.35,268.27L598.87,268.41L598.38,268.53C598.26,268.55,598.14,268.53,598.04,268.46C597.94,268.39,597.87,268.29,597.85,268.17L597.83,268.06L597.83,267.96L597.85,267.86L597.89,267.76L597.95,267.66C598.08,267.57,598.22,267.49,598.36,267.42L598.57,267.28C598.89,267.03,599.18,266.74,599.44,266.43C600.57,265.2,601.57,263.87,602.43,262.44L602.34,262.42C602.26,262.39,602.19,262.35,602.13,262.28L602.08,262.2L601.65,262.3L601.22,262.37L601.14,262.34L601.07,262.31L601,262.27C600.94,262.21,600.9,262.15,600.86,262.07C600.52,262.24,600.15,262.31,599.77,262.28C599.67,262.26,599.58,262.21,599.53,262.13C599.48,262.07,599.46,262,599.44,261.93C599.43,261.85,599.43,261.76,599.46,261.68C599.54,261.5,599.67,261.35,599.84,261.24C600.01,261.11,600.19,260.99,600.37,260.87L600.84,260.39C601.94,259.25,602.9,258,603.73,256.65L603.7,256.58L603.35,256.68L602.98,256.75C602.91,256.76,602.84,256.73,602.78,256.69L602.65,256.58L602.61,256.5L602.33,256.6C602.03,256.72,601.71,256.77,601.38,256.72C601.22,256.65,601.11,256.51,601.08,256.34C601.08,256.24,601.11,256.14,601.16,256.06L601.2,256L601.85,255.45C602.91,254.51,603.84,253.43,604.6,252.25C604.96,251.74,605.29,251.22,605.6,250.69L586.73,250.69z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M630.62,250.69L631.01,251.36C631.69,252.57,632.59,253.63,633.66,254.5L633.74,254.58L633.76,254.69L633.76,254.81C633.72,254.89,633.67,254.96,633.59,255C633.29,255.03,632.98,254.98,632.71,254.85C632.66,254.93,632.58,254.99,632.49,255.02C632.31,255.01,632.14,254.97,631.98,254.9L631.97,254.94L631.94,254.98L632.16,255.36C632.79,256.44,633.62,257.41,634.59,258.21C634.61,258.23,634.63,258.26,634.65,258.29C634.66,258.32,634.67,258.35,634.68,258.38L634.68,258.46C634.67,258.51,634.66,258.55,634.63,258.59C634.6,258.63,634.57,258.66,634.54,258.69C634.28,258.74,634.01,258.7,633.78,258.58C633.75,258.63,633.71,258.67,633.66,258.71C633.61,258.74,633.56,258.75,633.5,258.77C633.31,258.73,633.12,258.68,632.94,258.62C632.9,258.72,632.81,258.79,632.7,258.81C633.29,259.86,634.01,260.84,634.84,261.72C635.09,261.94,635.35,262.14,635.63,262.33L635.66,262.38C635.68,262.43,635.69,262.48,635.7,262.53C635.71,262.66,635.66,262.79,635.55,262.86C635.39,262.91,635.23,262.9,635.08,262.86C634.95,262.84,634.83,262.79,634.72,262.74C634.67,262.8,634.6,262.85,634.52,262.89C634.29,262.9,634.06,262.86,633.85,262.78C633.81,262.83,633.75,262.86,633.69,262.87L633.61,262.89L633.52,262.89L633.44,262.88C633.54,263.05,633.64,263.21,633.76,263.37C633.93,263.73,634.13,264.08,634.37,264.41L635.23,263.73C639.7,260.14,643.41,255.71,646.17,250.69L630.62,250.69z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M568.58,250.69L569.11,251.64C571.99,256.57,575.81,260.9,580.34,264.38L580.6,264.02C580.87,263.65,581.11,263.27,581.34,262.87C581.22,262.9,581.1,262.88,580.99,262.82C580.96,262.79,580.93,262.76,580.9,262.72C580.74,262.8,580.55,262.85,580.37,262.83L580.29,262.8L580.21,262.77L580.14,262.72L580.1,262.64L579.78,262.74L579.44,262.82L579.38,262.82C579.34,262.82,579.3,262.81,579.26,262.8C579.23,262.78,579.19,262.75,579.16,262.72L579.12,262.66L579.1,262.56C579.08,262.46,579.11,262.36,579.15,262.26C579.19,262.22,579.23,262.17,579.29,262.16L579.34,262.14C579.52,262.03,579.68,261.9,579.83,261.77C580.71,260.85,581.48,259.85,582.15,258.77C582.11,258.76,582.07,258.75,582.03,258.73L581.96,258.68L581.91,258.6L581.67,258.66C581.54,258.69,581.42,258.71,581.29,258.7C581.2,258.67,581.14,258.6,581.1,258.52C580.88,258.63,580.63,258.67,580.38,258.65L580.3,258.62L580.25,258.58L580.21,258.53L580.17,258.48L580.15,258.42L580.15,258.33L580.18,258.24C580.34,258.03,580.55,257.86,580.78,257.72L581.4,257.07C582,256.41,582.54,255.68,583.01,254.91L582.99,254.86L582.74,254.93C582.63,254.97,582.51,254.98,582.4,254.95C582.34,254.92,582.29,254.88,582.26,254.82C582.04,254.93,581.78,254.99,581.53,254.97L581.4,254.94C581.36,254.91,581.33,254.88,581.3,254.84L581.27,254.77L581.26,254.7L581.26,254.61L581.3,254.51L581.8,254.08C582.56,253.38,583.23,252.58,583.77,251.7C583.99,251.37,584.21,251.03,584.42,250.69L568.58,250.69z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M563.06,229.18L563.07,230.38C563.21,236.39,564.59,242.31,567.1,247.78L647.64,247.78C650.32,241.94,651.7,235.6,651.68,229.18L563.06,229.18z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M576.4,197.57L575.65,198.32C568.29,205.84,563.86,215.76,563.17,226.26L567.64,226.26L567.66,226.22L567.7,226.17L598.35,226.17C599.08,225.19,599.48,224.02,599.5,222.8C599.53,222.26,599.49,221.72,599.39,221.18C599.03,218.15,597.95,215.24,596.26,212.7L595.26,210.28C594.85,209.28,594.49,208.25,594.18,207.22L594.17,207.11C594.12,206.94,594,206.79,593.84,206.7C593.57,206.51,593.27,206.36,592.95,206.27L591.58,205.88L590.21,205.47C589.25,205.16,588.35,204.68,587.56,204.05C586.98,203.57,586.55,202.95,586.3,202.25L586.3,202.03C586.27,201.42,585.82,200.91,585.22,200.8L584.73,200.7C584.24,200.59,583.75,200.44,583.29,200.25C581.14,199.31,579.09,198.17,577.16,196.85L576.4,197.57z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M613.72,186.14C613.38,188.09,612.61,189.94,611.47,191.55C610.06,193.58,608.34,195.36,606.35,196.83L605.93,197.19C605.59,197.5,605.27,197.84,604.98,198.2C604.82,198.42,604.73,198.68,604.7,198.94C604.75,199.15,604.9,199.32,605.1,199.38C606.9,200.88,608.86,202.17,610.94,203.23L611.31,203.31C611.79,203.43,612.25,203.6,612.7,203.81L612.62,203.62C612.2,202.47,612.76,201.2,613.9,200.74C614.95,200.33,616.14,200.77,616.66,201.78C617.23,202.83,616.87,204.15,615.85,204.77C615.38,205.06,614.81,205.15,614.27,205.04L614.38,205.27C614.55,205.95,614.29,206.67,613.72,207.09C613.04,207.66,612.28,208.11,611.45,208.43C610.51,208.79,609.54,209.06,608.56,209.24L607.61,209.49C607.04,209.63,606.49,209.86,606,210.17C605.63,210.46,605.51,210.96,605.71,211.37C605.82,211.71,605.97,212.04,606.16,212.34L608.17,215.19C610.18,218.05,612.16,220.91,614.13,223.8C614.7,224.51,615.15,225.31,615.45,226.17L651.58,226.26C650.18,205.42,634.39,188.39,613.71,185.41L613.72,186.14z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M591.49,195.22L591.31,195.29C591.07,195.4,590.88,195.6,590.8,195.86L590.58,196.18C590.18,196.77,589.7,197.31,589.15,197.76L588.85,198.06C588.74,198.2,588.72,198.39,588.77,198.55C588.93,198.76,589.16,198.91,589.42,198.96C590.04,199.17,590.69,199.27,591.34,199.28C592.36,199.33,593.38,199.23,594.37,198.97C594.5,198.92,594.62,198.86,594.74,198.8C594.98,198.67,595.11,198.39,595.06,198.11C595.03,197.89,594.95,197.69,594.82,197.5C594.14,196.56,593.22,195.8,592.15,195.32C591.94,195.23,591.71,195.2,591.49,195.22z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M584.9,191.07L583.63,191.84C582.8,192.37,581.98,192.93,581.18,193.51C580.82,194.14,580.72,194.87,580.9,195.57C580.99,195.94,581.18,196.28,581.45,196.56C582.51,197.58,584.06,197.91,585.45,197.4C585.72,197.3,585.98,197.17,586.22,197.01C587.06,196.39,587.86,195.73,588.62,195.01C588.98,194.59,589.03,193.98,588.75,193.5C588.37,192.79,587.86,192.15,587.23,191.63C586.92,191.18,586.57,190.74,586.19,190.34L584.9,191.07z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M597.6,186.04L596.43,186.32C595.35,186.59,594.29,186.91,593.23,187.26L593.27,187.7C593.31,188.55,593.25,189.41,593.06,190.24L592.94,191.1C592.82,191.8,592.86,192.51,593.03,193.2C593.14,193.5,593.34,193.77,593.61,193.95C594.72,194.66,596.07,194.88,597.35,194.58C598.4,194.52,599.32,193.84,599.67,192.85C599.81,192.48,599.88,192.09,599.9,191.7C599.95,190.07,599.74,188.45,599.29,186.9C599.16,186.51,598.99,186.14,598.78,185.78L597.6,186.04z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M606.97,184.95L606.42,184.96L606.55,185.69C606.83,187.65,606.77,189.65,606.35,191.58L606.33,191.91C606.32,192.24,606.38,192.56,606.5,192.87C606.55,193,606.67,193.09,606.81,193.09C607.02,193.11,607.22,193.02,607.35,192.85L607.52,192.71C608.87,191.22,609.96,189.52,610.75,187.67C611.02,186.98,611.22,186.27,611.34,185.54L610.62,185.4C609.59,185.21,608.56,185.06,607.52,184.95L606.97,184.95z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M603.29,185.14L602.4,185.23C601.81,185.3,601.22,185.38,600.63,185.47C601.23,186.47,601.68,187.55,601.96,188.68C602.07,189.18,602.12,189.69,602.13,190.21L602.29,190.55C602.46,190.94,602.74,191.26,603.09,191.5C603.14,191.52,603.19,191.53,603.25,191.53C603.49,191.49,603.69,191.31,603.76,191.07C604.37,189.13,604.51,187.08,604.18,185.07L603.29,185.14z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M589.3,188.79L588.5,189.16L588.2,189.31C588.57,189.7,588.99,190.03,589.45,190.29C589.55,190.32,589.66,190.34,589.76,190.34C589.9,190.32,590.02,190.24,590.09,190.12C590.2,189.79,590.23,189.43,590.17,189.09L590.11,188.45L589.3,188.79z"/>
      <path transform="matrix(-1,0,0,1,-14.963,-60.176)" style="stroke:#ff3b30;stroke-width:1.2;fill:none" d="M607.12,173.39L606.86,173.43C605.5,173.71,604.61,175.05,604.89,176.42C605.14,177.7,606.33,178.58,607.64,178.45C609.03,178.31,610.04,177.07,609.91,175.68C609.78,174.37,608.69,173.38,607.38,173.38L607.12,173.39z"/>
    </svg>`,
    presets: {
      circleMode: "ring",
      circleRadius: 140,
      ringThickness: 18,
      useSupportBar: false,
      barHeight: 18.6,
      barYOffset: 0,
      fontSize: 84,
      textYOffset: 0
    },
    config: {
      targetWidthMultiplier: 2.0,
      fontSizeRatio: 28.3544,
      textCenterYOffsetRatio: 13.2709,
      slotHeightRatio: 18.600098,
      ringCenterRadiusRatio: 47.905,
      arcCenterYOffsetRatio: 4.0424,
      outerRadiusForFlatRatio: 54.863
    },
    rawSvg: `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="101.624mm" height="109.745mm" viewBox="-673.147522 110.156540 101.623779 109.745163">
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M607.937988,170.359131L608.288086,170.405762C611.292236,170.901306,613.34613,173.711151,612.907227,176.724121C612.807007,177.41568,612.577698,178.082275,612.230957,178.688965L613.434326,178.817627C640.957886,182.118958,660.750977,206.875122,657.914063,234.450439C655.065308,262.185455,630.402527,282.44574,602.64209,279.856201C575.083069,277.277344,554.656982,253.11377,556.70166,225.51001C558.525391,200.82338,577.880737,181.045364,602.522217,178.688965L602.367676,178.397705C601.032471,175.704254,602.069397,172.437485,604.713135,171.006348C605.69873,170.471741,606.822449,170.246124,607.937988,170.359131z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M609.528076,250.687988L610.032959,251.5271C610.636902,252.561676,611.344482,253.532211,612.144043,254.424316C612.709778,255.017334,613.310791,255.575684,613.944092,256.095947L613.98877,256.173584L614.023926,256.256104L614.041992,256.347168L614.040039,256.439941C614.009583,256.625336,613.871033,256.773529,613.687988,256.815918C613.265808,256.826416,612.847229,256.737671,612.464111,256.560059L612.407959,256.647949L612.334961,256.713135C612.253174,256.765717,612.16095,256.799744,612.063965,256.808105C611.822815,256.779144,611.585938,256.721436,611.360107,256.63208L611.303955,256.751953C611.928101,257.989319,612.743347,259.120667,613.719971,260.104004C614.256592,260.644287,614.80127,261.178284,615.352051,261.704102L615.387939,261.781494L615.410889,261.863037L615.420654,261.947021L615.416016,262.031982L615.352051,262.167969C615.313049,262.230865,615.262207,262.284637,615.203857,262.329834C615.058472,262.391296,614.897095,262.399811,614.744385,262.360352C614.50592,262.325928,614.271729,262.265686,614.048096,262.176025C614.019958,262.248596,613.97113,262.309875,613.912109,262.360107L613.812256,262.411865L613.704102,262.439941C613.392944,262.402069,613.087219,262.329529,612.791992,262.224121C612.762451,262.285278,612.729004,262.344543,612.679932,262.39209C612.608826,262.441223,612.531921,262.481384,612.447998,262.503906C613.262329,263.940796,614.219238,265.292053,615.304443,266.537109C615.748962,267.016357,616.25708,267.432404,616.813477,267.775391L616.896484,267.892334C616.96875,268.079834,616.949463,268.287506,616.852051,268.462891L616.800049,268.528076L616.715332,268.605957L616.60791,268.647949L616.50415,268.664063C616.151855,268.635559,615.80658,268.551025,615.47998,268.416016C615.410706,268.512085,615.319641,268.590851,615.208008,268.63208C614.853516,268.66156,614.497314,268.606323,614.167969,268.471924L614.112061,268.536133C613.941162,268.628845,613.744263,268.661194,613.552002,268.63208C613.69104,268.886108,613.849365,269.129028,614.023926,269.360107C614.65509,270.564697,615.449219,271.676392,616.384033,272.664063C620.502808,271.807617,624.47876,270.369202,628.192139,268.39209L628.199951,267.375977L628.1521,267.320068L627.974854,267.360352C627.77594,267.407074,627.569946,267.414703,627.36792,267.384033L627.248047,267.303955L626.90625,267.382813L626.560059,267.439941C626.497437,267.433105,626.435974,267.419067,626.375977,267.399902L626.328125,267.36792L626.288086,267.312012L626.093994,267.356689C625.996521,267.38205,625.894165,267.374695,625.802002,267.33374L625.719971,267.271973C625.692444,267.236877,625.67041,267.198303,625.647949,267.159912C625.479736,267.221466,625.305664,267.265808,625.12793,267.288086C625.083557,267.285675,625.041077,267.272583,625,267.256104C624.935486,267.205048,624.873657,267.150757,624.815918,267.092041C624.6651,267.116455,624.512756,267.129883,624.360107,267.135986L624.280029,267.095947L624.208008,267.048096C624.182312,267.010406,624.161865,266.969788,624.144043,266.927979C623.934265,267.003967,623.717163,267.057983,623.496094,267.088135L623.400146,267.063965C623.327087,267.000458,623.261841,266.928406,623.208008,266.8479C622.936096,266.98526,622.631836,267.047516,622.328125,267.023926C622.280762,267.005524,622.239685,266.975677,622.199951,266.944092L622.161133,266.875732L622.135254,266.80249C622.126587,266.751373,622.12561,266.699615,622.12793,266.647949C622.260376,266.42334,622.458923,266.244476,622.696045,266.135986L623.057617,265.765137C623.887146,264.886047,624.61377,263.915314,625.224121,262.87207C625.122375,262.880554,625.019836,262.877319,624.919922,262.855957C624.862854,262.82962,624.820862,262.779358,624.800049,262.719971C624.602234,262.809265,624.383484,262.841431,624.167969,262.815918L624.12793,262.800049L624.048096,262.728027L623.984131,262.640137L623.624268,262.748535L623.256104,262.823975C623.175842,262.81073,623.100891,262.774292,623.048096,262.711914L622.98999,262.605225C622.967896,262.527893,622.962158,262.446136,622.98999,262.370605C623.020386,262.309174,623.062256,262.254852,623.112061,262.208008L623.184082,262.159912L623.231934,262.144043C623.408325,262.037598,623.572632,261.912109,623.719971,261.768066C624.603394,260.858215,625.38147,259.851807,626.040039,258.768066L625.966797,258.74292C625.901794,258.706909,625.846313,258.657867,625.800049,258.600098C625.626404,258.658508,625.44635,258.695129,625.263916,258.711914C625.214233,258.705444,625.166931,258.689545,625.120117,258.672119C625.061646,258.637115,625.020813,258.581512,624.991943,258.52002C624.752747,258.642456,624.48053,258.687439,624.216064,258.640137L624.117432,258.561523L624.048096,258.456055C624.040894,258.405304,624.044678,258.354675,624.048096,258.303955C624.111938,258.161224,624.209839,258.035431,624.340088,257.948975L624.664063,257.719971C625.523865,256.880188,626.276245,255.936981,626.904053,254.912109L626.887939,254.864014L626.68457,254.918213C626.59198,254.947266,626.496704,254.966415,626.400146,254.976074L626.320068,254.959961L626.248047,254.927979L626.1521,254.815918L625.958496,254.89209C625.759949,254.954514,625.55127,254.978745,625.343994,254.959961C625.295715,254.932297,625.249878,254.900772,625.208008,254.864014C625.164612,254.779953,625.146667,254.686279,625.1521,254.592041L625.192139,254.511963L625.571045,254.192383C626.303711,253.544846,626.946228,252.801788,627.480957,251.983154C627.768921,251.560226,628.043396,251.128326,628.303955,250.687988L609.528076,250.687988z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M586.728027,250.687988L587.018555,251.179443L587.320068,251.664063L587.536133,252.019287C588.159546,252.977539,588.930725,253.831161,589.820801,254.548584L589.855957,254.60791L589.880127,254.719971C589.876648,254.774704,589.861816,254.827866,589.837402,254.876953C589.802124,254.919052,589.764038,254.958725,589.719971,254.991943C589.413635,255.041214,589.099243,254.991074,588.823975,254.8479C588.793701,254.891708,588.757202,254.930176,588.719971,254.968018L588.640137,255C588.493652,255.021194,588.344421,255.001419,588.208008,254.944092L588.088135,254.895996L588.056152,254.976074C588.360779,255.578064,588.73468,256.142273,589.171143,256.656738C589.652832,257.230194,590.178589,257.765167,590.744141,258.256104L590.776123,258.312012L590.790283,258.37915L590.791992,258.447998C590.778198,258.534454,590.739319,258.615051,590.672119,258.672119L590.592041,258.711914L590.449951,258.715088C590.25293,258.710266,590.057922,258.668671,589.880127,258.583984L589.823975,258.672119L589.744141,258.728027L589.656006,258.76001L589.471924,258.743896L589.048096,258.615967C589.027649,258.651672,589.003784,258.684479,588.984131,258.719971C588.951721,258.740753,588.919495,258.761841,588.887939,258.783936L588.815918,258.800049C589.312805,259.664825,589.880005,260.487183,590.512207,261.258545C590.824646,261.638,591.195557,261.96521,591.611572,262.227051L591.76001,262.360107L591.794678,262.485596L591.800049,262.615967C591.789063,262.661957,591.776306,262.70752,591.76001,262.751953C591.707703,262.814148,591.644531,262.866486,591.568115,262.895996C591.314392,262.893768,591.064026,262.838867,590.832031,262.736084C590.800537,262.793945,590.756226,262.844391,590.696045,262.87207C590.446533,262.906952,590.192322,262.873413,589.959961,262.776123L589.919922,262.815918L589.880127,262.840088C589.772705,262.880676,589.65741,262.897888,589.543945,262.880127C589.641418,263.048615,589.747498,263.211975,589.864014,263.36792C590.413635,264.391815,591.126892,265.318695,591.976074,266.112061L592.52002,266.60791C592.624634,266.694641,592.652649,266.847107,592.583496,266.963867L592.543945,267.023926L592.486084,267.072266L592.416016,267.104004C592.101624,267.121246,591.788391,267.055573,591.50415,266.919922L591.467285,266.98999C591.441162,267.029327,591.415771,267.06897,591.384033,267.104004L591.329834,267.132568L591.271973,267.1521L591.224121,267.159912C591.009155,267.121216,590.797791,267.065186,590.592041,266.991943C590.539795,267.120544,590.417725,267.203796,590.278809,267.203613C590.148865,267.198761,590.020874,267.169403,589.904053,267.112061C589.881409,267.158112,589.858337,267.203918,589.832031,267.248047L589.77002,267.29126L589.704102,267.328125L589.615967,267.335938L589.528076,267.328125L589.080078,267.199951C589.059692,267.237671,589.033691,267.269928,589.015869,267.307861L588.944092,267.366943L588.860107,267.408447L588.768066,267.432129C588.585144,267.423889,588.403992,267.390686,588.231934,267.328125L588.192139,267.36792L588.135986,267.399902C587.910461,267.442139,587.677734,267.419708,587.464111,267.335938C587.430176,267.368042,587.39801,267.40152,587.359131,267.427734C587.32196,267.442078,587.284241,267.455109,587.245117,267.463379L587.184082,267.464111L586.808105,267.407959L586.800049,268.528076C590.399292,270.419281,594.244202,271.800507,598.224121,272.63208C599.37738,271.418854,600.379272,270.070313,601.208008,268.615967L601.072021,268.63208L600.927979,268.63208L600.837402,268.611816C600.778259,268.592438,600.72522,268.560394,600.672119,268.528076C600.630859,268.484894,600.587585,268.44516,600.560059,268.39209C600.28656,268.485168,600.000793,268.538147,599.711914,268.543945C599.632813,268.538055,599.558105,268.506744,599.495117,268.458496L599.412109,268.375488L599.352051,268.271973L598.86792,268.414795L598.375977,268.528076C598.258301,268.554474,598.135132,268.529877,598.036133,268.461029C597.937073,268.392212,597.871094,268.285339,597.848633,268.166748L597.832275,268.063965L597.834229,267.959473L597.85498,267.856201L597.894287,267.756836L597.952148,267.664063C598.083008,267.572815,598.220337,267.491302,598.363525,267.420898L598.571777,267.280518C598.891052,267.029388,599.182007,266.744415,599.441406,266.431885C600.566284,265.202057,601.567993,263.865051,602.432129,262.439941L602.343994,262.424072C602.261902,262.3927,602.186462,262.345917,602.12793,262.280029L602.080078,262.199951L601.6521,262.304199L601.216064,262.36792L601.143311,262.342041L601.072021,262.312012L601.004395,262.269043C600.943604,262.214417,600.896973,262.146667,600.864014,262.072021C600.523743,262.235748,600.145935,262.305695,599.769531,262.275635C599.671753,262.263336,599.582153,262.210022,599.528076,262.12793C599.481506,262.072449,599.457825,262.003662,599.444092,261.932861C599.428955,261.848724,599.432678,261.762177,599.456055,261.679932C599.536011,261.497986,599.674805,261.350464,599.840088,261.23999C600.011597,261.111145,600.1875,260.988159,600.36792,260.87207L600.843994,260.390625C601.937561,259.250793,602.904663,257.996002,603.728027,256.647949L603.704102,256.583984L603.34668,256.679199L602.984131,256.751953C602.909668,256.755005,602.83783,256.729675,602.776123,256.687988L602.647949,256.583984L602.60791,256.503906L602.334717,256.599854C602.034363,256.724182,601.705688,256.766663,601.384033,256.719971C601.2229,256.652802,601.110291,256.50769,601.080078,256.335938C601.079468,256.236725,601.105652,256.139038,601.159912,256.055908L601.199951,256L601.853027,255.44873C602.911011,254.510452,603.836609,253.432587,604.604492,252.245117C604.961182,251.742386,605.293396,251.222702,605.600098,250.687988L586.728027,250.687988z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M630.615967,250.687988L631.01416,251.359619C631.685059,252.567459,632.585266,253.632889,633.664063,254.496094L633.736084,254.583984L633.760986,254.69458L633.76001,254.808105C633.723938,254.888,633.6698,254.959335,633.592041,255C633.289978,255.033524,632.984009,254.983276,632.711914,254.8479C632.659424,254.929581,632.583313,254.994354,632.488037,255.016113C632.313293,255.012939,632.142151,254.97049,631.984131,254.895996L631.968018,254.936035L631.944092,254.976074L632.155273,255.356201C632.7948,256.441742,633.619629,257.40686,634.592041,258.208008C634.610718,258.23465,634.629944,258.260864,634.647949,258.288086C634.65741,258.32019,634.671143,258.350739,634.679932,258.384033L634.679932,258.464111C634.666138,258.507599,634.655151,258.551361,634.630615,258.590332C634.6026,258.62616,634.57019,258.65799,634.536133,258.687988C634.278503,258.74353,634.010803,258.703735,633.776123,258.583984C633.748901,258.63443,633.707092,258.673004,633.663574,258.709717C633.614319,258.739288,633.559387,258.754059,633.50415,258.768066C633.310608,258.734314,633.120178,258.684479,632.936035,258.615967C632.897705,258.716431,632.810181,258.790527,632.704102,258.808105C633.29364,259.863251,634.010254,260.842041,634.838135,261.722656C635.089233,261.941711,635.354187,262.144318,635.63208,262.328125L635.664063,262.375977C635.678833,262.425812,635.686646,262.47702,635.696045,262.528076C635.714355,262.658783,635.657043,262.789368,635.547119,262.862305C635.394104,262.907715,635.231506,262.903381,635.079346,262.855469C634.953735,262.836823,634.832825,262.794617,634.719971,262.736084C634.665649,262.801422,634.597351,262.852844,634.52002,262.887939C634.29071,262.898621,634.06189,262.859924,633.848145,262.776123C633.811646,262.82901,633.752808,262.862335,633.688965,262.871338L633.60791,262.887939L633.52002,262.887939L633.439941,262.880127C633.536194,263.049347,633.642456,263.212738,633.76001,263.36792C633.928528,263.733429,634.132202,264.081726,634.36792,264.407959L635.234619,263.727539C639.69574,260.13678,643.410156,255.706909,646.167969,250.687988L630.615967,250.687988z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M568.575928,250.687988L569.114502,251.636963C571.994019,256.572754,575.805054,260.902008,580.335938,264.384033L580.603271,264.017578C580.865479,263.647583,581.109924,263.265228,581.335938,262.87207C581.21936,262.896362,581.098511,262.877167,580.991943,262.823975C580.956665,262.794128,580.933777,262.755493,580.904053,262.719971C580.738525,262.80426,580.553345,262.846283,580.369141,262.825684L580.285645,262.804932L580.207275,262.769775L580.135986,262.719971L580.095947,262.640137L579.776123,262.736084L579.439941,262.815918L579.384033,262.823975C579.343567,262.81842,579.303467,262.810364,579.263916,262.800049C579.2276,262.775604,579.194031,262.747498,579.163818,262.71582L579.120117,262.656006L579.095947,262.560059C579.082031,262.458038,579.108093,262.356873,579.1521,262.263916C579.186462,262.215454,579.230042,262.173615,579.288086,262.159912L579.343994,262.144043C579.516052,262.031403,579.678589,261.904968,579.832031,261.768066C580.706909,260.850861,581.48468,259.845673,582.1521,258.768066C582.110291,258.760681,582.070374,258.746033,582.031982,258.728027L581.959961,258.679932L581.912109,258.600098L581.666016,258.664307C581.542358,258.694824,581.415466,258.710236,581.288086,258.704102C581.203979,258.671112,581.137207,258.604065,581.104004,258.52002C580.878967,258.626953,580.630066,258.673615,580.381836,258.652588L580.303955,258.624023L580.252686,258.582031L580.208984,258.533203L580.174805,258.477783L580.1521,258.416016L580.1521,258.328125L580.176025,258.23999C580.341064,258.029999,580.546082,257.85553,580.776123,257.719971L581.400146,257.072266C582.003906,256.405243,582.542175,255.681763,583.008057,254.912109L582.991943,254.864014L582.743164,254.933838C582.632141,254.970413,582.513794,254.980423,582.400146,254.951904C582.344482,254.916885,582.292969,254.875275,582.263916,254.815918C582.038635,254.933609,581.784302,254.986603,581.531006,254.965576L581.400146,254.936035C581.363403,254.907333,581.329468,254.875351,581.300537,254.838623L581.271484,254.77417L581.256104,254.704102L581.263916,254.60791L581.303955,254.511963L581.800293,254.076416C582.564697,253.382294,583.226929,252.583511,583.768066,251.704102C583.993469,251.371475,584.209717,251.032791,584.416016,250.687988L568.575928,250.687988z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M563.056152,229.176025L563.068604,230.378906C563.213257,236.392517,564.586487,242.312973,567.104004,247.776123L647.640137,247.776123C650.321899,241.943176,651.700562,235.595901,651.679932,229.176025L563.056152,229.176025z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M576.39624,197.574463L575.651855,198.317383C568.286743,205.84317,563.858459,215.756485,563.167969,226.263916L567.640137,226.263916L567.656006,226.224121L567.704102,226.167969L598.352051,226.167969C599.078125,225.194885,599.480835,224.018127,599.503662,222.804199C599.528076,222.261673,599.490967,221.718079,599.39209,221.184082C599.027771,218.14769,597.953491,215.239914,596.256104,212.696045L595.263916,210.280029C594.852905,209.277466,594.492554,208.254776,594.184082,207.216064L594.166748,207.113281C594.119995,206.936066,594.00293,206.786316,593.843994,206.695068C593.571289,206.507233,593.27002,206.364777,592.952148,206.271973L591.577637,205.880371L590.208008,205.471924C589.246277,205.157959,588.348083,204.675461,587.55542,204.046875C586.982056,203.570923,586.547241,202.949631,586.296143,202.248047L586.302246,202.025635C586.269348,201.417114,585.823486,200.9104,585.224121,200.800049L584.729004,200.702881C584.236877,200.591736,583.754883,200.439453,583.288086,200.248047C581.141418,199.313095,579.089294,198.174515,577.159912,196.8479L576.39624,197.574463z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M613.719971,186.135986C613.382813,188.086838,612.613098,189.937408,611.467773,191.552246C610.064026,193.580353,608.335327,195.362915,606.351318,196.828369L605.92627,197.189209C605.588562,197.504303,605.271729,197.841003,604.977783,198.197266C604.822693,198.417984,604.727966,198.6754,604.704102,198.944092C604.752136,199.150116,604.903381,199.316849,605.104004,199.384033C606.904724,200.875565,608.863098,202.165833,610.944092,203.231934L611.308105,203.312012C611.78595,203.431244,612.250854,203.597443,612.696045,203.808105L612.615479,203.615479C612.197571,202.469086,612.763977,201.19751,613.89624,200.742676C614.947632,200.326401,616.143799,200.772369,616.664063,201.776367C617.233765,202.831909,616.87384,204.148651,615.847412,204.769287C615.375366,205.055496,614.812439,205.15271,614.271973,205.040039L614.375977,205.271973C614.55304,205.954437,614.291687,206.674927,613.719727,207.087158C613.042908,207.655853,612.276489,208.108459,611.45166,208.426514C610.514526,208.790085,609.5448,209.06308,608.555664,209.241943L607.60791,209.488037C607.037537,209.62886,606.493652,209.860611,605.99707,210.174561C605.633484,210.458984,605.512817,210.956558,605.708252,211.374756C605.816528,211.713379,605.968506,212.036331,606.159912,212.335938L608.173584,215.187256C610.181458,218.045746,612.163269,220.912384,614.12793,223.800049C614.702209,224.506638,615.14856,225.308105,615.447998,226.167969L651.575928,226.263916C650.17627,205.416595,634.392883,188.386093,613.711914,185.407959L613.719971,186.135986z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M591.488037,195.224121L591.30835,195.289551C591.068359,195.400803,590.883545,195.60495,590.800049,195.855957L590.584229,196.180908C590.184448,196.773697,589.700623,197.305313,589.148193,197.759277L588.848145,198.063965C588.74231,198.20314,588.716125,198.385193,588.768066,198.552002C588.933167,198.760315,589.164673,198.905533,589.423828,198.9646C590.04303,199.16687,590.689453,199.273743,591.34082,199.28125C592.361145,199.334747,593.383545,199.229599,594.371582,198.969238C594.495972,198.918411,594.618286,198.862732,594.73877,198.803223C594.981262,198.665497,595.109192,198.389648,595.062256,198.114746C595.033386,197.894836,594.951599,197.685303,594.823975,197.503906C594.139038,196.555359,593.217957,195.80249,592.151855,195.320557C591.943176,195.23024,591.713928,195.197784,591.488037,195.224121z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M584.899658,191.065186L583.633545,191.837891C582.798157,192.367691,581.98114,192.926086,581.184082,193.511963C580.824707,194.1353,580.722351,194.87468,580.899658,195.572021C580.990845,195.944321,581.181152,196.284851,581.451172,196.556885C582.51239,197.57933,584.064453,197.906891,585.448242,197.400146C585.717773,197.297897,585.975525,197.166916,586.216064,197.008057C587.05835,196.393875,587.859924,195.725754,588.615967,195.008057C588.979858,194.587997,589.033691,193.982941,588.751953,193.503906C588.374451,192.785294,587.857849,192.148926,587.231934,191.63208C586.915222,191.176895,586.56781,190.74379,586.192139,190.335938L584.899658,191.065186z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M597.60498,186.037598L596.433105,186.321045C595.353821,186.592133,594.285828,186.906647,593.231934,187.263916L593.27002,187.701172C593.31427,188.553787,593.245544,189.408478,593.064941,190.24292L592.936035,191.095947C592.823914,191.7966,592.856506,192.512589,593.030762,193.200439C593.141174,193.504318,593.342896,193.766724,593.60791,193.951904C594.720947,194.655899,596.070923,194.881119,597.352051,194.575928C598.402954,194.523178,599.318481,193.842606,599.672119,192.851563C599.806152,192.479965,599.884521,192.090591,599.904053,191.696045C599.949158,190.073807,599.743042,188.454468,599.292969,186.895264C599.162292,186.508163,598.991699,186.135727,598.783936,185.783936L597.60498,186.037598z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M606.971924,184.953369L606.424072,184.959961L606.547119,185.690918C606.834534,187.651184,606.768433,189.647018,606.352051,191.583984L606.327881,191.910889C606.324951,192.238861,606.381958,192.56459,606.496094,192.87207C606.546082,193.003113,606.6698,193.094055,606.809814,193.092041C607.018616,193.105972,607.220825,193.015732,607.354004,192.854248L607.52002,192.711914C608.866028,191.220337,609.956787,189.517075,610.748779,187.670654C611.020874,186.984467,611.220337,186.271667,611.343994,185.543945L610.617432,185.4021C609.591492,185.210129,608.558167,185.059906,607.52002,184.951904L606.971924,184.953369z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M603.286865,185.141602L602.399414,185.231445C601.808533,185.298187,601.21936,185.378296,600.63208,185.471924C601.234131,186.468979,601.682922,187.550766,601.963623,188.681152C602.066833,189.183746,602.122009,189.694977,602.12793,190.208008L602.288086,190.552002C602.458862,190.936752,602.736694,191.264389,603.088135,191.496094C603.139404,191.515121,603.193115,191.526001,603.247559,191.530762C603.493225,191.490326,603.692322,191.311615,603.76001,191.072021C604.371399,189.133102,604.513977,187.076782,604.176025,185.072021L603.286865,185.141602z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M589.303467,188.793701L588.50415,189.159912L588.199951,189.312012C588.567749,189.696991,589.049417,190.025528,589.454346,190.286865C589.552856,190.32489,589.657593,190.344101,589.763184,190.342773C589.90094,190.323853,590.022888,190.242981,590.088135,190.120117C590.198792,189.787766,590.226257,189.433289,590.167969,189.087891L590.112061,188.447998L589.303467,188.793701z"/>
    <path transform="matrix(-1,0,0,1,-14.963074,-60.176025)" style="stroke:#ff0000;stroke-width:0.05mm;fill:none" d="M607.116943,173.392578L606.86499,173.431152C605.496216,173.713638,604.611816,175.047714,604.885742,176.418213C605.142883,177.702103,606.333679,178.580048,607.63623,178.446777C609.025024,178.305023,610.037964,177.067688,609.905273,175.677979C609.781128,174.37442,608.686157,173.378922,607.376709,173.379395L607.116943,173.392578z"/>
</svg>`
  };

  window.LaserTemplates.reindeer = reindeerTemplate;
  window.LaserTemplates.reindeer_ornament = reindeerTemplate;
})();


/* ==========================================================================
   2D MODULE: laser-studio-template-ring.js
   ========================================================================== */

/**
 * Laser Ornament Studio - Template: Standard Circular Ring
 * File: assets/laser-studio-template-ring.js
 * Template ID: "none"
 */

(function () {
  window.LaserTemplates = window.LaserTemplates || {};

  const ringTemplate = {
    id: "none",
    name: "Standard Ring",
    badge: "Circular",
    description: "Clean circular ring frame with support for dual outer arc scoring",
    iconSvg: `<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M 45.16 19.34 A 6.5 6.5 0 1 1 54.84 19.34 A 35 35 0 1 1 45.16 19.34 Z" style="stroke:#ff3b30;stroke-width:1.5;fill:none"/>
      <circle cx="50" cy="15" r="2.8" style="stroke:#ff3b30;stroke-width:1.5;fill:none"/>
      <circle cx="50" cy="54" r="28" style="stroke:#ff3b30;stroke-width:1.5;fill:none"/>
      <text x="50" y="59.5" fill="#007aff" font-size="16" font-family="'Segoe UI', Inter, -apple-system, sans-serif" font-weight="900" text-anchor="middle" letter-spacing="1.5">NAME</text>
    </svg>`,
    presets: {
      circleMode: "ring",
      sizeInches: 4.0,
      circleRadius: 144,
      ringThickness: 25.2,
      useLoop: true,
      loopOuterRad: 13.5,
      loopInnerRad: 4.5,
      useSupportBar: false,
      barHeight: 12,
      barYOffset: 0,
      fontSize: 84,
      textYOffset: 0,
      outerFontSize: 20
    }
  };

  window.LaserTemplates.none = ringTemplate;
})();


/* ==========================================================================
   2D MODULE: laser-studio-geo-math.js
   ========================================================================== */

/**
 * Laser Ornament Studio - 2D Vector CAD Math & Style Utilities
 * Module: assets/laser-studio-geo-math.js
 * Responsibility: Pure geometric math, chord containment calculation, and CAD path styling tokens.
 */

window.LaserStudio = window.LaserStudio || {};

window.LaserStudio.GeoMath = {
  // Laser cutting & scoring visual stroke tokens
  STYLES: {
    CUT: {
      strokeColor: '#e11d48', // Laser Cut Red
      strokeWidth: 2,
      fillColor: null
    },
    SCORE: {
      strokeColor: '#0078ec', // Laser Score Blue (F&C Brand Accent)
      strokeWidth: 1.5,
      fillColor: null
    }
  },

  /**
   * Applies stroke, stroke width, fill, and debug name to a Paper.js path item
   * @param {paper.Item} pathItem - Target Paper.js path or compound path
   * @param {Object} style - Object with strokeColor, strokeWidth, fillColor
   * @param {String} [name] - Optional identifying label
   */
  applyStyle: function (pathItem, style, name) {
    if (!pathItem) return;
    pathItem.fillColor = style.fillColor;
    pathItem.strokeColor = style.strokeColor;
    pathItem.strokeWidth = style.strokeWidth;
    if (name) pathItem.name = name;
  },

  /**
   * Calculates maximum horizontal span of a circular chord given radius and Y-offset
   * Formula: W = 2 * sqrt(R^2 - dY^2)
   * @param {Number} radius - Inner or outer circle radius
   * @param {Number} dY - Vertical offset from circle center
   * @returns {Number} Maximum chord width in points/pixels
   */
  calculateChordWidth: function (radius, dY) {
    const absDY = Math.abs(dY);
    if (absDY >= radius) return radius * 0.85 * 2;
    return Math.sqrt(radius * radius - absDY * absDY) * 2;
  },

  /**
   * Calculates safe max width contained within an inner ring boundary
   * @param {Number} innerRadius - Inner boundary radius
   * @param {Number} dY - Vertical offset
   * @param {Number} [margin=16] - Safety clearance margin from inner edge
   * @returns {Number} Safe width constraint
   */
  calculateMaxContainedWidth: function (innerRadius, dY, margin = 16) {
    const fullChord = this.calculateChordWidth(innerRadius, dY);
    return Math.max(20, fullChord - margin);
  }
};


/* ==========================================================================
   2D MODULE: laser-studio-geo-contact.js
   ========================================================================== */

/**
 * Laser Ornament Studio - 2D Vector CAD Contact Solver & Seam Extractor
 * Module: assets/laser-studio-geo-contact.js
 * Responsibility: Bounded binary search contact positioning, letter weld overlap solver, and 1D score seam extraction.
 */

window.LaserStudio = window.LaserStudio || {};

window.LaserStudio.GeoContact = {
  /**
   * Positions sequential letter glyph paths so they touch and overlap with structural integrity
   * Uses bounded binary search O(log K) for accurate contact detection across complex typography
   * @param {Array<paper.Path>} charPaths - Individual glyph paths
   * @param {Number} fontSize - Font size in points
   * @param {Number} letterSpacing - User kerning/tracking adjustment
   */
  positionTouchingLetters: function (charPaths, fontSize, letterSpacing) {
    if (!charPaths || charPaths.length < 2) return;

    // Base structural overlap required for laser cutting integrity (~3% of font size)
    const baseOverlap = Math.max(2.5, fontSize * 0.03);
    const targetOverlap = Math.max(0.5, baseOverlap - letterSpacing);

    for (let i = 1; i < charPaths.length; i++) {
      const pPrev = charPaths[i - 1];
      const pCurr = charPaths[i];

      if (!pPrev || !pCurr || pPrev.bounds.width === 0 || pCurr.bounds.width === 0) continue;

      // 1. Initial bounding box shift
      const initialDx = (pPrev.bounds.right - pCurr.bounds.left) - targetOverlap;
      pCurr.position.x += initialDx;

      // 2. Test geometric intersection
      let intersects = pPrev.intersects(pCurr);

      // 3. If slanted or complex glyph does not intersect, binary search to find exact contact point
      if (!intersects) {
        const maxExtraShift = pPrev.bounds.width * 0.75;
        const baseX = pCurr.position.x;

        pCurr.position.x = baseX - maxExtraShift;
        if (pPrev.intersects(pCurr)) {
          let low = 0;
          let high = maxExtraShift;
          let optimalShift = maxExtraShift;

          // 6 iterations gives sub-pixel precision (< 0.5px) with negligible CPU overhead
          for (let iter = 0; iter < 6; iter++) {
            const mid = (low + high) / 2;
            pCurr.position.x = baseX - mid;
            if (pPrev.intersects(pCurr)) {
              optimalShift = mid;
              high = mid;
            } else {
              low = mid;
            }
          }

          pCurr.position.x = baseX - optimalShift - Math.min(targetOverlap, 2.0);
        } else {
          pCurr.position.x = baseX;
        }
      }
    }
  },

  /**
   * Extracts clean 1D curve/line segments of a target path that lie strictly inside a clipping shape
   * Filters out horizontal top/bottom baseline stubs to leave only clean vertical/tangent dividing seams
   * @param {paper.Path|paper.CompoundPath} targetPath - Source path to extract seams from
   * @param {paper.Path|paper.CompoundPath} clipShape - Clipping boundary
   * @returns {Array<paper.Path>} Array of clean 1D score paths
   */
  extractInternalCurves: function (targetPath, clipShape) {
    if (!targetPath || !clipShape) return [];

    const collectedSegments = [];
    const pathsToProcess = [];

    if (targetPath instanceof paper.CompoundPath) {
      targetPath.children.forEach((c) => {
        if (c instanceof paper.Path) pathsToProcess.push(c.clone());
      });
    } else if (targetPath instanceof paper.Path) {
      pathsToProcess.push(targetPath.clone());
    }

    for (const pathItem of pathsToProcess) {
      try {
        const intersections = pathItem.getIntersections(clipShape);

        if (!intersections || intersections.length === 0) {
          if (pathItem.curves && pathItem.curves.length > 0) {
            const midPt = pathItem.curves[0].getPointAt(0.5);
            const isInside = clipShape.contains(midPt) || clipShape.hitTest(midPt, { fill: true, tolerance: 0.5 });
            const onClipStroke = clipShape.hitTest(midPt, { stroke: true, tolerance: 0.5 });

            if (isInside && !onClipStroke) {
              const cloned = pathItem.clone();
              cloned.fillColor = null;
              collectedSegments.push(cloned);
            }
          }
          pathItem.remove();
          continue;
        }

        // Divide path at intersection locations (descending order to maintain valid offsets)
        const sortedLocs = intersections.slice().sort((a, b) => b.offset - a.offset);
        for (const loc of sortedLocs) {
          try {
            pathItem.divideAt(loc);
          } catch (e) {}
        }

        for (let cIdx = 0; cIdx < pathItem.curves.length; cIdx++) {
          const curve = pathItem.curves[cIdx];
          if (!curve || curve.length < 0.3) continue;

          // Skip horizontal top/bottom baseline caps along cut edges
          const isHorizontalStub =
            (curve.isStraight() || (curve.handle1.isZero() && curve.handle2.isZero())) &&
            Math.abs(curve.point1.y - curve.point2.y) < 0.8 &&
            Math.abs(curve.point1.x - curve.point2.x) > 0.2;

          if (isHorizontalStub) continue;

          const midPoint = curve.getPointAt(0.5);
          const isInside = clipShape.contains(midPoint) || clipShape.hitTest(midPoint, { fill: true, tolerance: 0.5 });
          const onClipBoundary = clipShape.hitTest(midPoint, { stroke: true, tolerance: 0.5 });

          if (isInside && !onClipBoundary) {
            const scoreLine = new paper.Path();
            scoreLine.moveTo(curve.point1);
            if (curve.isStraight() || (curve.handle1.isZero() && curve.handle2.isZero())) {
              scoreLine.lineTo(curve.point2);
            } else {
              scoreLine.cubicCurveTo(
                curve.point1.add(curve.handle1),
                curve.point2.add(curve.handle2),
                curve.point2
              );
            }
            scoreLine.fillColor = null;
            collectedSegments.push(scoreLine);
          }
        }
      } catch (err) {
        console.warn('[LaserStudio.GeoContact] Error extracting internal curves:', err);
      }
      pathItem.remove();
    }

    return collectedSegments;
  },

  /**
   * Generates clean 1D dividing score lines between adjacent overlapping letters
   * @param {Array<paper.Path>} letterPaths - Array of individual letter paths
   * @returns {paper.CompoundPath|null} Compound path of score seams or null
   */
  getLetterOverlapScoreSegments: function (letterPaths) {
    if (!letterPaths || letterPaths.length < 2) return null;

    const scoreItems = [];
    for (let i = 1; i < letterPaths.length; i++) {
      const pPrev = letterPaths[i - 1];
      const pCurr = letterPaths[i];

      if (!pPrev || !pCurr || pPrev.bounds.width === 0 || pCurr.bounds.width === 0) continue;

      if (pPrev.intersects(pCurr) || pPrev.bounds.intersects(pCurr.bounds)) {
        try {
          let seam = this.extractInternalCurves(pCurr, pPrev);
          if (!seam || seam.length === 0) {
            seam = this.extractInternalCurves(pPrev, pCurr);
          }
          if (seam && seam.length > 0) {
            scoreItems.push(...seam);
          }
        } catch (e) {
          console.warn('[LaserStudio.GeoContact] Error computing letter overlap seam:', e);
        }
      }
    }

    return scoreItems.length > 0 ? new paper.CompoundPath({ children: scoreItems }) : null;
  },

  /**
   * Generates 1D score segments where a horizontal support bar intersects textShape
   * @param {paper.Path|paper.CompoundPath} textShape - Merged text shape
   * @param {Number} yPos - Horizontal line Y position
   * @returns {paper.CompoundPath|null}
   */
  getHorizontalScoreSegments: function (textShape, yPos) {
    if (!textShape || !Number.isFinite(yPos)) return null;

    const testLine = new paper.Path.Line(
      new paper.Point(textShape.bounds.left - 50, yPos),
      new paper.Point(textShape.bounds.right + 50, yPos)
    );

    const intersections = textShape.getIntersections(testLine);
    testLine.remove();

    if (!intersections || intersections.length < 2) return null;

    const sortedPoints = intersections.map((loc) => loc.point).sort((a, b) => a.x - b.x);
    const uniquePoints = sortedPoints.filter((pt, i, arr) => i === 0 || Math.abs(pt.x - arr[i - 1].x) > 0.05);

    if (uniquePoints.length < 2) return null;

    const segments = [];
    for (let i = 0; i < uniquePoints.length - 1; i++) {
      const p1 = uniquePoints[i];
      const p2 = uniquePoints[i + 1];
      const dx = p2.x - p1.x;

      if (dx > 0.2) {
        const midPoint = new paper.Point((p1.x + p2.x) / 2, yPos);
        if (textShape.contains(midPoint) || textShape.hitTest(midPoint, { fill: true, tolerance: 0.5 })) {
          segments.push(new paper.Path.Line(new paper.Point(p1.x, yPos), new paper.Point(p2.x, yPos)));
        }
      }
    }

    return segments.length > 0 ? new paper.CompoundPath({ children: segments }) : null;
  },

  /**
   * Generates 1D score segments where a circular boundary intersects textShape
   * @param {paper.Path|paper.CompoundPath} textShape - Merged text shape
   * @param {paper.Point} centerPoint - Center of the circular boundary
   * @param {Number} radius - Radius of boundary
   * @returns {paper.CompoundPath|null}
   */
  getCircularScoreSegments: function (textShape, centerPoint, radius) {
    if (!textShape || !centerPoint || !Number.isFinite(radius) || radius <= 0) return null;

    const testCircle = new paper.Path.Circle(centerPoint, radius);
    const intersections = textShape.getIntersections(testCircle);
    testCircle.remove();

    if (!intersections || intersections.length < 2) return null;

    const sorted = intersections
      .map((loc) => ({ pt: loc.point, angle: Math.atan2(loc.point.y - centerPoint.y, loc.point.x - centerPoint.x) }))
      .sort((a, b) => a.angle - b.angle)
      .filter((item, i, arr) => i === 0 || Math.abs(item.angle - arr[i - 1].angle) > 0.005);

    if (sorted.length < 2) return null;

    const segments = [];
    const count = sorted.length;

    for (let i = 0; i < count; i++) {
      const p1 = sorted[i];
      const p2 = sorted[(i + 1) % count];
      let dAngle = p2.angle - p1.angle;
      if (dAngle < 0) dAngle += 2 * Math.PI;

      if (dAngle > 0.005 && dAngle < Math.PI) {
        const midAngle = p1.angle + dAngle / 2;
        const midPoint = new paper.Point(
          centerPoint.x + radius * Math.cos(midAngle),
          centerPoint.y + radius * Math.sin(midAngle)
        );

        if (textShape.contains(midPoint) || textShape.hitTest(midPoint, { fill: true, tolerance: 0.5 })) {
          segments.push(new paper.Path.Arc(p1.pt, midPoint, p2.pt));
        }
      }
    }

    return segments.length > 0 ? new paper.CompoundPath({ children: segments }) : null;
  }
};


/* ==========================================================================
   2D MODULE: laser-studio-geo-text.js
   ========================================================================== */

/**
 * Laser Ornament Studio - 2D Vector CAD OpenType-to-Paper Converter
 * Module: assets/laser-studio-geo-text.js
 * Responsibility: Converts OpenType glyph outlines to Paper.js vector paths, applies height scaling, and welds sequential letter shapes.
 */

window.LaserStudio = window.LaserStudio || {};

window.LaserStudio.GeoText = {
  /**
   * Resolves the active font from state with closest available weight fallback
   * @param {Object} state - Application state tree (window.LaserState)
   * @param {String} fontFam - Desired font family name
   * @param {Number} rawWeight - Numerical weight (400-800)
   * @returns {opentype.Font|null}
   */
  resolveActiveFont: function (state, fontFam, rawWeight) {
    if (!state || !state.fonts) return null;

    if (state.fonts[fontFam] && state.fonts[fontFam][rawWeight]) {
      return state.fonts[fontFam][rawWeight];
    }

    if (state.fonts[fontFam]) {
      const availableWeights = Object.keys(state.fonts[fontFam])
        .map(Number)
        .filter((w) => state.fonts[fontFam][w]);

      if (availableWeights.length > 0) {
        availableWeights.sort((a, b) => Math.abs(a - rawWeight) - Math.abs(b - rawWeight));
        return state.fonts[fontFam][availableWeights[0]];
      }
    }

    // Global fallback to any loaded font
    for (const fam of Object.keys(state.fonts)) {
      for (const w of Object.keys(state.fonts[fam])) {
        if (state.fonts[fam][w]) {
          return state.fonts[fam][w];
        }
      }
    }

    return null;
  },

  /**
   * Converts a string into a merged, sequentially welded Paper.js CompoundPath
   * @param {opentype.Font} targetFont - OpenType font instance
   * @param {String} text - Main text string
   * @param {Number} fontSize - Font size in points
   * @param {Number} letterSpacing - Tracking adjustment
   * @param {paper.Point} centerPoint - Desired visual center
   * @param {Object} [fitHeightOptions] - { topY, bottomY, weldMargin } for slot line constraint
   * @param {Boolean} [touchingLetters=false] - If true, overlaps adjacent letters to form single welded chain
   * @returns {paper.Path|paper.CompoundPath|null}
   */
  getUnitedTextPath: function (targetFont, text, fontSize, letterSpacing, centerPoint, fitHeightOptions, touchingLetters) {
    if (!targetFont || !text) return null;

    let x = 0;
    const charPaths = [];

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const glyph = targetFont.charToGlyph(char);

      let kerning = 0;
      if (i > 0 && !touchingLetters) {
        const prevGlyph = targetFont.charToGlyph(text[i - 1]);
        const kerningVal = targetFont.getKerningValue(prevGlyph, glyph);
        kerning = kerningVal * (fontSize / targetFont.unitsPerEm);
      }

      x += kerning;

      const glyphPath = glyph.getPath(x, 0, fontSize);
      const pathData = glyphPath.toPathData(2);

      if (pathData) {
        const paperCharPath = new paper.CompoundPath(pathData);
        paperCharPath.fillColor = 'black';
        charPaths.push(paperCharPath);
      }

      const advance = glyph.advanceWidth * (fontSize / targetFont.unitsPerEm);
      x += advance + (touchingLetters ? 0 : letterSpacing);
    }

    if (charPaths.length === 0) return null;

    // Scale vertical bounds to fit exactly between support lines if configured
    if (fitHeightOptions && Number.isFinite(fitHeightOptions.topY) && Number.isFinite(fitHeightOptions.bottomY)) {
      const weldMargin = fitHeightOptions.weldMargin !== undefined ? fitHeightOptions.weldMargin : 0.4;
      const targetTop = fitHeightOptions.topY - weldMargin;
      const targetBottom = fitHeightOptions.bottomY + weldMargin;
      const targetHeight = targetBottom - targetTop;
      const targetCenterY = (targetTop + targetBottom) / 2;

      for (let i = 0; i < charPaths.length; i++) {
        const p = charPaths[i];
        if (p.bounds.height > 0 && targetHeight > 0) {
          const scaleY = targetHeight / p.bounds.height;
          p.scale(1, scaleY, p.position);
          p.position.y = targetCenterY;
        }
      }
    } else if (touchingLetters && window.LaserStudio.GeoContact) {
      window.LaserStudio.GeoContact.positionTouchingLetters(charPaths, fontSize, letterSpacing);
    }

    const tempGroup = new paper.Group(charPaths);
    const boundsOffsetX = centerPoint.x - tempGroup.position.x;
    const boundsOffsetY =
      fitHeightOptions && Number.isFinite(fitHeightOptions.topY)
        ? 0
        : centerPoint.y - tempGroup.position.y;

    tempGroup.translate(new paper.Point(boundsOffsetX, boundsOffsetY));

    const positionedPaths = [];
    while (tempGroup.children.length > 0) {
      const child = tempGroup.children[0];
      tempGroup.project.activeLayer.addChild(child);
      positionedPaths.push(child);
    }
    tempGroup.remove();

    // Retain individual letter outlines for stroke/score rendering
    const individualLetterPaths = positionedPaths.map((p) => p.clone());

    let unitedText = positionedPaths[0];
    for (let i = 1; i < positionedPaths.length; i++) {
      const nextChar = positionedPaths[i];
      const temp = unitedText.unite(nextChar);
      unitedText.remove();
      nextChar.remove();
      unitedText = temp;
    }

    if (unitedText) {
      unitedText.individualPaths = individualLetterPaths;
    }

    return unitedText;
  }
};


/* ==========================================================================
   2D MODULE: laser-studio-geo-scores.js
   ========================================================================== */

/**
 * Laser Ornament Studio - 2D Vector CAD Curved Arc Scoring Engine
 * Module: assets/laser-studio-geo-scores.js
 * Responsibility: Generates curved text along circular arcs with polar-to-Cartesian transforms and glyph rotation for laser scoring.
 */

window.LaserStudio = window.LaserStudio || {};

window.LaserStudio.GeoScores = {
  /**
   * Converts a string into a curved Paper.js CompoundPath positioned along a circular arc
   * @param {opentype.Font} targetFont - OpenType font instance
   * @param {String} text - Text string to curve
   * @param {Number} fontSize - Font size in points
   * @param {Number} r - Radius of the arc from centerPoint
   * @param {paper.Point} centerPoint - Center of the circular ornament
   * @param {Boolean} isTop - True for top arc (text upright along top), false for bottom arc (upright along bottom)
   * @returns {paper.Path|paper.CompoundPath|null}
   */
  getCurvedTextPath: function (targetFont, text, fontSize, r, centerPoint, isTop) {
    if (!targetFont || !text) return null;

    const glyphs = [];
    const widths = [];
    let totalWidth = 0;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const glyph = targetFont.charToGlyph(char);
      glyphs.push(glyph);

      const advance = glyph.advanceWidth * (fontSize / targetFont.unitsPerEm);
      widths.push(advance);
      totalWidth += advance;

      if (i < text.length - 1) {
        totalWidth += 1.5;
      }
    }

    const charPaths = [];
    let currentX = 0;

    for (let i = 0; i < text.length; i++) {
      const glyph = glyphs[i];
      const advance = widths[i];

      const charMidX = currentX + advance / 2;
      currentX += advance + 1.5;

      let angle = 0;
      if (isTop) {
        angle = -Math.PI / 2 + (charMidX - totalWidth / 2) / r;
      } else {
        angle = Math.PI / 2 - (charMidX - totalWidth / 2) / r;
      }

      const posX = centerPoint.x + r * Math.cos(angle);
      const posY = centerPoint.y + r * Math.sin(angle);

      const glyphPath = glyph.getPath(-advance / 2, 0, fontSize);
      const pathData = glyphPath.toPathData(2);

      if (pathData) {
        const charPath = new paper.CompoundPath(pathData);
        charPath.fillColor = 'black';

        const rotateRad = isTop ? angle + Math.PI / 2 : angle - Math.PI / 2;
        const rotateDeg = rotateRad * (180 / Math.PI);

        charPath.rotate(rotateDeg, new paper.Point(0, 0));
        charPath.translate(new paper.Point(posX, posY));

        charPaths.push(charPath);
      }
    }

    if (charPaths.length === 0) return null;

    let unitedText = charPaths[0];
    for (let i = 1; i < charPaths.length; i++) {
      const nextChar = charPaths[i];
      const temp = unitedText.unite(nextChar);
      unitedText.remove();
      nextChar.remove();
      unitedText = temp;
    }

    return unitedText;
  }
};


/* ==========================================================================
   2D MODULE: laser-studio-geo.js
   ========================================================================== */

/**
 * Laser Ornament Studio - Master 2D Vector CAD Geometry Engine
 * Module: assets/laser-studio-geo.js
 * Responsibility: Master orchestrator synthesizing base templates, support bars, font geometry, middle text welding, and arc scoring.
 */

window.LaserStudio = window.LaserStudio || {};

window.LaserGeometry = {
  // Default CAD Golden Ratios for templates
  DEFAULT_CONFIG: {
    targetWidthMultiplier: 2.0,
    fontSizeRatio: 28.3544,
    textCenterYOffsetRatio: 3.069432,
    slotHeightRatio: 17.728028,
    ringCenterRadiusRatio: 47.905,
    arcCenterYOffsetRatio: 4.0424,
    outerRadiusForFlatRatio: 54.863
  },

  // Method Aliases from Sub-Modules for Backwards Compatibility
  applyStyle: function (pathItem, style, name) {
    return window.LaserStudio.GeoMath.applyStyle(pathItem, style, name);
  },

  getUnitedTextPath: function (targetFont, text, fontSize, letterSpacing, centerPoint, fitHeightOptions, touchingLetters) {
    return window.LaserStudio.GeoText.getUnitedTextPath(targetFont, text, fontSize, letterSpacing, centerPoint, fitHeightOptions, touchingLetters);
  },

  positionTouchingLetters: function (charPaths, fontSize, letterSpacing) {
    return window.LaserStudio.GeoContact.positionTouchingLetters(charPaths, fontSize, letterSpacing);
  },

  extractInternalCurves: function (targetPath, clipShape) {
    return window.LaserStudio.GeoContact.extractInternalCurves(targetPath, clipShape);
  },

  getLetterOverlapScoreSegments: function (letterPaths) {
    return window.LaserStudio.GeoContact.getLetterOverlapScoreSegments(letterPaths);
  },

  getCurvedTextPath: function (targetFont, text, fontSize, r, centerPoint, isTop) {
    return window.LaserStudio.GeoScores.getCurvedTextPath(targetFont, text, fontSize, r, centerPoint, isTop);
  },

  getHorizontalScoreSegments: function (textShape, yPos) {
    return window.LaserStudio.GeoContact.getHorizontalScoreSegments(textShape, yPos);
  },

  getCircularScoreSegments: function (textShape, centerPoint, radius) {
    return window.LaserStudio.GeoContact.getCircularScoreSegments(textShape, centerPoint, radius);
  },

  /**
   * Main CAD pipeline orchestrator. Reads state, generates Paper.js vector geometry,
   * performs Boolean unions & score extraction, and dispatches 3D updates.
   * @param {Object} [customConfig] - Optional override parameters
   */
  generateDesign: function (customConfig) {
    const state = window.LaserState;
    if (!state || !paper || !paper.project) return;

    const math = window.LaserStudio.GeoMath;
    const contact = window.LaserStudio.GeoContact;
    const textMod = window.LaserStudio.GeoText;
    const scores = window.LaserStudio.GeoScores;
    const STYLES = math.STYLES;

    // 1. Resolve configuration
    const config = customConfig || (window.LaserEvents && window.LaserEvents.getDesignConfig ? window.LaserEvents.getDesignConfig() : {});
    const fontFam = config.fontFamily || config.fontSelect || 'archivo';
    const rawWeight = parseInt(config.fontWeight, 10) || 600;

    const activeFont = textMod.resolveActiveFont(state, fontFam, rawWeight);
    if (!activeFont) {
      console.warn('[LaserStudio.Geometry] Fonts are not loaded yet.');
      return;
    }

    // Clear active Paper.js canvas layer
    paper.project.activeLayer.removeChildren();

    const mainTextVal = config.mainText || '';
    const textActionVal = config.textAction || 'cut';
    const topTextVal = config.topText || '';
    const bottomTextVal = config.bottomText || '';
    const circleModeVal = config.circleMode || 'ring';
    const circleRadiusVal = Number.isFinite(config.circleRadius) ? config.circleRadius : 144;
    const ringThicknessVal = Number.isFinite(config.ringThickness) ? config.ringThickness : (circleRadiusVal * 0.175);
    const useLoopVal = config.useLoop !== undefined ? config.useLoop : true;
    const loopOuterRadVal = Number.isFinite(config.loopOuterRad) ? config.loopOuterRad : (circleRadiusVal * (13.5 / 144));
    const loopInnerRadVal = Number.isFinite(config.loopInnerRad) ? config.loopInnerRad : (circleRadiusVal * (4.5 / 144));
    const fontSizeVal = Number.isFinite(config.fontSize) ? config.fontSize : 120;
    const textYOffsetVal = Number.isFinite(config.textYOffset) ? config.textYOffset : 0;
    const letterSpacingVal = Number.isFinite(config.letterSpacing) ? config.letterSpacing : 0;
    const useSupportBarVal = config.useSupportBar !== undefined ? config.useSupportBar : false;
    const barHeightVal = Number.isFinite(config.barHeight) ? config.barHeight : 12;
    const barYOffsetVal = Number.isFinite(config.barYOffset) ? config.barYOffset : 0;
    const outerFontSizeVal = Number.isFinite(config.outerFontSize) ? config.outerFontSize : 20;
    const outerOffsetVal = Number.isFinite(config.outerOffset) ? config.outerOffset : 0;
    const curveOuterTextVal = config.curveOuterText !== undefined ? config.curveOuterText : true;

    // 2. Create base template or standard circle/ring geometry
    const selectedTemplate = state.selectedTemplate || 'none';
    const templateObj = (selectedTemplate !== 'none' && window.LaserTemplates) ? window.LaserTemplates[selectedTemplate] : null;
    const templateConfig = templateObj ? (templateObj.config || window.LaserGeometry.DEFAULT_CONFIG) : null;
    let baseCutPath = null;
    let templateGroup = null;
    let ornamentScaleFactor = 1;

    if (templateObj && templateObj.rawSvg) {
      templateGroup = paper.project.importSVG(templateObj.rawSvg, { expandShapes: true });

      if (templateGroup) {
        templateGroup.position = state.DESIGN_CENTER;

        const targetWidth = circleRadiusVal * 2.0; // 4.00 inches (288 pt)
        const targetHeight = targetWidth * 1.0625; // 4.25 inches (306 pt)
        const scaleX = targetWidth / templateGroup.bounds.width;
        const scaleY = targetHeight / templateGroup.bounds.height;
        ornamentScaleFactor = scaleX;
        templateGroup.scale(scaleX, scaleY, state.DESIGN_CENTER);

        baseCutPath = new paper.CompoundPath();
        baseCutPath.fillColor = 'black';

        const pathsToWeld = [];
        function collectPaths(item) {
          if (item instanceof paper.Path || item instanceof paper.CompoundPath) {
            pathsToWeld.push(item);
          } else if (item instanceof paper.Group) {
            item.children.forEach(collectPaths);
          }
        }
        templateGroup.children.forEach(collectPaths);

        pathsToWeld.forEach((p) => {
          const cloned = p.clone();
          cloned.fillColor = 'black';
          cloned.strokeColor = null;
          baseCutPath.addChild(cloned);
        });

        templateGroup.remove();
        templateGroup = null;
      }
    } else {
      // Standard circular ring or solid disk
      if (circleModeVal === 'ring') {
        const outer = new paper.Path.Circle(state.DESIGN_CENTER, circleRadiusVal);
        const inner = new paper.Path.Circle(state.DESIGN_CENTER, Math.max(1, circleRadiusVal - ringThicknessVal));
        outer.fillColor = 'black';
        inner.fillColor = 'black';
        baseCutPath = outer.subtract(inner);
        outer.remove();
        inner.remove();

        // Top hanging loop (tangent to circle apex)
        if (useLoopVal) {
          const loopCenter = new paper.Point(
            state.DESIGN_CENTER.x,
            state.DESIGN_CENTER.y - circleRadiusVal - loopInnerRadVal
          );
          const outerLoop = new paper.Path.Circle(loopCenter, loopOuterRadVal);
          const innerLoop = new paper.Path.Circle(loopCenter, loopInnerRadVal);
          outerLoop.fillColor = 'black';
          innerLoop.fillColor = 'black';

          const temp = baseCutPath.unite(outerLoop).subtract(innerLoop);
          baseCutPath.remove();
          outerLoop.remove();
          innerLoop.remove();
          baseCutPath = temp;
        }
      } else {
        baseCutPath = new paper.Path.Circle(state.DESIGN_CENTER, circleRadiusVal);
        baseCutPath.fillColor = 'black';
      }
    }

    // 3. Resolve Support Line Bounds
    let supportLines = null;
    if (templateConfig) {
      const slotHeight = templateConfig.slotHeightRatio * ornamentScaleFactor;
      const slotCenterY = state.DESIGN_CENTER.y + templateConfig.textCenterYOffsetRatio * ornamentScaleFactor;
      supportLines = {
        topY: slotCenterY - slotHeight / 2,
        bottomY: slotCenterY + slotHeight / 2
      };
    } else if (useSupportBarVal && circleModeVal === 'ring') {
      const scaleFactor = circleRadiusVal / 144;
      const defaultSlotHeight = 54.0 * scaleFactor;
      const slotHeight = Math.max(10, defaultSlotHeight + barYOffsetVal * 2);
      const effectiveBarHeight = 11;
      const topBarCenterY = state.DESIGN_CENTER.y + textYOffsetVal - (slotHeight / 2 + effectiveBarHeight / 2);
      const bottomBarCenterY = state.DESIGN_CENTER.y + textYOffsetVal + (slotHeight / 2 + effectiveBarHeight / 2);

      supportLines = {
        topY: topBarCenterY + effectiveBarHeight / 2,
        bottomY: bottomBarCenterY - effectiveBarHeight / 2,
        barTopCenterY: topBarCenterY,
        barBottomCenterY: bottomBarCenterY,
        slotHeight: slotHeight,
        barHeight: effectiveBarHeight
      };
    }

    // 4. Create primary center text
    let textShape = null;
    if (mainTextVal) {
      let effectiveFontSize = fontSizeVal;
      let effectiveTextCenter = new paper.Point(state.DESIGN_CENTER.x, state.DESIGN_CENTER.y + textYOffsetVal);
      let fitHeightOptions = null;

      if (templateConfig) {
        effectiveFontSize = templateConfig.fontSizeRatio * ornamentScaleFactor;
        effectiveTextCenter = new paper.Point(
          state.DESIGN_CENTER.x,
          state.DESIGN_CENTER.y + templateConfig.textCenterYOffsetRatio * ornamentScaleFactor
        );
      } else if (useSupportBarVal && circleModeVal === 'ring') {
        const scaleFactor = circleRadiusVal / 144;
        effectiveFontSize = 86.4 * scaleFactor;
      }

      if (supportLines) {
        const targetGap = Math.abs(supportLines.bottomY - supportLines.topY);
        fitHeightOptions = {
          topY: supportLines.topY,
          bottomY: supportLines.bottomY,
          weldMargin: Math.max(2.5, targetGap * 0.05)
        };
      }

      const isNoSupportLine = !supportLines;
      textShape = textMod.getUnitedTextPath(
        activeFont,
        mainTextVal,
        effectiveFontSize,
        letterSpacingVal,
        effectiveTextCenter,
        fitHeightOptions,
        isNoSupportLine
      );

      // Auto-weld span calculation for Standard Ornament without support lines
      if (selectedTemplate === 'none' && !useSupportBarVal) {
        let targetBoundaryRadius = circleRadiusVal;
        if (circleModeVal === 'ring') {
          targetBoundaryRadius = Math.max(1, circleRadiusVal - ringThicknessVal);
        }
        const chordWidth = math.calculateChordWidth(targetBoundaryRadius, textYOffsetVal);
        const targetWidth = chordWidth + 3.0; // 3px weld overlap

        if (textShape && textShape.bounds.width > 0 && targetWidth > 0) {
          const scaleFactor = targetWidth / textShape.bounds.width;
          textShape.scale(scaleFactor, textShape.position);
          textShape.position = effectiveTextCenter;

          if (textShape.individualPaths && textShape.individualPaths.length > 0) {
            for (let p of textShape.individualPaths) {
              p.scale(scaleFactor, effectiveTextCenter);
            }
          }
        }
      } else if (selectedTemplate === 'none' && useSupportBarVal && textShape) {
        let targetBoundaryRadius = circleRadiusVal;
        if (circleModeVal === 'ring') {
          targetBoundaryRadius = Math.max(1, circleRadiusVal - ringThicknessVal);
        }
        const maxDY = Math.max(Math.abs(supportLines.topY - state.DESIGN_CENTER.y), Math.abs(supportLines.bottomY - state.DESIGN_CENTER.y));
        const safeMaxWidth = math.calculateMaxContainedWidth(targetBoundaryRadius, maxDY, 16);

        if (textShape.bounds.width > safeMaxWidth && safeMaxWidth > 0) {
          const scaleFactorX = safeMaxWidth / textShape.bounds.width;
          textShape.scale(scaleFactorX, 1, effectiveTextCenter);

          if (textShape.individualPaths && textShape.individualPaths.length > 0) {
            for (let p of textShape.individualPaths) {
              p.scale(scaleFactorX, 1, effectiveTextCenter);
            }
          }
        }

        const shiftX = effectiveTextCenter.x - textShape.position.x;
        textShape.position.x = effectiveTextCenter.x;
        if (textShape.individualPaths && textShape.individualPaths.length > 0 && Math.abs(shiftX) > 0.01) {
          for (let p of textShape.individualPaths) p.position.x += shiftX;
        }
      } else if (selectedTemplate !== 'none' && textShape && templateConfig) {
        const innerRadius = (templateConfig.ringCenterRadiusRatio || 47.905) * ornamentScaleFactor;
        const dY = Math.abs(templateConfig.textCenterYOffsetRatio * ornamentScaleFactor);
        const safeMaxWidth = math.calculateMaxContainedWidth(innerRadius, dY, 16);

        if (textShape.bounds.width > safeMaxWidth && safeMaxWidth > 0) {
          const scaleFactorX = safeMaxWidth / textShape.bounds.width;
          textShape.scale(scaleFactorX, 1, effectiveTextCenter);

          if (textShape.individualPaths && textShape.individualPaths.length > 0) {
            for (let p of textShape.individualPaths) {
              p.scale(scaleFactorX, 1, effectiveTextCenter);
            }
          }
        }

        const shiftX = effectiveTextCenter.x - textShape.position.x;
        textShape.position.x = effectiveTextCenter.x;
        if (textShape.individualPaths && textShape.individualPaths.length > 0 && Math.abs(shiftX) > 0.01) {
          for (let p of textShape.individualPaths) p.position.x += shiftX;
        }
      }
    }

    // 5. Create Support Bars if enabled
    let supportBarPath = null;
    if (useSupportBarVal && circleModeVal === 'ring' && !templateObj) {
      const barWidth = circleRadiusVal * 2;
      const topY = supportLines ? supportLines.barTopCenterY : (state.DESIGN_CENTER.y + textYOffsetVal - 33);
      const bottomY = supportLines ? supportLines.barBottomCenterY : (state.DESIGN_CENTER.y + textYOffsetVal + 33);
      const barH = (supportLines && supportLines.barHeight) ? supportLines.barHeight : barHeightVal;

      const pathTop = new paper.Path.Rectangle(
        new paper.Rectangle(state.DESIGN_CENTER.x - barWidth / 2, topY - barH / 2, barWidth, barH)
      );
      const pathBottom = new paper.Path.Rectangle(
        new paper.Rectangle(state.DESIGN_CENTER.x - barWidth / 2, bottomY - barH / 2, barWidth, barH)
      );
      pathTop.fillColor = 'black';
      pathBottom.fillColor = 'black';

      const unitedBars = pathTop.unite(pathBottom);
      pathTop.remove();
      pathBottom.remove();

      const outerCircle = new paper.Path.Circle(state.DESIGN_CENTER, circleRadiusVal);
      outerCircle.fillColor = 'black';

      supportBarPath = unitedBars.intersect(outerCircle);
      unitedBars.remove();
      outerCircle.remove();
    }

    // 6. Boolean Union (Weld) & Score Seam Generation
    let finalCutShape = baseCutPath;

    if (supportBarPath && finalCutShape) {
      const united = finalCutShape.unite(supportBarPath);
      finalCutShape.remove();
      supportBarPath.remove();
      finalCutShape = united;
    }

    if (textShape) {
      const letterPaths = textShape.individualPaths || [];

      if (textActionVal === 'weld_score_touching') {
        const scoreSegments = [];

        if (supportLines && Number.isFinite(supportLines.topY) && Number.isFinite(supportLines.bottomY)) {
          const topScore = contact.getHorizontalScoreSegments(textShape, supportLines.topY);
          const bottomScore = contact.getHorizontalScoreSegments(textShape, supportLines.bottomY);

          if (topScore) scoreSegments.push(...(topScore.children ? topScore.children : [topScore]));
          if (bottomScore) scoreSegments.push(...(bottomScore.children ? bottomScore.children : [bottomScore]));

          if (letterPaths.length > 1) {
            const letterScore = contact.getLetterOverlapScoreSegments(letterPaths);
            if (letterScore) scoreSegments.push(...(letterScore.children ? letterScore.children : [letterScore]));
          }
        } else if (selectedTemplate === 'none') {
          const boundaryRadius = circleModeVal === 'ring' ? Math.max(1, circleRadiusVal - ringThicknessVal) : circleRadiusVal;
          const innerRingCircle = new paper.Path.Circle(state.DESIGN_CENTER, boundaryRadius);
          const ringArcScore = contact.extractInternalCurves(innerRingCircle, textShape);
          if (ringArcScore && ringArcScore.length > 0) scoreSegments.push(...ringArcScore);
          innerRingCircle.remove();

          if (letterPaths.length > 1) {
            const letterScore = contact.getLetterOverlapScoreSegments(letterPaths);
            if (letterScore) scoreSegments.push(...(letterScore.children ? letterScore.children : [letterScore]));
          }
        } else {
          const targetBoundary = finalCutShape || supportBarPath;
          if (targetBoundary) {
            const fallbackSeams = contact.extractInternalCurves(textShape, targetBoundary);
            if (fallbackSeams && fallbackSeams.length > 0) scoreSegments.push(...fallbackSeams);
          }
        }

        if (scoreSegments.length > 0) {
          const touchingScore = new paper.CompoundPath({ children: scoreSegments });
          math.applyStyle(touchingScore, STYLES.SCORE, 'score-touching-edge');
        }
      } else if (textActionVal === 'weld_score_full') {
        if (letterPaths.length > 0) {
          const clonedLetterPaths = letterPaths.map((p) => {
            const c = p.clone();
            c.fillColor = null;
            return c;
          });
          const scoreOutline = new paper.CompoundPath({ children: clonedLetterPaths });
          math.applyStyle(scoreOutline, STYLES.SCORE, 'score-main-text');
        } else {
          const scoreOutline = textShape.clone();
          math.applyStyle(scoreOutline, STYLES.SCORE, 'score-main-text');
        }
      }

      if (letterPaths.length > 0) {
        letterPaths.forEach((p) => p.remove());
        textShape.individualPaths = null;
      }

      if (finalCutShape) {
        const united = finalCutShape.unite(textShape);
        finalCutShape.remove();
        textShape.remove();
        finalCutShape = united;
      } else if (supportBarPath) {
        const united = textShape.unite(supportBarPath);
        textShape.remove();
        supportBarPath.remove();
        math.applyStyle(united, STYLES.CUT, 'cut-welded-text');
      } else {
        math.applyStyle(textShape, STYLES.CUT, 'cut-main-text');
      }
    }

    if (finalCutShape) {
      math.applyStyle(finalCutShape, STYLES.CUT, 'cut-shape');
    }

    // 7. Generate Outer Curved Scoring Text (Standard Ring only)
    if (selectedTemplate === 'none') {
      const ringCenterRadius = circleRadiusVal - ringThicknessVal / 2;
      const textHeightOffset = outerFontSizeVal * 0.35;
      const outerTextFont =
        (state.fonts && state.fonts['montserrat'] && (state.fonts['montserrat'][600] || state.fonts['montserrat'][700] || state.fonts['montserrat'][500] || state.fonts['montserrat'][400])) ||
        activeFont;

      if (topTextVal && outerTextFont) {
        let topOuterText = null;
        if (curveOuterTextVal) {
          const topRadius = ringCenterRadius - textHeightOffset + outerOffsetVal;
          topOuterText = scores.getCurvedTextPath(outerTextFont, topTextVal, outerFontSizeVal, topRadius, state.DESIGN_CENTER, true);
        } else {
          const topCenter = new paper.Point(state.DESIGN_CENTER.x, state.DESIGN_CENTER.y - circleRadiusVal - outerOffsetVal);
          topOuterText = textMod.getUnitedTextPath(outerTextFont, topTextVal, outerFontSizeVal, 0.5, topCenter);
        }

        if (topOuterText) {
          math.applyStyle(topOuterText, STYLES.SCORE, 'score-top');
        }
      }

      if (bottomTextVal && outerTextFont) {
        let bottomOuterText = null;
        if (curveOuterTextVal) {
          const bottomRadius = ringCenterRadius + textHeightOffset + outerOffsetVal;
          bottomOuterText = scores.getCurvedTextPath(outerTextFont, bottomTextVal, outerFontSizeVal, bottomRadius, state.DESIGN_CENTER, false);
        } else {
          const bottomCenter = new paper.Point(state.DESIGN_CENTER.x, state.DESIGN_CENTER.y + circleRadiusVal + outerOffsetVal);
          bottomOuterText = textMod.getUnitedTextPath(outerTextFont, bottomTextVal, outerFontSizeVal, 0.5, bottomCenter);
        }

        if (bottomOuterText) {
          math.applyStyle(bottomOuterText, STYLES.SCORE, 'score-bottom');
        }
      }
    }

    // Draw active project to Paper.js view
    paper.view.draw();

    // 8. Live update 3D Wood Viewport if in 3D Mode
    if (window.currentViewMode === '3d' && window.wood3DViewer && window.LaserExporter) {
      clearTimeout(window._wood3DLiveUpdateTimer);
      window._wood3DLiveUpdateTimer = setTimeout(() => {
        const svgString = window.LaserExporter.generateSVGString();
        window.wood3DViewer.loadSVG(svgString);
      }, 20);
    }
  }
};

// Aliases for global namespace compatibility
window.LaserStudio.Geometry = window.LaserGeometry;


/* ==========================================================================
   2D MODULE: laser-studio-exporter.js
   ========================================================================== */

/**
 * Laser Ornament Studio - SVG Laser Cut & Score Exporter
 * Module: assets/laser-studio-exporter.js
 * Responsibility: Generates clean, production-ready vector SVG files formatted with physical dimensions, hair lines, and triggers instant download.
 */

window.LaserStudio = window.LaserStudio || {};

window.LaserExporter = {
  /**
   * Generates clean XML SVG string from active Paper.js project with physical units in inches (72pt/in)
   * @returns {String} XML formatted SVG document
   */
  generateSVGString: function () {
    if (typeof paper === 'undefined' || !paper.project || !paper.project.activeLayer) {
      return '';
    }

    const strokeSelect = document.getElementById('strokeWidth');
    const exportWeightSelect = strokeSelect ? strokeSelect.value : '1.0';
    let exportStrokeWidth = parseFloat(exportWeightSelect) || 1.0;
    if (exportWeightSelect === '0.001') {
      exportStrokeWidth = 0.072; // ~0.001 inches in points
    }

    // Save original styles and apply export stroke width & clear fills
    const originalStyles = [];
    paper.project.activeLayer.children.forEach((item) => {
      if (!item.name || item.name.startsWith('cut-') || item.name.startsWith('score-') || item.name.startsWith('template-')) {
        originalStyles.push({
          item: item,
          strokeWidth: item.strokeWidth,
          fillColor: item.fillColor
        });
        item.strokeWidth = exportStrokeWidth;
        item.fillColor = null;
      } else {
        originalStyles.push({
          item: item,
          visible: item.visible
        });
        item.visible = false;
      }
    });

    const svgString = paper.project.exportSVG({
      asString: true,
      bounds: 'content'
    });

    // Restore original styles immediately
    originalStyles.forEach((style) => {
      if (style.strokeWidth !== undefined) {
        style.item.strokeWidth = style.strokeWidth;
        style.item.fillColor = style.fillColor;
      }
      if (style.visible !== undefined) {
        style.item.visible = style.visible;
      }
    });

    // Inject physical dimension units (inches) into SVG root
    let processedSvg = svgString;
    const widthMatch = svgString.match(/width="([\d\.]+)"/);
    const heightMatch = svgString.match(/height="([\d\.]+)"/);
    if (widthMatch && heightMatch) {
      const widthPx = parseFloat(widthMatch[1]);
      const heightPx = parseFloat(heightMatch[1]);
      const widthIn = (widthPx / 72).toFixed(4);
      const heightIn = (heightPx / 72).toFixed(4);
      processedSvg = processedSvg
        .replace(/width="[\d\.]+"/, `width="${widthIn}in"`)
        .replace(/height="[\d\.]+"/, `height="${heightIn}in"`);
    }

    return '<?xml version="1.0" encoding="utf-8"?>\n' + processedSvg;
  },

  /**
   * Triggers client-side browser file download of the customized ornament SVG
   */
  exportSVGFile: function () {
    const svgString = window.LaserExporter.generateSVGString();
    if (!svgString) return;

    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const mainTextInput = document.getElementById('mainText');
    const mainTextVal = mainTextInput ? mainTextInput.value : 'LaserOrnament';
    const filename = `${mainTextVal.trim().toLowerCase().replace(/\s+/g, '_')}_laser_ornament.svg`;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    window.LaserExporter.showStatus('SVG file exported successfully!');
  },

  /**
   * Compiles complete customer personalization choices into Shopify Line Item Properties
   */
  compileLineItemProperties: function () {
    const state = window.LaserState || {};
    const mainText = (document.getElementById('mainText')?.value || '').trim();
    const topText = (document.getElementById('topText')?.value || '').trim();
    const bottomText = (document.getElementById('bottomText')?.value || '').trim();
    const rawTemplateKey = state.selectedTemplate || 'elf';
    const templateCleanKey = rawTemplateKey.replace(/_(ornament|template)$/i, '');
    const templateName = templateCleanKey.charAt(0).toUpperCase() + templateCleanKey.slice(1);

    const activeWeightBtn = document.querySelector('#fontWeightSelector .weight-pill-btn.active');
    const activeWeight = activeWeightBtn ? activeWeightBtn.dataset.weight : (state.selectedFontWeight || 400);

    const woodSpeciesKey = state.selectedWoodSpecies || 'birch';
    const woodSpeciesName = woodSpeciesKey.charAt(0).toUpperCase() + woodSpeciesKey.slice(1);

    const thicknessMm = state.selectedThickness || 5.0;
    const thicknessMap = { 3.0: '1/8"', 4.0: '5/32"', 5.0: '3/16"', 6.0: '1/4"' };
    const thicknessLabel = thicknessMap[thicknessMm] || `${thicknessMm}"`;

    const isLayer2 = !!(state.layer2 && state.layer2.enabled);
    const layer2ColorName = isLayer2 ? (state.layer2.name || state.layer2.colorId || 'Acrylic Backplate') : 'None';

    // Generate unique Design ID for client-side localStorage indexing
    const designId = 'fc_orn_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 7);

    const fontSelectEl = document.getElementById('fontSelect');
    const fontDisplayName = fontSelectEl && fontSelectEl.options[fontSelectEl.selectedIndex]
      ? fontSelectEl.options[fontSelectEl.selectedIndex].text.trim()
      : (state.selectedFontFamily ? state.selectedFontFamily.charAt(0).toUpperCase() + state.selectedFontFamily.slice(1) : 'Bentillo');

    const singleWeightFamilies = ['pacifico', 'bentillo', 'captain-heroes', 'heloderma', 'retro-world'];
    const currentFontKey = (state.selectedFontFamily || (fontSelectEl ? fontSelectEl.value : 'bentillo')).toLowerCase();
    const isSingleWeight = singleWeightFamilies.includes(currentFontKey);
    const fontLabel = isSingleWeight ? fontDisplayName : `${fontDisplayName} (${activeWeight})`;

    const properties = {
      'Ornament Template': templateName,
      'Center Text': mainText || 'Center Text',
      'Font': fontLabel,
      'Wood Material': woodSpeciesName,
      'Thickness': thicknessLabel,
      'Layer 2 Infill': isLayer2 ? `Yes - ${layer2ColorName}` : 'No',
      '_design_id': designId
    };

    if (topText) properties['Top Curved Text'] = topText;
    if (bottomText) properties['Bottom Curved Text'] = bottomText;

    // Attach high-resolution 3D preview snapshot and store in localStorage
    if (window.wood3DViewer && typeof window.wood3DViewer.captureSnapshot === 'function') {
      try {
        const snapshotUrl = window.wood3DViewer.captureSnapshot(400, 400, 'image/webp', 0.9);
        if (snapshotUrl) {
          // 1. Save to Client LocalStorage
          if (window.LaserStorageManager && typeof window.LaserStorageManager.saveSnapshot === 'function') {
            window.LaserStorageManager.saveSnapshot(designId, snapshotUrl, {
              template: templateName,
              text: mainText,
              material: woodSpeciesName,
              timestamp: Date.now()
            });
          }

          // 2. Also pass into line item property if under payload limit as fallback
          if (snapshotUrl.length < 65000) {
            properties['_preview_image'] = snapshotUrl;
          }
        }
      } catch (err) {
        console.warn('[LaserStudio] Snapshot capture skipped:', err);
      }
    }

    return properties;
  },

  /**
   * Adds the customized ornament to the Shopify Cart via Ajax API (/cart/add.js)
   */
  addToCart: async function () {
    const config = window.LaserStudioProductConfig || {};
    const state = window.LaserState || {};
    const ctaBtn = document.getElementById('downloadSVG');
    const ctaBtnText = document.getElementById('ctaBtnText');
    const originalText = ctaBtnText ? ctaBtnText.innerText : 'Add to Cart';

    // 1. Determine target Variant ID (Single Layer vs Dual Layer)
    let targetVariantId = config.currentProduct?.variantId;
    const variants = config.currentProduct?.variants || [];
    const isLayer2 = !!(state.layer2 && state.layer2.enabled);

    if (variants.length > 1) {
      let matchedVariant = null;
      if (isLayer2) {
        matchedVariant = variants.find((v) => v.title.toLowerCase().includes('dual') || v.title.toLowerCase().includes('color') || v.title.toLowerCase().includes('infill') || v.price > variants[0].price) || variants[variants.length - 1];
      } else {
        matchedVariant = variants.find((v) => v.title.toLowerCase().includes('single') || v.price <= variants[0].price) || variants[0];
      }
      if (matchedVariant && matchedVariant.id) {
        targetVariantId = matchedVariant.id;
      }
    }

    const currentTemplate = state.selectedTemplate || 'elf';
    if (!targetVariantId && config.templateMap && config.templateMap[currentTemplate] && config.templateMap[currentTemplate].variantId) {
      targetVariantId = config.templateMap[currentTemplate].variantId;
    }

    // Fallback: If no variant ID is found (e.g. testing standalone), download the SVG
    if (!targetVariantId) {
      window.LaserExporter.exportSVGFile();
      return;
    }

    // 2. Set UI loading state
    if (ctaBtn) ctaBtn.disabled = true;
    if (ctaBtnText) ctaBtnText.innerText = 'Adding to Cart...';

    try {
      const properties = window.LaserExporter.compileLineItemProperties();
      const payload = {
        items: [
          {
            id: targetVariantId,
            quantity: 1,
            properties: properties
          }
        ]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Cart API error: ${response.statusText}`);
      }

      const cartItem = await response.json();
      console.log('[LaserStudio] Item successfully added to cart:', cartItem);

      // 3. Success feedback
      if (ctaBtnText) ctaBtnText.innerText = 'Added to Cart! ✓';
      window.LaserExporter.showStatus('Custom ornament added to your cart!');

      // 4. Trigger Cart Drawer or redirect to Cart
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');

      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else if (cartNotification && typeof cartNotification.open === 'function') {
        cartNotification.open();
      } else {
        setTimeout(() => {
          window.location.href = '/cart';
        }, 800);
      }
    } catch (error) {
      console.error('[LaserStudio] Failed to add to cart:', error);
      if (ctaBtnText) ctaBtnText.innerText = 'Error adding to cart';
      window.LaserExporter.showStatus('Unable to add to cart. Please try again.');
    } finally {
      setTimeout(() => {
        if (ctaBtn) ctaBtn.disabled = false;
        if (ctaBtnText) ctaBtnText.innerText = originalText;
      }, 3000);
    }
  },

  /**
   * Shows brief feedback message in status container
   */
  showStatus: function (message) {
    const status = document.getElementById('statusMsg');
    if (!status) return;
    status.innerText = message;
    if (status.parentElement) {
      status.parentElement.style.background = 'rgba(52, 199, 89, 0.2)';
    }

    setTimeout(() => {
      status.innerText = 'Vector paths computed';
      if (status.parentElement) {
        status.parentElement.style.background = 'rgba(52, 199, 89, 0.1)';
      }
    }, 4000);
  }
};

// Aliases for global namespace compatibility
window.LaserStudio.Exporter = window.LaserExporter;


/* ==========================================================================
   2D MODULE: laser-studio-events.js
   ========================================================================== */

/**
 * Laser Ornament Studio - Event Orchestrator & UI Binding Engine
 * Module: assets/laser-studio-events.js
 * Responsibility: Synchronizes UI inputs, mode tabs, wood species, infill swatches, 2D/3D view switches, and debounced CAD/WebGL rendering.
 */

window.LaserStudio = window.LaserStudio || {};

const DEFAULT_SIZE_INCHES = 4.0;
const DEFAULT_LOOP_OUTER_RAD = 13.5;
const DEFAULT_LOOP_INNER_RAD = 4.5;

window.LaserEvents = {
  /**
   * Sets up 2D Paper.js canvas pan and center-offset wheel zoom
   */
  setupPanAndZoom: function (canvas) {
    if (!canvas || typeof paper === 'undefined') return;

    const tool = new paper.Tool();

    tool.onMouseDrag = function (event) {
      if (window.currentViewMode === '2d') {
        paper.view.scrollBy(event.downPoint.subtract(event.point));
      }
    };

    canvas.addEventListener('wheel', function (event) {
      if (window.currentViewMode !== '2d') return;
      event.preventDefault();
      const oldZoom = paper.view.zoom;
      const factor = event.deltaY < 0 ? 1.15 : 0.85;

      const nextZoom = Math.min(Math.max(paper.view.zoom * factor, 0.2), 10);
      paper.view.zoom = nextZoom;

      const mousePosition = paper.view.viewToProject(new paper.Point(event.offsetX, event.offsetY));
      paper.view.center = paper.view.center.add(
        mousePosition.subtract(paper.view.center).multiply(1 - oldZoom / paper.view.zoom)
      );
    });
  },

  /**
   * Reads current form controls defensively and returns design configuration object
   */
  getDesignConfig: function () {
    const getVal = (id, fallback = '') => {
      const el = document.getElementById(id);
      return el ? el.value : fallback;
    };
    const getInt = (id, fallback = 0) => {
      const el = document.getElementById(id);
      const val = el ? parseInt(el.value, 10) : fallback;
      return Number.isFinite(val) ? val : fallback;
    };
    const getFloat = (id, fallback = 0) => {
      const el = document.getElementById(id);
      const val = el ? parseFloat(el.value) : fallback;
      return Number.isFinite(val) ? val : fallback;
    };
    const getChecked = (id, fallback = false) => {
      const el = document.getElementById(id);
      return el ? el.checked : fallback;
    };

    const activeDot = document.querySelector('#sizeDotsSelector .size-dot-btn.active');
    let sizeInches = DEFAULT_SIZE_INCHES;
    if (activeDot && activeDot.dataset.size) {
      const parsed = parseFloat(activeDot.dataset.size);
      if (Number.isFinite(parsed)) sizeInches = parsed;
    }

    const circleRadius = sizeInches * 36;
    const family = getVal('fontSelect', 'bentillo');
    const singleWeightFamilies = ['pacifico', 'bentillo', 'captain-heroes', 'heloderma', 'retro-world'];
    let fontWeight = 600;
    if (singleWeightFamilies.includes(family)) {
      fontWeight = 400;
    } else {
      const activeWeightBtn = document.querySelector('#fontWeightSelector .weight-pill-btn.active');
      fontWeight = activeWeightBtn ? parseInt(activeWeightBtn.dataset.weight, 10) : 600;
    }

    return {
      mainText: getVal('mainText', 'NAME'),
      textAction: getVal('textAction', 'cut'),
      topText: getVal('topText', 'EST. 2026'),
      bottomText: getVal('bottomText', 'LASER PROJECT'),
      fontFamily: family,
      fontSelect: family,
      fontWeight: fontWeight,
      circleMode: 'ring',
      sizeInches: sizeInches,
      circleRadius: circleRadius,
      ringThickness: circleRadius * 0.175,
      useLoop: true,
      loopOuterRad: DEFAULT_LOOP_OUTER_RAD,
      loopInnerRad: DEFAULT_LOOP_INNER_RAD,
      fontSize: getInt('fontSize', 120),
      textYOffset: getInt('textYOffset', 0),
      letterSpacing: getFloat('letterSpacing', 0),
      useSupportBar: getChecked('useSupportBar', false),
      barHeight: getInt('barHeight', 12),
      barYOffset: getInt('barYOffset', 0),
      outerFontSize: getInt('outerFontSize', 20),
      outerOffset: 0,
      curveOuterText: true
    };
  },

  /**
   * Updates numeric displays next to range sliders
   */
  updateDisplayValues: function (config) {
    if (!config) config = window.LaserEvents.getDesignConfig();

    const sizeDisplay = document.getElementById('sizeDisplayVal');
    if (sizeDisplay) {
      const sizeInches = config.sizeInches || 4.0;
      sizeDisplay.innerText = `${sizeInches.toFixed(1)}"`;
    }

    const displayMap = {
      fontSizeVal: config.fontSize,
      textYOffsetVal: config.textYOffset,
      letterSpacingVal: config.letterSpacing,
      barHeightVal: config.barHeight,
      barYOffsetVal: config.barYOffset,
      outerFontSizeVal: config.outerFontSize
    };

    for (const [id, val] of Object.entries(displayMap)) {
      const el = document.getElementById(id);
      if (el && val !== undefined) {
        el.innerText = val;
      }
    }
  },

  /**
   * Synchronizes font weight pill selector visibility based on chosen font family
   */
  updateFontWeightAvailability: function () {
    const family = document.getElementById('fontSelect') ? document.getElementById('fontSelect').value : 'bentillo';
    const weightWrapper = document.getElementById('fontWeightWrapper');
    const weightBtns = document.querySelectorAll('#fontWeightSelector .weight-pill-btn');

    const singleWeightFamilies = ['pacifico', 'bentillo', 'captain-heroes', 'heloderma', 'retro-world'];
    const isSingleWeight = singleWeightFamilies.includes(family);
    const wasSingleWeight = window.LaserState ? window.LaserState.lastWasSingleWeight : false;

    if (window.LaserState) {
      window.LaserState.lastWasSingleWeight = isSingleWeight;
    }

    if (weightWrapper) {
      weightWrapper.style.display = isSingleWeight ? 'none' : '';
    }

    if (isSingleWeight) {
      weightBtns.forEach((btn) => {
        const w = parseInt(btn.dataset.weight, 10);
        if (w !== 400) {
          btn.disabled = true;
          btn.classList.remove('active');
        } else {
          btn.disabled = false;
          btn.classList.add('active');
        }
      });
    } else {
      weightBtns.forEach((btn) => {
        btn.disabled = false;
      });

      const activeBtn = document.querySelector('#fontWeightSelector .weight-pill-btn.active');
      if (!activeBtn || wasSingleWeight) {
        weightBtns.forEach((b) => b.classList.remove('active'));
        const semiBtn = document.querySelector('#fontWeightSelector .weight-pill-btn[data-weight="600"]');
        if (semiBtn) semiBtn.classList.add('active');
      }
    }

    const fontSelectEl = document.getElementById('fontSelect');
    if (fontSelectEl) {
      const fontNameMap = {
        archivo: "'Archivo', sans-serif",
        montserrat: "'Montserrat', sans-serif",
        oswald: "'Oswald', sans-serif",
        pacifico: "'Pacifico', cursive",
        bentillo: "'Bentillo', serif",
        'captain-heroes': "'Captain Heroes', fantasy, sans-serif",
        heloderma: "'Heloderma', serif",
        'retro-world': "'Retro World', sans-serif"
      };
      fontSelectEl.style.fontFamily = fontNameMap[family] || 'inherit';
    }
  },

  /**
   * Constrains letter spacing slider max dynamically so text fits within template inner boundaries
   */
  updateLetterSpacingBounds: function () {
    const letterSpacingInput = document.getElementById('letterSpacing');
    if (!letterSpacingInput) return;

    const selectedTemplate = window.LaserState ? window.LaserState.selectedTemplate : 'elf';
    const templateObj = (selectedTemplate !== 'none' && window.LaserStudio && window.LaserStudio.TemplateRegistry)
      ? window.LaserStudio.TemplateRegistry.get(selectedTemplate)
      : (window.LaserTemplates ? window.LaserTemplates[selectedTemplate] : null);

    if (selectedTemplate !== 'none' && templateObj) {
      const templateConfig = templateObj.config;
      const mainText = (document.getElementById('mainText') ? document.getElementById('mainText').value : 'NAME') || '';

      const activeDot = document.querySelector('#sizeDotsSelector .size-dot-btn.active');
      const sizeInches = activeDot && activeDot.dataset.size ? parseFloat(activeDot.dataset.size) : 4.0;
      const circleRadiusVal = sizeInches * 36;
      const targetWidthMultiplier = templateConfig ? templateConfig.targetWidthMultiplier : 2.0;
      const targetWidth = circleRadiusVal * targetWidthMultiplier;
      const ornamentScaleFactor = targetWidth / 101.6;

      const innerRadius = (templateConfig && templateConfig.ringCenterRadiusRatio ? templateConfig.ringCenterRadiusRatio : 47.905) * ornamentScaleFactor;
      const dY = templateConfig ? Math.abs(templateConfig.textCenterYOffsetRatio * ornamentScaleFactor) : 0;
      const maxChordHalfWidth = dY < innerRadius ? Math.sqrt(innerRadius * innerRadius - dY * dY) : (innerRadius * 0.85);
      const innerChordWidth = maxChordHalfWidth * 2;
      const safeMaxWidth = Math.max(20, innerChordWidth - 16);

      const fontSelect = document.getElementById('fontSelect');
      const fontFamily = fontSelect ? fontSelect.value : 'archivo';
      const state = window.LaserState;
      let activeFont = null;
      if (state && state.fonts && state.fonts[fontFamily]) {
        activeFont = state.fonts[fontFamily][600] || state.fonts[fontFamily][400] || Object.values(state.fonts[fontFamily]).find((f) => f);
      }
      const fontSize = (templateConfig ? templateConfig.fontSizeRatio : 28.3544) * ornamentScaleFactor;

      let baseTextWidth = 0;
      if (activeFont && mainText) {
        for (let i = 0; i < mainText.length; i++) {
          const glyph = activeFont.charToGlyph(mainText[i]);
          if (glyph) {
            baseTextWidth += glyph.advanceWidth * (fontSize / activeFont.unitsPerEm);
          }
        }
      } else {
        baseTextWidth = mainText.length * fontSize * 0.55;
      }

      const numGaps = Math.max(1, mainText.length - 1);
      const remainingSpace = safeMaxWidth - baseTextWidth;
      const maxSpacing = Math.max(0, remainingSpace / numGaps);

      const computedMax = Math.max(0, Math.floor(maxSpacing * 2) / 2);
      letterSpacingInput.max = computedMax.toString();

      if (parseFloat(letterSpacingInput.value) > computedMax) {
        letterSpacingInput.value = computedMax.toString();
        const valDisplay = document.getElementById('letterSpacingVal');
        if (valDisplay) valDisplay.textContent = computedMax.toString();
      }
    } else {
      letterSpacingInput.max = '50';
    }
  },

  /**
   * Debounced render trigger for responsive updates during typing and slider interactions
   */
  triggerRender: function (immediate = false) {
    const executeRender = () => {
      window.LaserEvents.updateLetterSpacingBounds();
      const config = window.LaserEvents.getDesignConfig();
      window.LaserEvents.updateDisplayValues(config);
      if (window.LaserGeometry) {
        window.LaserGeometry.generateDesign(config);
      }
      if (window.currentViewMode === '3d' && window.wood3DViewer && window.LaserExporter) {
        const svgString = window.LaserExporter.generateSVGString();
        window.wood3DViewer.loadSVG(svgString);
      }
    };

    if (window._renderDebounceTimer) {
      clearTimeout(window._renderDebounceTimer);
      window._renderDebounceTimer = null;
    }

    if (immediate) {
      window._renderDebounceTimer = setTimeout(executeRender, 10);
      return;
    }

    window._renderDebounceTimer = setTimeout(executeRender, 20);
  },

  /**
   * Applies presets for chosen template or ring
   */
  applyTemplatePresets: function (templateId) {
    const template =
      (window.LaserStudio && window.LaserStudio.TemplateRegistry)
        ? window.LaserStudio.TemplateRegistry.get(templateId)
        : (window.LaserTemplates ? window.LaserTemplates[templateId] : null);

    if (template && template.presets) {
      const p = template.presets;
      const sizeDots = document.querySelectorAll('#sizeDotsSelector .size-dot-btn');
      if (p.sizeInches !== undefined) {
        const matchingDot = document.querySelector(`#sizeDotsSelector .size-dot-btn[data-size="${p.sizeInches}"]`);
        if (matchingDot) {
          sizeDots.forEach((b) => b.classList.remove('active'));
          matchingDot.classList.add('active');
        }
      }
      if (p.barHeight !== undefined) {
        const el = document.getElementById('barHeight');
        if (el) el.value = p.barHeight;
      }
      if (p.barYOffset !== undefined) {
        const el = document.getElementById('barYOffset');
        if (el) el.value = p.barYOffset;
      }
      if (p.fontSize !== undefined) {
        const el = document.getElementById('fontSize');
        if (el) el.value = p.fontSize;
      }
      if (p.useSupportBar !== undefined) {
        const el = document.getElementById('useSupportBar');
        if (el) el.checked = p.useSupportBar;
      }
      if (p.outerFontSize !== undefined) {
        const el = document.getElementById('outerFontSize');
        if (el) el.value = p.outerFontSize;
      }
    }
    window.LaserEvents.updateLetterSpacingBounds();
  },

  /**
   * Toggles visibility of template cards active state
   */
  updateTabModeUI: function () {
    const selectedTemplate = window.LaserState ? window.LaserState.selectedTemplate : 'elf';
    const cards = document.querySelectorAll('#templatePicker .template-card');
    cards.forEach((card) => {
      const tId = card.getAttribute('data-template');
      card.classList.toggle('active', tId === selectedTemplate);
    });
  },

  /**
   * Selects a template, applies its presets, updates UI, history URL, and triggers rendering
   */
  selectTemplate: function (tId, updateUrl = true) {
    if (!tId) return;
    const state = window.LaserState;
    if (state) {
      state.selectedTemplate = tId;
      state.lastSelectedTemplate = tId;
      window.LaserEvents.applyTemplatePresets(tId);
    }
    window.LaserEvents.updateTabModeUI();
    window.LaserEvents.triggerRender(true);

    // Dynamic History URL and Product Title Sync
    if (updateUrl && window.LaserStudioProductConfig) {
      const config = window.LaserStudioProductConfig;
      const targetProduct = config.templateMap ? config.templateMap[tId] : null;

      if (targetProduct && targetProduct.handle) {
        const newUrl = `/products/${targetProduct.handle}`;
        if (window.location.pathname !== newUrl && window.history && window.history.replaceState) {
          window.history.replaceState({ template: tId, handle: targetProduct.handle }, targetProduct.title || document.title, newUrl);
        }
        if (targetProduct.title) {
          document.title = `${targetProduct.title} – ${window.Shopify?.shop || 'FnC Workshop'}`;
        }
      }
    }
  },

  /**
   * Detects the active product handle / tags on load and auto-selects the matching template
   */
  detectInitialProductTemplate: function () {
    const config = window.LaserStudioProductConfig;
    const currentHandle = config?.currentProduct?.handle || window.location.pathname.split('/').pop();
    const currentTags = config?.currentProduct?.tags || [];

    let detectedTemplate = null;

    // 1. Match from templateMap handles
    if (config && config.templateMap) {
      for (const [key, item] of Object.entries(config.templateMap)) {
        if (item.handle && currentHandle && (currentHandle === item.handle || currentHandle.includes(item.handle))) {
          detectedTemplate = key;
          break;
        }
      }
    }

    // 2. Match from tags (e.g. "template:gingerbread" or "template:elf")
    if (!detectedTemplate && currentTags.length > 0) {
      for (const tag of currentTags) {
        const lowerTag = tag.toLowerCase().trim();
        if (lowerTag.startsWith('template:')) {
          const tName = lowerTag.replace('template:', '').trim();
          if (['elf', 'gingerbread', 'reindeer', 'ring', 'none'].includes(tName)) {
            detectedTemplate = tName;
            break;
          }
        }
      }
    }

    // 3. Fallback to keyword matching in handle/title
    if (!detectedTemplate && currentHandle) {
      const lower = currentHandle.toLowerCase();
      if (lower.includes('gingerbread')) detectedTemplate = 'gingerbread';
      else if (lower.includes('reindeer')) detectedTemplate = 'reindeer';
      else if (lower.includes('elf')) detectedTemplate = 'elf';
      else if (lower.includes('ring')) detectedTemplate = 'ring';
    }

    if (detectedTemplate) {
      const state = window.LaserState;
      if (state) {
        state.selectedTemplate = detectedTemplate;
        state.lastSelectedTemplate = detectedTemplate;
      }
      window.LaserEvents.applyTemplatePresets(detectedTemplate);
      window.LaserEvents.updateTabModeUI();
    }
  },

  /**
   * Updates price on CTA button dynamically when Layer 2 is enabled ($20) or disabled ($18)
   */
  updateDynamicPriceDisplay: function (isLayer2Enabled) {
    const config = window.LaserStudioProductConfig;
    if (!config || !config.isProduct || !config.currentProduct) return;
    const variants = (config.currentProduct.variants || []).slice();
    if (variants.length === 0) return;

    let targetVariant = null;
    if (isLayer2Enabled) {
      targetVariant = variants.find((v) => v.title.toLowerCase().includes('dual') || v.title.toLowerCase().includes('color') || v.title.toLowerCase().includes('infill')) || variants.sort((a, b) => b.price - a.price)[0];
    } else {
      targetVariant = variants.find((v) => v.title.toLowerCase().includes('single')) || variants.sort((a, b) => a.price - b.price)[0];
    }

    const priceEl = document.getElementById('ctaBtnPrice');
    if (priceEl && targetVariant && targetVariant.priceFormatted) {
      priceEl.textContent = `• ${targetVariant.priceFormatted}`;
    }
  },

  /**
   * Binds all DOM elements, buttons, inputs, swatches, and view toggles
   */
  bindControls: function () {
    // 1. Text Inputs
    const textInputIds = ['mainText', 'topText', 'bottomText'];
    textInputIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('input', () => window.LaserEvents.triggerRender(false));
      }
    });

    // 2. Text Action Select
    const textActionEl = document.getElementById('textAction');
    if (textActionEl) {
      textActionEl.addEventListener('change', () => window.LaserEvents.triggerRender(true));
    }

    // 3. Font Family Select
    const fontSelect = document.getElementById('fontSelect');
    if (fontSelect) {
      fontSelect.addEventListener('change', () => {
        window.LaserEvents.updateFontWeightAvailability();
        window.LaserEvents.triggerRender(true);
      });
    }

    // 4. Font Weight Pills
    const weightBtns = document.querySelectorAll('#fontWeightSelector .weight-pill-btn');
    weightBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        weightBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        window.LaserEvents.triggerRender(true);
      });
    });

    // 5. Sliders & Checkboxes
    const sliderIds = ['textYOffset', 'letterSpacing', 'barHeight', 'barYOffset', 'outerFontSize'];
    sliderIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('input', () => {
          const valDisplay = document.getElementById(`${id}Val`);
          if (valDisplay) valDisplay.innerText = el.value;
          window.LaserEvents.triggerRender(false);
        });
      }
    });

    const useSupportBar = document.getElementById('useSupportBar');
    if (useSupportBar) {
      useSupportBar.addEventListener('change', () => window.LaserEvents.triggerRender(true));
    }

    // 6. Template Picker Cards
    const templateCards = document.querySelectorAll('#templatePicker .template-card');
    templateCards.forEach((card) => {
      card.addEventListener('click', () => {
        const tId = card.getAttribute('data-template');
        if (!tId) return;
        window.LaserEvents.selectTemplate(tId, true);
      });
    });

    // 8. Size Dots (Standard 4.0" is selected)
    const sizeDots = document.querySelectorAll('#sizeDotsSelector .size-dot-btn');
    sizeDots.forEach((dot) => {
      dot.addEventListener('click', () => {
        if (dot.disabled) return;
        sizeDots.forEach((d) => d.classList.remove('active'));
        dot.classList.add('active');
        window.LaserEvents.triggerRender(true);
      });
    });

    // 9. Synchronize Wood Thickness Pills (Sidebar + 3D Viewport Dock)
    function syncThickness(thicknessVal) {
      const state = window.LaserState;
      if (state) state.selectedThickness = parseFloat(thicknessVal);

      const allThicknessBtns = document.querySelectorAll('.thickness-pill-btn');
      allThicknessBtns.forEach((btn) => {
        btn.classList.toggle('active', btn.dataset.thickness === thicknessVal.toString());
      });

      if (window.wood3DViewer) {
        window.wood3DViewer.updateThickness(thicknessVal);
      }
    }

    const thicknessBtns = document.querySelectorAll('.thickness-pill-btn');
    thicknessBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const val = btn.dataset.thickness;
        if (val) syncThickness(val);
      });
    });

    // 10. Layer 2 Infill Toggle & Color Swatches (Sidebar + 3D Viewport Dock)
    function syncLayer2Toggle(enable) {
      const state = window.LaserState;
      if (state && state.layer2) state.layer2.enabled = !!enable;

      const toggleSidebar = document.getElementById('btnToggleLayer2Sidebar');
      const togglePreview = document.getElementById('btnToggleLayer2Preview');

      [toggleSidebar, togglePreview].forEach((toggle) => {
        if (toggle) {
          toggle.classList.toggle('active', !!enable);
          toggle.classList.toggle('inactive', !enable);
          toggle.setAttribute('aria-checked', enable ? 'true' : 'false');
        }
      });

      // Synchronize swatch active rings (remove when disabled, show active color when enabled)
      const allSwatches = document.querySelectorAll('.filler-swatch');
      allSwatches.forEach((swatch) => {
        if (!enable) {
          swatch.classList.remove('active');
        } else {
          swatch.classList.toggle('active', swatch.dataset.filler === (state?.layer2?.colorId || 'green'));
        }
      });

      if (window.wood3DViewer) {
        window.wood3DViewer.updateLayer2({ enabled: !!enable });
      }

      window.LaserEvents.updateDynamicPriceDisplay(enable);
    }

    function syncLayer2Color(colorId, hex, name) {
      const state = window.LaserState;
      if (state && state.layer2) {
        state.layer2.colorId = colorId;
        state.layer2.color = hex;
        state.layer2.name = name;
      }

      const allSwatches = document.querySelectorAll('.filler-swatch');
      allSwatches.forEach((swatch) => {
        swatch.classList.toggle('active', swatch.dataset.filler === colorId);
      });

      syncLayer2Toggle(true);

      if (window.wood3DViewer) {
        window.wood3DViewer.updateLayer2({ colorId, color: hex, name, enabled: true });
      }
    }

    const toggleSidebar = document.getElementById('btnToggleLayer2Sidebar');
    const togglePreview = document.getElementById('btnToggleLayer2Preview');

    [toggleSidebar, togglePreview].forEach((toggle) => {
      if (toggle) {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          const state = window.LaserState;
          const currentEnabled = state && state.layer2 ? state.layer2.enabled : false;
          syncLayer2Toggle(!currentEnabled);
        });
      }
    });

    const fillerSwatches = document.querySelectorAll('.filler-swatch');
    fillerSwatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const id = swatch.dataset.filler;
        const hex = swatch.dataset.hex;
        const name = swatch.dataset.name;
        if (id && hex) syncLayer2Color(id, hex, name);
      });
    });

    // 11. 2D / 3D Mode Toggle Switch
    const toggleLaser2D = document.getElementById('toggleLaser2D');
    const paperCanvas = document.getElementById('paperCanvas');
    const wood3DViewport = document.getElementById('wood3DViewport');
    const zoomControls = document.getElementById('zoomControls');

    window.currentViewMode = '3d'; // Default 3D photorealistic mode

    if (toggleLaser2D) {
      toggleLaser2D.addEventListener('change', () => {
        const is2D = toggleLaser2D.checked;
        window.currentViewMode = is2D ? '2d' : '3d';

        if (paperCanvas) paperCanvas.style.display = is2D ? 'block' : 'none';
        if (wood3DViewport) wood3DViewport.style.display = is2D ? 'none' : 'block';
        if (zoomControls) zoomControls.style.display = 'flex';

        if (!is2D && window.wood3DViewer && window.LaserExporter) {
          const svgString = window.LaserExporter.generateSVGString();
          window.wood3DViewer.loadSVG(svgString);
        } else if (is2D) {
          paper.view.center = window.LaserState.DESIGN_CENTER;
          paper.view.zoom = 1.0;
          paper.view.draw();
        }
      });
    }

    // 12. Zoom Overlay Controls (2D Canvas & 3D WebGL Viewport)
    const btnZoomIn = document.getElementById('zoomIn');
    const btnZoomOut = document.getElementById('zoomOut');
    const btnZoomReset = document.getElementById('zoomReset');

    if (btnZoomIn) {
      btnZoomIn.addEventListener('click', (e) => {
        e.preventDefault();
        if (window.currentViewMode === '2d') {
          paper.view.zoom = Math.min(paper.view.zoom * 1.25, 10);
          paper.view.draw();
        } else if (window.wood3DViewer) {
          if (typeof window.wood3DViewer.zoomIn === 'function') {
            window.wood3DViewer.zoomIn();
          } else if (window.wood3DViewer.camera) {
            window.wood3DViewer.camera.position.multiplyScalar(0.8);
            if (window.wood3DViewer.controls) window.wood3DViewer.controls.update();
          }
        }
      });
    }

    if (btnZoomOut) {
      btnZoomOut.addEventListener('click', (e) => {
        e.preventDefault();
        if (window.currentViewMode === '2d') {
          paper.view.zoom = Math.max(paper.view.zoom * 0.8, 0.2);
          paper.view.draw();
        } else if (window.wood3DViewer) {
          if (typeof window.wood3DViewer.zoomOut === 'function') {
            window.wood3DViewer.zoomOut();
          } else if (window.wood3DViewer.camera) {
            window.wood3DViewer.camera.position.multiplyScalar(1.25);
            if (window.wood3DViewer.controls) window.wood3DViewer.controls.update();
          }
        }
      });
    }

    if (btnZoomReset) {
      btnZoomReset.addEventListener('click', (e) => {
        e.preventDefault();
        if (window.currentViewMode === '2d') {
          paper.view.zoom = 1.0;
          paper.view.center = window.LaserState.DESIGN_CENTER;
          paper.view.draw();
        } else if (window.wood3DViewer) {
          if (typeof window.wood3DViewer.resetCamera === 'function') {
            window.wood3DViewer.resetCamera();
          } else if (typeof window.wood3DViewer.setCameraView === 'function') {
            window.wood3DViewer.setCameraView('front');
          }
        }
      });
    }

    // 13. Primary Action CTA Button (Add to Cart / Download SVG)
    const downloadBtn = document.getElementById('downloadSVG');
    if (downloadBtn) {
      downloadBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const config = window.LaserStudioProductConfig;
        if (config && config.isProduct && window.LaserExporter && typeof window.LaserExporter.addToCart === 'function') {
          window.LaserExporter.addToCart();
        } else if (window.LaserExporter) {
          window.LaserExporter.exportSVGFile();
        }
      });
    }

    // Initial auto-detection of product handle template
    window.LaserEvents.detectInitialProductTemplate();

    // Browser navigation Back/Forward event listener
    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.template) {
        window.LaserEvents.selectTemplate(e.state.template, false);
      } else {
        window.LaserEvents.detectInitialProductTemplate();
        window.LaserEvents.triggerRender(true);
      }
    });

    // Initial sync of font weight availability and tab UI
    window.LaserEvents.updateFontWeightAvailability();
    window.LaserEvents.updateTabModeUI();

    // Initial sync of Layer 2 Infill UI & Dynamic Starting Price ($18.00)
    const initL2 = window.LaserState && window.LaserState.layer2 ? window.LaserState.layer2 : { enabled: false, colorId: 'green' };
    syncLayer2Toggle(!!initL2.enabled);
  }
};

// Aliases for global namespace compatibility
window.LaserStudio.Events = window.LaserEvents;


/* ==========================================================================
   2D MODULE: laser-studio-app.js
   ========================================================================== */

/**
 * Laser Ornament Studio - Master Application Bootstrapper
 * Module: assets/laser-studio-app.js
 * Responsibility: Initializes Paper.js 2D Canvas, Three.js 3D Viewport, async Font Pipeline, and wires DOM events on load.
 */

(function () {
  'use strict';

  function initApp() {
    const canvas = document.getElementById('paperCanvas');
    if (!canvas) {
      console.warn('[LaserStudio] Canvas element #paperCanvas not found on page.');
      return;
    }

    // 1. Setup Paper.js Canvas & Global Coordinate Space
    paper.setup(canvas);

    window.LaserState = window.LaserState || {};
    window.LaserState.DESIGN_CENTER = new paper.Point(
      paper.view.center.x,
      paper.view.center.y
    );

    // 2. Setup 2D Pan & Zoom interaction
    if (window.LaserEvents) {
      window.LaserEvents.setupPanAndZoom(canvas);
      window.LaserEvents.bindControls();
    }

    // 3. Initialize 3D Wood WebGL Viewport
    const wood3DContainer = document.getElementById('wood3DCanvas');
    if (wood3DContainer && window.Wood3D && window.Wood3D.Viewer) {
      window.wood3DViewer = new window.Wood3D.Viewer(wood3DContainer, {
        woodType: window.LaserState.selectedWoodSpecies || 'birch',
        thickness: window.LaserState.selectedThickness || 5.0,
        lighting: 'warm_studio',
        autoRotate: false,
        layer2: window.LaserState.layer2 || {
          enabled: false,
          colorId: 'green',
          color: '#059669',
          name: 'Emerald Pine Green',
          material: 'acrylic_gloss',
          thickness: 3.0
        }
      });
    }

    // 4. Asynchronously Load OpenType Font Binaries
    if (window.LaserFontLoader) {
      window.LaserFontLoader.loadFonts(function () {
        // Compute initial CAD vector design
        const initialConfig = window.LaserEvents ? window.LaserEvents.getDesignConfig() : {};
        if (window.LaserEvents) {
          window.LaserEvents.updateDisplayValues(initialConfig);
        }

        if (window.LaserGeometry) {
          window.LaserGeometry.generateDesign(initialConfig);
        }

        // Initialize 3D Card Thumbnail Renderer
        if (window.Template3DRenderer) {
          window.Template3DRenderer.init();
        }

        // Load initial SVG into 3D Wood Viewport
        if (window.wood3DViewer && window.LaserExporter) {
          const svgString = window.LaserExporter.generateSVGString();
          window.wood3DViewer.loadSVG(svgString);
        }

        console.log('🌲 [LaserStudio] Initialized successfully in Shopify.');
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initApp);
  } else {
    initApp();
  }
})();

